import { useEffect, useRef, useState } from "react";
import { fabric } from "fabric";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  canvasUrl,
  listBankImages,
  loadDocument,
  renderDocument,
  saveDocument,
  toAbsoluteUrl,
  uploadAsset,
  uploadBankImage,
} from "../api.js";
import masIntegration from "../masIntegration.json";
import ColorInput, { TemplateColorsContext } from "../ColorInput.jsx";
import { attachAlignmentGuides } from "../alignGuides.js";
import { DEFAULT_FONT_FAMILY, FONT_FAMILIES, ensureFontsInjected } from "../fonts.js";
import {
  BLOB_DEFS,
  FRAME_DEFS,
  SHAPE_DEFS,
  SPECIAL_SHAPE_DEFS,
  GRID_KINDS,
  ShapeIcon,
  makeFabricShape,
  roundedRectPath,
} from "../shapes.jsx";
import {
  DASH_STYLES,
  DEFAULT_FILL_GRADIENT,
  IMAGE_ADJUST_DEFAULTS,
  fabricDash,
  fabricGradient,
  imageAdjustFilter,
} from "../layerStyle.jsx";
import { isSvgUrl, loadSvgText, recolorSvg, svgColors } from "../svgColors.js";
import Icon from "../components/Icon.jsx";
import LogoLoader from "../components/LogoLoader.jsx";
import DataFieldsPanel from "../components/DataFieldsPanel.jsx";
import { combineOutlines, customMaskShape, isFrameOperand, kindOutlinePath, roundFramePath } from "../frameGeometry.js";

// Shape kinds whose vertices can be rounded with the corner control.
const ROUNDABLE_SHAPES = new Set([
  "triangle",
  "star",
  "arrow",
  "hexagon",
  "diamond",
  "trapezium",
  "parallelogram",
  "seal",
]);

// Custom fields that must survive fabric's JSON serialization (undo history,
// clipboard clones).
const HISTORY_PROPS = [
  "layerType",
  "layerName",
  "dataField",
  "layerId",
  "shapeKind",
  "cornerRadii",
  "cornerRadius",
  "assetSrc",
  "crossOrigin",
  "shadowColor",
  "shadowOpacity",
  "manualHeight",
  "fitToWidth",
  "fitWidthBoxWidth",
  "fitToHeight",
  "verticalAlign",
  "locked",
  "selectable",
  "hasControls",
  "hoverCursor",
  "editable",
  "lockMovementX",
  "lockMovementY",
  "lockScalingX",
  "lockScalingY",
  "lockRotation",
  "fillGradient",
  "strokeDash",
  "maskShape",
  "maskPath",
  "maskBasePath",
  "maskCornerRadius",
  "lockAspect",
  "gridColumns",
  "gridRows",
  "dotColor",
  "dotSize",
  "imageAdjust",
  "svgSrc",
  "svgEdits",
];

// Copied elements are kept in the browser, not just this page, so they can be
// pasted into another template or design, even in another tab.
const CLIPBOARD_KEY = "at-editor-clipboard";

// Photo adjustments are drawn with the same CSS filter the server uses, so the
// canvas matches the render. Blur is in device pixels on a canvas context.
const renderImageFill = fabric.Image.prototype._renderFill;
fabric.Image.prototype._renderFill = function (ctx) {
  const zoom = (this.canvas?.getZoom() || 1) * (this.canvas?.getRetinaScaling() || 1);
  const filter = imageAdjustFilter(this.imageAdjust, zoom);
  if (!filter) return renderImageFill.call(this, ctx);
  const previous = ctx.filter;
  ctx.filter = filter;
  renderImageFill.call(this, ctx);
  ctx.filter = previous;
};

// Clicks on a frame count only inside its shape, not its whole box, so what
// shows outside the shape (like a logo under a cut-out corner) can still be
// clicked. Tested by tracing the frame's clip outline and asking the 2D
// context whether the point is in it.
const hitTestContext = document.createElement("canvas").getContext("2d");
const imageContainsPoint = fabric.Image.prototype.containsPoint;
fabric.Image.prototype.containsPoint = function (point, lines, absolute, calculate) {
  if (!imageContainsPoint.call(this, point, lines, absolute, calculate)) return false;
  const clip = this.clipPath;
  if (!this.maskShape || !clip || !this.canvas) return true;
  const { invertTransform, transformPoint } = fabric.util;
  // findTarget passes screen (zoomed) coordinates unless absolute.
  const scene = absolute ? point : transformPoint(point, invertTransform(this.canvas.viewportTransform));
  const inImage = transformPoint(scene, invertTransform(this.calcTransformMatrix()));
  const inClip = transformPoint(inImage, invertTransform(clip.calcTransformMatrix()));
  hitTestContext.setTransform(1, 0, 0, 1, 0, 0);
  hitTestContext.beginPath();
  clip._render(hitTestContext);
  return hitTestContext.isPointInPath(inClip.x, inClip.y, clip.fillRule === "evenodd" ? "evenodd" : "nonzero");
};

// Fabric's plain "justify" also stretches each paragraph's last line. This one
// leaves it alone, like CSS text-align:justify in the render.
const FABRIC_JUSTIFY = "justify-left";

function applyLock(obj, locked) {
  obj.locked = locked;
  obj.lockMovementX = locked;
  obj.lockMovementY = locked;
  obj.lockScalingX = locked;
  obj.lockScalingY = locked;
  obj.lockRotation = locked;
  obj.hasControls = !locked;
  obj.selectable = !locked;
  obj.hoverCursor = locked ? "default" : "move";
  if (obj.type === "textbox") obj.editable = !locked;
}

// Aspect-ratio lock: corner handles already scale uniformly (fabric's
// uniformScaling), so hiding the side handles leaves no way to distort it.
// Control visibility isn't serialized, so this is re-applied after undo.
function applyAspectLock(obj, locked) {
  obj.lockAspect = Boolean(locked);
  obj.setControlsVisibility({ ml: !locked, mr: !locked, mt: !locked, mb: !locked });
}

// Text colour used for a solid fill, also when the fill is a gradient (the
// API's `fill` override and older readers expect a plain colour).
function solidFill(obj) {
  return typeof obj.fill === "string" ? obj.fill : obj.fillGradient?.color_start || "#cccccc";
}

// Template-JSON props every layer type shares, applied after the type-specific
// constructor (opacity, visibility, aspect lock, dash, gradient, image frame).
function applyCommonLayerProps(obj, layer) {
  obj.set({ opacity: layer.opacity ?? 1, visible: !layer.hidden });
  obj.dataField = Boolean(layer.data_field);
  if (layer.lock_aspect) applyAspectLock(obj, true);
  fixPillShape(obj);
  if (obj.layerType === "rect" || obj.layerType === "shape") {
    obj.strokeDash = layer.stroke_dash || "solid";
    obj.set(fabricDash(obj.strokeDash, layer.stroke_width));
    if (layer.fill_gradient) {
      obj.fillGradient = layer.fill_gradient;
      obj.set({ fill: fabricGradient(layer.fill_gradient) });
    }
  }
  if (obj.layerType === "image") {
    obj.imageAdjust = layer.image_adjust || null;
    obj.svgSrc = layer.svg_src || null;
    obj.svgEdits = layer.svg_edits || null;
  }
  if (obj.layerType === "image" && (layer.mask_path || layer.mask_shape)) {
    // A built-in frame kind, or "custom" (union/intersection/subtract of
    // shapes, frameGeometry.js). mask_path is the outline actually drawn
    // (custom, or a kind with rounded corners); mask_base_path the custom
    // outline before rounding.
    obj.maskShape = layer.mask_shape || "custom";
    obj.maskPath = layer.mask_path || null;
    obj.maskBasePath = layer.mask_base_path || (layer.mask_shape ? null : layer.mask_path);
    obj.maskCornerRadius = layer.mask_corner_radius || 0;
    applyImageCornerRadius(obj);
  }
  return obj;
}

// Grey "drop an image here" tile for empty image frames.
const FRAME_PLACEHOLDER =
  "data:image/svg+xml;charset=utf-8," +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="400" height="400">' +
      '<rect width="400" height="400" fill="#E6EAEF"/>' +
      '<g fill="none" stroke="#9AA4B2" stroke-width="10" stroke-linecap="round" stroke-linejoin="round">' +
      '<rect x="130" y="140" width="140" height="120" rx="14"/><circle cx="172" cy="182" r="12"/>' +
      '<path d="M140 250l45-45 30 30 20-20 25 25"/></g></svg>'
  );

// A frame with no photo in it yet.
const isEmptyFrame = (obj) => !obj.assetSrc || obj.assetSrc === FRAME_PLACEHOLDER;

function hexToRgba(hex, alpha) {
  const h = (hex || "#000000").replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const bigint = parseInt(full, 16) || 0;
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// fabric.Shadow has no separate opacity — it's baked into the CSS color
// string — so shadowColor/shadowOpacity are kept as custom hex/number
// fields alongside it purely so the UI has something plain to edit. Returns
// a {shadow, shadowColor, shadowOpacity} patch for onChange/active.set().
function shadowPatch(obj, patch) {
  const current = {
    color: obj.shadowColor || "#000000",
    opacity: obj.shadowOpacity ?? 0.4,
    blur: obj.shadow?.blur ?? 8,
    offsetX: obj.shadow?.offsetX ?? 4,
    offsetY: obj.shadow?.offsetY ?? 4,
  };
  const next = { ...current, ...patch };
  return {
    shadow: new fabric.Shadow({
      color: hexToRgba(next.color, next.opacity),
      blur: next.blur,
      offsetX: next.offsetX,
      offsetY: next.offsetY,
    }),
    shadowColor: next.color,
    shadowOpacity: next.opacity,
  };
}

// Reconstructs a fabric.Shadow (+ its custom hex/opacity twins) from a saved
// layer's shadow_* fields, for applying to a freshly-loaded fabric object.
function layerShadowProps(layer) {
  if (!layer.shadow_color) return {};
  const opacity = layer.shadow_opacity ?? 0.4;
  return {
    shadowColor: layer.shadow_color,
    shadowOpacity: opacity,
    shadow: new fabric.Shadow({
      color: hexToRgba(layer.shadow_color, opacity),
      blur: layer.shadow_blur ?? 8,
      offsetX: layer.shadow_offset_x ?? 4,
      offsetY: layer.shadow_offset_y ?? 4,
    }),
  };
}

// letter_spacing is stored as CSS px (font-size-independent, matches how a
// designer thinks about tracking) but fabric's charSpacing is thousandths
// of an em — relative to the object's own font size, so it must be
// recomputed any time font size changes, not just set once at construction.
function pxToCharSpacing(px, fontSize) {
  if (!px || !fontSize) return 0;
  return (px / fontSize) * 1000;
}
function charSpacingToPx(charSpacing, fontSize) {
  if (!charSpacing || !fontSize) return 0;
  return (charSpacing / 1000) * fontSize;
}

// Mirrors fabric's own superscript/subscript schema (60% size factor) so a
// per-character style can be classified back into "super"/"sub"/null.
const SCRIPT_SIZE_FACTOR = 0.6;

function scriptKindAt(obj, lineIndex, charIndex) {
  const decl = obj.styles?.[lineIndex]?.[charIndex];
  if (!decl || typeof decl.deltaY !== "number" || typeof decl.fontSize !== "number") return null;
  const ratio = decl.fontSize / (obj.fontSize || 1);
  if (Math.abs(ratio - SCRIPT_SIZE_FACTOR) > 0.05) return null;
  if (decl.deltaY < 0) return "super";
  if (decl.deltaY > 0) return "sub";
  return null;
}

// Walks a Textbox's per-character `styles` overrides and collapses runs of
// consecutive superscript/subscript characters into {start, end, script}
// ranges (character offsets into the plain obj.text string) for saving.
function extractScriptRuns(obj) {
  const lines = (obj.text || "").split("\n");
  const runs = [];
  let globalIndex = 0;
  let current = null;
  lines.forEach((line, lineIndex) => {
    for (let charIndex = 0; charIndex < line.length; charIndex++) {
      const kind = scriptKindAt(obj, lineIndex, charIndex);
      const pos = globalIndex + charIndex;
      if (kind !== (current?.kind || null)) {
        if (current) runs.push({ start: current.start, end: pos, script: current.kind });
        current = kind ? { start: pos, kind } : null;
      }
    }
    globalIndex += line.length;
    if (lineIndex < lines.length - 1) {
      if (current) {
        runs.push({ start: current.start, end: globalIndex, script: current.kind });
        current = null;
      }
      globalIndex += 1; // the newline character itself
    }
  });
  if (current) runs.push({ start: current.start, end: globalIndex, script: current.kind });
  return runs;
}

let _measureCanvas = null;
function measureTextWidth(text, fontSize, fontFamily, fontWeight) {
  if (!_measureCanvas) _measureCanvas = document.createElement("canvas");
  const ctx = _measureCanvas.getContext("2d");
  ctx.font = `${fontWeight === "bold" ? "bold " : ""}${fontSize}px '${fontFamily}'`;
  return ctx.measureText(text).width;
}

// Fixed ceiling/floor shared by every "fit" layer, not derived from the
// layer's own current/previous size — that's what let the old auto-fit
// feature's per-layer ceiling ratchet up or down forever on repeated
// toggles. A single constant can't drift. Mirrors the backend's
// _FIT_MAX/_FIT_MIN exactly, so the editor and the rendered PNG always
// agree.
const FIT_MAX = 400;
const FIT_MIN = 8;

// "Fit to height" searches for the LARGEST font whose wrapped text still
// fits the box, which converges to a near-zero gap against the top/bottom
// border as text gets longer (short text leaves a visible gap only
// incidentally, when it happens to be width-bound instead) — inconsistent
// and cramped for longer text. Reserving this fraction of the box height as
// top+bottom padding keeps a fixed minimum gap regardless of text length,
// same idea as an Excel cell's own internal padding. Mirrors the backend's
// _FIT_HEIGHT_PAD_RATIO exactly.
const FIT_HEIGHT_PAD_RATIO = 0.14;
const fitHeightPadding = (height) => height * FIT_HEIGHT_PAD_RATIO;

// Direction (dx, dy) each linear gradient position's color_start anchors
// toward color_end — e.g. "top" starts at the top edge and heads down.
// Diagonals use the actual corner-to-corner delta (not a fixed 45°) so the
// line still runs exactly corner-to-corner on a non-square canvas. Mirrors
// the backend's _LINEAR_DIRECTIONS exactly, so the editor and the rendered
// PNG always agree.
const LINEAR_GRADIENT_DIRECTIONS = {
  top: [0, 1],
  bottom: [0, -1],
  left: [1, 0],
  right: [-1, 0],
  "top-left": [1, 1],
  "top-right": [-1, 1],
  "bottom-left": [1, -1],
  "bottom-right": [-1, -1],
};

// "Fit to width": largest font size at which `text` still fits on one line
// within `width`.
function computeFitToWidthFontSize(text, width, fontFamily, fontWeight) {
  const firstLine = (text || "").split("\n")[0];
  let size = FIT_MAX;
  while (size > FIT_MIN && measureTextWidth(firstLine, size, fontFamily, fontWeight) > width) {
    size -= 1;
  }
  return size;
}

// "Fit to height": largest font size at which `text`, wrapped within
// `width`, still fits within the box's padded content area (height minus
// top+bottom padding — see FIT_HEIGHT_PAD_RATIO). Wrapping isn't a single
// measureText call like the width case — it depends on where each line
// breaks, which is exactly what fabric.Textbox already computes internally.
// Rather than reimplement word-wrap by hand, build a scratch Textbox (never
// added to the canvas) and read its own computed height back for each
// candidate size. Binary search keeps that to ~9 probes instead of ~400.
function computeFitToHeightFontSize(text, width, height, fontFamily, fontWeight, lineHeight) {
  height -= fitHeightPadding(height) * 2;
  const wrappedHeight = (size) => {
    const probe = new fabric.Textbox(text || "", {
      width,
      fontSize: size,
      fontFamily,
      fontWeight,
      lineHeight: lineHeight ?? 1.2,
    });
    // fabric.Textbox grows its own width past what was asked for whenever a
    // single unbreakable word is wider than the box (dynamicMinWidth) — it
    // can't wrap mid-word. That's a genuine "doesn't fit at this size", not
    // a valid probe of the target width (the resulting height would be
    // wrongly small, since the word got more room than the real fixed box
    // allows) — treat it as not fitting so the search keeps shrinking.
    if (probe.width > width) return Infinity;
    return probe.height;
  };
  if (wrappedHeight(FIT_MAX) <= height) return FIT_MAX;
  if (wrappedHeight(FIT_MIN) > height) return FIT_MIN;
  let lo = FIT_MIN;
  let hi = FIT_MAX;
  while (hi - lo > 1) {
    const mid = Math.floor((lo + hi) / 2);
    if (wrappedHeight(mid) <= height) lo = mid;
    else hi = mid;
  }
  return lo;
}

// Absolute placement of any object — for a group child, its own transform
// composed with the group's; for a top-level object, just its own. Used to
// flatten groups into plain layers on save, and (universally, for every
// object) to compute x/y/rotation the way the backend's CSS renderer
// actually interprets them.
//
// q.translateX/Y (from decomposing calcTransformMatrix) is the object's
// true geometric CENTER — the same value fabric itself uses to paint the
// object, correct regardless of what sequence of moves/rotates/resizes
// produced the current state. CSS positions an unrotated box by its
// top-left corner and rotates the whole thing around that box's own
// (angle-independent) center — so recovering x/y from the center is a
// plain center-minus-half-size subtraction, NOT a rotation. Earlier this
// rotated the half-size offset by the object's angle before subtracting,
// which instead reproduces fabric's own corner-pivot rotation semantics —
// exactly the mismatch that caused rotated+resized layers to save into the
// wrong place.
function absolutePlacement(child) {
  const matrix = child.calcTransformMatrix();
  const q = fabric.util.qrDecompose(matrix);
  // qrDecompose can't tell "flipX/flipY" apart from a plain negative scale
  // — both collapse to the same matrix, which is exactly what makes this a
  // reliable way to read the EFFECTIVE flip (accounting for a parent
  // group's own flip too, not just this object's own flipX/flipY flags).
  // width/height must stay non-negative regardless (invalid CSS otherwise,
  // and the x/y formula below assumes a positive extent), so the sign is
  // pulled out into flipX/flipY and magnitude-only abs() feeds the box size
  // — the backend applies the mirror itself via a separate scale() (see
  // layer_to_html), the same composition order fabric uses internally.
  const flipX = q.scaleX < 0;
  const flipY = q.scaleY < 0;
  const w = (child.width || 0) * Math.abs(q.scaleX);
  const h = (child.height || 0) * Math.abs(q.scaleY);
  // Fabric's left/top is the outside of the stroke, and its centre includes
  // the stroke: leaving it out here moved stroked layers on every save.
  const stroke = child.strokeUniform ? child.strokeWidth || 0 : (child.strokeWidth || 0) * Math.abs(q.scaleX);
  return {
    x: Math.round(q.translateX - (w + stroke) / 2),
    y: Math.round(q.translateY - (h + stroke) / 2),
    width: Math.round(w),
    height: Math.round(h),
    rotation: Math.round(q.angle),
    flipX,
    flipY,
  };
}

// Vertical alignment ("top"/"middle"/"bottom") within a text box, matching
// the backend's flex align-items — only meaningful once the box has a fixed
// height (manualHeight), same as an Excel cell: with no extra height beyond
// the content there's nothing to distribute. fabric.Textbox always draws
// its lines starting at the top of the box and growing downward; the one
// internal hook every rendering path (glyphs, underline/strikethrough,
// highlight background, and the editing cursor) funnels through to find
// that start point is `_getTopOffset()`. Overriding it per-instance shifts
// where text paints WITHOUT touching the object's own top/height/selection
// bounds, so drag/resize/rotate keep working unmodified and rotation is
// handled for free (the offset is applied in the object's own local,
// pre-rotation coordinate space, same as fabric's default). It reads
// `calcTextHeight()` live on every call, so text/font/width edits that
// change the wrapped content height are picked up automatically with no
// extra recompute-on-change hooks needed.
function installVerticalAlignOffset(obj) {
  const defaultGetTopOffset = fabric.Textbox.prototype._getTopOffset;
  obj._getTopOffset = function () {
    const base = defaultGetTopOffset.call(this);
    if (this.manualHeight == null) return base;
    // Fit-to-height reserves a guaranteed top/bottom padding (see
    // FIT_HEIGHT_PAD_RATIO) so text never touches the border regardless of
    // length — applies even at "top"/unset alignment, unlike the plain
    // (non-fit) locked-height case below, which keeps its original
    // zero-gap default.
    const pad = this.fitToHeight ? fitHeightPadding(this.manualHeight) : 0;
    if (!this.verticalAlign || this.verticalAlign === "top") {
      return base + pad;
    }
    const extra = Math.max(0, this.manualHeight - pad * 2 - this.calcTextHeight());
    return base + pad + (this.verticalAlign === "middle" ? extra / 2 : extra);
  };
}

// fabric.Textbox's own constructor (used by clone()/fromObject() — copy,
// paste, undo/redo) always ends with initDimensions(), which recalculates
// height from wrapped content unconditionally, discarding whatever height
// was in the source data. manualHeight itself is a plain custom property
// and survives that untouched, so it's the reliable source to re-pin
// height from afterward — same two-step reasoning as everywhere else
// height gets set alongside a dimension-affecting prop.
function restorePinnedTextHeight(obj) {
  if (obj.manualHeight != null) obj.set({ height: obj.manualHeight });
}

// Rounded image corners, matching how the backend renders border_radius
// (a fixed pixel value on the FINAL box, like a shape's or rect's corner
// radius — not a fraction of the box that would distort with its aspect
// ratio). fabric.Image has no native rx/ry the way fabric.Rect does, so
// this uses a clipPath rect sized to the image's own natural (pre-scale)
// bounds; since a clipPath's geometry is stretched by the SAME scaleX/
// scaleY as the object it clips, the clip rect's own rx/ry are divided by
// the current scale so the radius that actually reaches the screen stays
// at the fixed pixel value the user set, regardless of how the box has
// been resized. img.cornerRadius (a plain custom property, the
// user-facing/final-space value) is the source of truth this recomputes
// from — call again whenever scaleX/scaleY changes (interactive resize,
// crop, or a Width/Height field edit) to keep the two in sync.
// Pills keep true semicircular ends at any size, like the server's
// border-radius:9999px: the corner radius is half the short side in final
// pixels, divided back into the object's own scale (fabric scales rx/ry
// with the object, which would squash the ends into ovals).
function pillRadii(width, height, scaleX = 1, scaleY = 1) {
  const r = Math.min(width * scaleX, height * scaleY) / 2;
  return { rx: r / scaleX, ry: r / scaleY };
}
function fixPillShape(obj) {
  if (obj?.layerType === "shape" && obj.shapeKind === "pill" && obj.type === "rect") {
    obj.set(pillRadii(obj.width, obj.height, obj.scaleX || 1, obj.scaleY || 1));
  }
}

// The outline a frame actually draws (img.maskPath): its custom outline or
// built-in kind, with every sharp corner rounded by maskCornerRadius pixels
// at the frame's current on-canvas size. Re-run whenever the radius, kind
// or size changes.
function bakeFrameMask(img) {
  const radius = img.maskCornerRadius || 0;
  const w = img.width * (img.scaleX || 1);
  const h = img.height * (img.scaleY || 1);
  if (img.maskShape === "custom") {
    if (!img.maskBasePath) img.maskBasePath = img.maskPath;
    img.maskPath = radius ? roundFramePath(img.maskBasePath, w, h, radius) : img.maskBasePath;
  } else if (radius && img.maskShape !== "pill" && img.maskShape !== "circle") {
    img.maskPath = roundFramePath(kindOutlinePath(img.maskShape), w, h, radius);
  } else {
    img.maskPath = null;
  }
}

function applyImageCornerRadius(img) {
  // A frame shape takes over the clip (corner radius doesn't apply). Built
  // in the image's own unscaled box, so it stretches with the image exactly
  // like the server's mask does with the layer box.
  if (img.maskShape) {
    bakeFrameMask(img);
    const clip =
      img.maskPath
        ? customMaskShape(img.maskPath, img.width, img.height)
        : makeFabricShape(img.maskShape === "custom" ? "circle" : img.maskShape, {
            width: img.width,
            height: img.height,
            left: 0,
            top: 0,
          });
    clip.set({ originX: "center", originY: "center", left: 0, top: 0 });
    if (img.maskShape === "pill" && !img.maskPath) clip.set(pillRadii(img.width, img.height, img.scaleX || 1, img.scaleY || 1));
    img.clipPath = clip;
    return;
  }
  const radius = img.cornerRadius || 0;
  if (!radius) {
    img.clipPath = null;
    return;
  }
  const rx = radius / (img.scaleX || 1);
  const ry = radius / (img.scaleY || 1);
  if (img.clipPath && img.clipPath.type === "rect") {
    img.clipPath.set({ rx, ry, width: img.width, height: img.height });
  } else {
    img.clipPath = new fabric.Rect({
      width: img.width,
      height: img.height,
      rx,
      ry,
      originX: "center",
      originY: "center",
    });
  }
}

// MAS: the only connected app is MAS Job Posts itself; its field list drives
// "This layer is the..." in Properties (copied from AutoTemplated's
// /v1/integrations definition into masIntegration.json).
const MAS_INTEGRATIONS = [masIntegration];
function findAppType(integrations, integrationKey, typeKey) {
  const app = (integrations || []).find((i) => i.key === integrationKey);
  return app?.types.find((t) => t.key === typeKey) || null;
}

ensureFontsInjected();

// Shapes offered as outline-only presets in the Elements panel.
const OUTLINE_KINDS = [
  { kind: "rect", label: "Rectangle" },
  { kind: "circle", label: "Circle" },
  { kind: "triangle", label: "Triangle" },
  { kind: "star", label: "Star" },
  { kind: "hexagon", label: "Hexagon" },
  { kind: "heart", label: "Heart" },
  { kind: "bubble", label: "Speech bubble" },
  { kind: "diamond", label: "Diamond" },
];

const LAYER_GLYPHS = { text: "T", image: "▣", rect: "▭", shape: "◆", group: "❏" };

// MAS keeps six of AutoTemplated's tabs (no Templates, AI, Use in app).
// A generated design is edited as a design: job data is already in it, so the
// Data fields tab, its labels and the field tags are templates only.
const RAIL_TABS = [
  {
    id: "fields",
    label: "Data fields",
    stroke: true,
    icon: "M4 6c0-1.7 3.6-3 8-3s8 1.3 8 3-3.6 3-8 3-8-1.3-8-3zM4 6v12c0 1.7 3.6 3 8 3s8-1.3 8-3V6M4 12c0 1.7 3.6 3 8 3s8-1.3 8-3",
  },
  { id: "text", label: "Text", icon: "M5 5h14v3h-5.5v11h-3V8H5z" },
  { id: "elements", label: "Elements", icon: "M12 3l9 9-9 9-9-9z" },
  { id: "frames", label: "Frames", stroke: true, icon: "M4 5h16v14H4zM6.5 16l3.5-4.5 2.5 3 2-2.5 3 4M9 9.2h.01" },
  { id: "uploads", label: "Images", icon: "M12 3l5 5h-3v7h-4V8H7zM5 18h14v3H5z" },
  { id: "canvas", label: "Background", stroke: true, icon: "M4 6h16M4 12h16M4 18h10" },
];

function RailIcon({ path, stroke }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24">
      <path
        d={path}
        fill={stroke ? "none" : "currentColor"}
        stroke={stroke ? "currentColor" : "none"}
        strokeWidth={stroke ? 2.2 : 0}
        strokeLinecap="round"
      />
    </svg>
  );
}

const MAX_PREVIEW_WIDTH = 640;
// Breathing room left around the canvas at "Fit" — without this the canvas
// DOM element touches the stage's edges on whichever axis is the tighter fit, unlike
// a design tool's stage, which always keeps a visible margin on every side.
const CANVAS_STAGE_MARGIN = 56;
const ZOOM_MIN = 0.1;
const ZOOM_MAX = 3;
// Zoom per wheel-delta pixel. A trackpad pinch sends many small deltas and a
// mouse wheel one big one, so the delta is capped to keep a mouse notch at
// about 10%.
const ZOOM_WHEEL_SPEED = 0.01;
const ZOOM_WHEEL_MAX_DELTA = 10;
// How long after the last wheel event a pinch counts as finished.
const ZOOM_SETTLE_MS = 120;

function makeLayerId() {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

// Replace fabric's plain rotation handle with an obvious rotate icon so
// users can find it. Applies to every object type via the shared prototype.
const ROTATE_ICON_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
  <circle cx="12" cy="12" r="11" fill="#ffffff" stroke="#00a69c" stroke-width="1.5"/>
  <path d="M16.5 8.5 A6 6 0 1 0 18 12" fill="none" stroke="#00a69c" stroke-width="2" stroke-linecap="round"/>
  <polygon points="18.6,5.2 19.2,9.4 15,8.8" fill="#00a69c"/>
</svg>`;

const rotateIconImg = new Image();
rotateIconImg.src = "data:image/svg+xml;utf8," + encodeURIComponent(ROTATE_ICON_SVG);

const rotateControl = new fabric.Control({
  x: 0,
  y: -0.5,
  offsetY: -34,
  withConnection: true,
  actionName: "rotate",
  actionHandler: fabric.controlsUtils.rotationWithSnapping,
  cursorStyleHandler: fabric.controlsUtils.rotationStyleHandler,
  cornerSize: 26,
  render: (ctx, left, top, _styleOverride, fabricObject) => {
    const size = 26;
    ctx.save();
    ctx.translate(left, top);
    ctx.rotate(fabric.util.degreesToRadians(fabricObject.angle));
    ctx.drawImage(rotateIconImg, -size / 2, -size / 2, size, size);
    ctx.restore();
  },
});

fabric.Object.prototype.controls.mtr = rotateControl;
// Textbox keeps its own control set — give it the same rotate handle.
if (fabric.Textbox?.prototype?.controls) {
  fabric.Textbox.prototype.controls.mtr = rotateControl;
}

// fabric only re-renders (rather than reusing its cached bitmap) an object
// whose changed property is listed in cacheProperties — custom properties
// like verticalAlign/manualHeight aren't there by default, so toggling
// vertical alignment would silently do nothing until some unrelated
// cache-dirtying change (e.g. moving the object) happened to force a
// redraw. Textbox inherits this array from fabric.Text's prototype.
fabric.Text.prototype.cacheProperties = fabric.Text.prototype.cacheProperties.concat(
  "verticalAlign",
  "manualHeight"
);
// Same reasoning for images: cornerRadius is what applyImageCornerRadius
// reads, but changing it (typing a new value) only touches the nested
// clipPath rect's own rx/ry — the image's cache-dirty check has no way to
// know its clip changed, so it keeps reusing the stale cached bitmap until
// some unrelated cache-dirtying change (e.g. a resize, which touches
// scaleX/scaleY — already tracked) forces a rebuild.
fabric.Image.prototype.cacheProperties = fabric.Image.prototype.cacheProperties.concat(
  "cornerRadius",
  "maskShape",
  "maskCornerRadius",
  "maskPath"
);

// Design-tool selection chrome: purple borders, white round corner handles.
Object.assign(fabric.Object.prototype, {
  borderColor: "#00a69c",
  cornerColor: "#ffffff",
  cornerStrokeColor: "#00a69c",
  cornerStyle: "circle",
  cornerSize: 10,
  transparentCorners: false,
  borderScaleFactor: 1.5,
  // Fabric's default resize behavior flips flipX/flipY when a corner/edge
  // handle is dragged past the opposite side — easy to trigger by accident
  // while resizing, and there's no mirror/flip feature anywhere in this
  // editor's UI to intentionally use it. The backend's CSS renderer only
  // understands plain rotation (layer_to_html's transform:rotate()), not a
  // mirror, so a flipped layer renders correctly on-canvas here but wrong
  // once generated — this locks scaling to stop at zero instead of flipping.
  lockScalingFlip: true,
});

export default function TemplateEditor({ mode = "template" }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // MAS: a template (/editor/template/:id) or one History design
  // (/editor/design/:id, its record passed in router state).
  const jobId = location.state?.jobId;
  const backTo = mode === "template" ? "/templates" : `/history${jobId ? `?job=${jobId}` : ""}`;
  const [loadError, setLoadError] = useState(null);

  const canvasElRef = useRef(null);
  const canvasWrapRef = useRef(null);
  const zoomPctRef = useRef(null);
  const zoomSliderRef = useRef(null);
  const fabricRef = useRef(null);
  const fileInputRef = useRef(null);

  // A new template (no :id) can arrive with a size already chosen in
  // TemplateList.jsx's "+ New Template" modal, passed via navigation state
  // (already converted to px there — see NewTemplateModal). Falls back to
  // today's 1080x1080 default if the editor is reached without going
  // through that modal (e.g. a stale bookmark to /templates/new).
  const [name, setName] = useState(location.state?.name ?? "Untitled template");
  const [width, setWidth] = useState(location.state?.width ?? 1080);
  const [height, setHeight] = useState(location.state?.height ?? 1080);
  // Kept in sync below — lets the once-only canvas-init effect (alignment
  // guides) always read the current template size despite closing over it
  // only at mount.
  const widthRef = useRef(width);
  const heightRef = useRef(height);
  useEffect(() => {
    widthRef.current = Number(width) || 1080;
    heightRef.current = Number(height) || 1080;
  }, [width, height]);
  const [backgroundColor, setBackgroundColor] = useState("#ffffff");
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [bgPickerOpen, setBgPickerOpen] = useState(false);
  // null = no gradient overlay. Kept in a ref alongside state (mirrors why
  // snapshot() reads the live fabric object for backgroundColor instead of
  // React state) so the undo-history snapshot never captures a stale value
  // from before React re-renders.
  const [backgroundGradient, setBackgroundGradientState] = useState(null);
  const backgroundGradientRef = useRef(null);
  const setBackgroundGradient = (value) => {
    backgroundGradientRef.current = value;
    setBackgroundGradientState(value);
  };
  // Which folder this template belongs to (the Templates page's folders).
  // For a brand-new template, arrives via navigation state the same way
  // width/height do (set from whichever folder tab was active in
  // TemplateList.jsx); for an existing one, set from the loaded template
  // below. No UI to change it from inside the editor — assignment happens
  // from the Templates grid — so a ref is enough, no re-render needed.
  const folderIdRef = useRef(location.state?.folderId ?? null);
  // Connected app + template type. A new template can
  // arrive already tagged via the /connect/:integration/:type link an app
  // shows its users (see ConnectRedirect in App.jsx).
  const [integration, setIntegration] = useState(location.state?.integration ?? null);
  const [templateType, setTemplateType] = useState(location.state?.templateType ?? null);
  // Per-template text rules for the app's fields (see ConnectPanel).
  const [fieldRules, setFieldRules] = useState({});
  const integrations = MAS_INTEGRATIONS;
  // Set once the placeholder layers for a deep-linked new template are added.
  const autoFieldsAddedRef = useRef(false);
  const bgFileInputRef = useRef(null);

  // Sets (or clears) the canvas background image, cover-fit to the template
  // size — mirrors the renderer's background-size: cover.
  const setCanvasBackgroundImage = (url, { withSnapshot = true } = {}) => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    setBackgroundImage(url || null);
    // The solid background_color is rendered as a real canvas object (see
    // ensurePageBackground) so it can sit behind everything, but fabric's
    // object stack always paints above canvas.backgroundImage regardless of
    // sendToBack — left visible, it fully hides the real background photo
    // underneath. Match the server renderer's CSS layering (image over
    // color) by hiding the color rect whenever an image is active.
    const pageBg = canvas.getObjects().find((o) => o.isPageBackground);
    if (!url) {
      canvas.setBackgroundImage(null, () => {
        if (pageBg) pageBg.set({ visible: true });
        canvas.renderAll();
        if (withSnapshot) snapshot();
      });
      return;
    }
    // Read from the refs (kept in sync with width/height every render), not
    // the width/height state directly — this can run in the same tick as
    // setWidth/setHeight during template load, before React re-renders, so
    // the state closure here would still hold the previous template's size.
    const w = widthRef.current || 1080;
    const h = heightRef.current || 1080;
    fabric.Image.fromURL(
      canvasUrl(url),
      (img) => {
        const scale = Math.max(w / img.width, h / img.height);
        img.set({
          originX: "left",
          originY: "top",
          scaleX: scale,
          scaleY: scale,
          left: (w - img.width * scale) / 2,
          top: (h - img.height * scale) / 2,
        });
        img.assetSrc = url;
        canvas.setBackgroundImage(img, () => {
          if (pageBg) pageBg.set({ visible: false });
          canvas.renderAll();
          if (withSnapshot) snapshot();
        });
      },
      { crossOrigin: "anonymous" }
    );
  };

  // Upload with the liquid-logo overlay over the canvas: progress while the
  // file goes up, then "Placing on canvas" while the browser fetches the
  // stored image (preloaded, so fabric then gets it from cache). Callers
  // clear it with setUpload(null) once the image is in place. The user's
  // own photos go into the Image Bank (and the Uploads tab) so they can be
  // reused; derived files like crops use uploadAsset directly instead.
  const [upload, setUpload] = useState(null); // { label, progress, placing }
  const uploadWithProgress = async (file, label = file.name) => {
    setUpload({ label, progress: 0, placing: false });
    try {
      const saved = await uploadBankImage(file, (p) => setUpload((u) => u && { ...u, progress: p }));
      const { url } = saved;
      setBankImages((prev) => (prev ? [saved, ...prev] : prev));
      setUpload((u) => u && { ...u, progress: 1, placing: true });
      await new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = img.onerror = resolve;
        img.src = canvasUrl(url);
      });
      return url;
    } catch (err) {
      setUpload(null);
      throw err;
    }
  };
  const uploadFailed = (err) => {
    console.error("Upload failed", err);
    setUpload(null);
    setCanvasNotice(err.response?.data?.detail || "The upload didn't go through. Check the file and try again.");
  };
  const [canvasNotice, setCanvasNotice] = useState(null);
  useEffect(() => {
    if (!canvasNotice) return undefined;
    const t = setTimeout(() => setCanvasNotice(null), 4000);
    return () => clearTimeout(t);
  }, [canvasNotice]);

  const onBgFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const url = await uploadWithProgress(file, "Background image");
      setCanvasBackgroundImage(url);
      setUpload(null);
    } catch (err) {
      uploadFailed(err);
    }
  };
  const [selected, setSelected] = useState(null);
  const [tick, setTick] = useState(0);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(!id);
  const designMode = mode === "design";
  const railTabs = designMode ? RAIL_TABS.filter((tab) => tab.id !== "fields") : RAIL_TABS;
  const [activeTab, setActiveTab] = useState(designMode ? "text" : "fields");
  useEffect(() => {
    showFieldTagsRef.current = activeTab === "fields";
    fabricRef.current?.requestRenderAll();
  }, [activeTab]);
  const [bankImages, setBankImages] = useState(null);
  const [leftOpen, setLeftOpen] = useState(true);
  // Collapsed by default — on a fresh load (or a new, empty template) both
  // panels have nothing useful to show yet ("Select a layer...", "No
  // layers yet"), so starting open is just clutter until something's
  // actually selected/added.
  const [propsOpen, setPropsOpen] = useState(false);
  const [layersOpen, setLayersOpen] = useState(false);
  const clipboardRef = useRef(null);
  const [ctxMenu, setCtxMenu] = useState(null); // {x, y}
  const actionsRef = useRef({});
  // Read by the canvas after:render hook (registered once) to draw field tags.
  const showFieldTagsRef = useRef(false);
  const [cropping, setCropping] = useState(false);
  // Photo size in adjust mode, relative to just covering its frame.
  const [adjustZoom, setAdjustZoom] = useState(1);
  // null = auto-fit the canvas to the available space; a number pins zoom to
  // that fraction (1 = actual size) until "Fit" is pressed again.
  const [zoomOverride, setZoomOverride] = useState(null);
  const cropRef = useRef({
    active: false,
    image: null,
    rect: null,
    overlays: [],
    backup: null,
    prevEvented: new Map(),
  });

  // The canvas DOM element IS sized to the design's actual on-screen size
  // (w*scale x h*scale) so .canvas-wrap-open's native `overflow: auto`
  // produces real scrollbars once it's bigger than the stage — no translate
  // offset needed since the canvas already starts exactly at the design's
  // origin. Re-centers the scroll position after every scale change so
  // zooming stays anchored on the middle.
  const applyScale = (canvas, w, h, zoomFraction) => {
    const wrap = canvasWrapRef.current;
    const availW = Math.max(320, (wrap?.clientWidth || MAX_PREVIEW_WIDTH) - CANVAS_STAGE_MARGIN * 2);
    const availH = Math.max(320, (wrap?.clientHeight || 600) - CANVAS_STAGE_MARGIN * 2);
    const fitScale = Math.min(1, availW / w, availH / h);
    const scale = zoomFraction ?? fitScale;
    const targetW = Math.round(w * scale);
    const targetH = Math.round(h * scale);
    // Skip the resize (and the re-center that goes with it) when nothing
    // actually changed — e.g. undo/redo calls this on every restore even
    // when the template's size/zoom weren't touched (only an object
    // moved), and canvas.setDimensions() always reallocates the bitmap,
    // which was showing up as a visible flash/"auto-resize" on every undo.
    const resized = canvas.getWidth() !== targetW || canvas.getHeight() !== targetH;
    if (resized) {
      canvas.setDimensions({ width: targetW, height: targetH });
      canvas.setViewportTransform([scale, 0, 0, scale, 0, 0]);
    }
    if (wrap) {
      // Pad the scrollable stage so a canvas smaller than it sits
      // centered, via ordinary block-flow padding rather than flex
      // `justify-content: center` — centering an overflowing flex item
      // that way makes the portion past center unreachable by scroll in
      // several browsers. Padding drops to 0 once the canvas is bigger
      // than the stage, and plain overflow:auto scrolling — fully
      // reachable in every direction — takes over from there.
      const padX = Math.max(0, (wrap.clientWidth - targetW) / 2);
      const padY = Math.max(0, (wrap.clientHeight - targetH) / 2);
      wrap.style.paddingLeft = `${padX}px`;
      wrap.style.paddingRight = `${padX}px`;
      wrap.style.paddingTop = `${padY}px`;
      wrap.style.paddingBottom = `${padY}px`;
      if (resized) {
        wrap.scrollLeft = (targetW - wrap.clientWidth) / 2;
        wrap.scrollTop = (targetH - wrap.clientHeight) / 2;
      }
    }
    // Resizing wipes the bitmap, so redraw now: a deferred render would leave
    // the canvas blank for a frame and it blinks while zooming.
    if (resized) canvas.renderAll();
    else canvas.requestRenderAll();
    return scale;
  };

  const computeFitScale = (w, h) => {
    const canvas = fabricRef.current;
    if (!canvas) return 1;
    return Math.min(1, canvas.getWidth() / w, canvas.getHeight() / h);
  };

  // fabric.Canvas's own backgroundColor is drawn straight into raw canvas
  // pixel space BEFORE the viewport transform is applied (confirmed in
  // fabric's source: the fill path is built from (0,0) to (canvas.width,
  // canvas.height), and viewportTransform is only applied afterward to
  // affect pattern/gradient sampling, not the path itself) — so a solid
  // fabric.Canvas.backgroundColor floods the whole visible viewport
  // regardless of zoom/pan instead of scaling and panning with the design
  // like every other layer. A plain fabric.Rect, sized to the template and
  // kept at the very back of the stack, behaves correctly instead. It's
  // excluded from canvas.toJSON() (native fabric behavior for
  // excludeFromExport) and from buildPayload's serialization (existing
  // `!o.excludeFromExport` filter), so it must be re-created after every
  // loadFromJSON (undo/redo, template load) rather than expected to persist.
  const ensurePageBackground = (canvas, color, w, h) => {
    let bg = canvas.getObjects().find((o) => o.isPageBackground);
    if (!bg) {
      bg = new fabric.Rect({
        left: 0,
        top: 0,
        selectable: false,
        evented: false,
        excludeFromExport: true,
        hoverCursor: "default",
        // A real background image (fabric's native canvas.backgroundImage)
        // always paints beneath this rect no matter its stacking order, so
        // if one's already active this rect must start hidden or it blots
        // the photo out with a flat color — see setCanvasBackgroundImage.
        visible: !canvas.backgroundImage,
      });
      bg.isPageBackground = true;
      canvas.add(bg);
    }
    bg.set({ width: w, height: h, fill: color });
    canvas.sendToBack(bg);
    return bg;
  };

  // Overlay above the color/image and below every content layer — "radial"
  // spreads from an anchor point (center/top/bottom) out to the farthest
  // corner, matching the server's radial-gradient(circle at ...). "linear"
  // is a straight band running from one edge/corner to the opposite one,
  // matching the server's linear-gradient(<angle>deg, ...) — see
  // LINEAR_GRADIENT_DIRECTIONS for the shared angle math. Same
  // excludeFromExport + re-create-after-loadFromJSON pattern as
  // ensurePageBackground, and must stay ordered directly above the color
  // rect (below every content layer, above canvas.backgroundImage — which
  // paints beneath every fabric object natively regardless of z-order, so
  // no extra handling is needed for it).
  const ensureGradientOverlay = (canvas, gradient, w, h) => {
    let rect = canvas.getObjects().find((o) => o.isGradientOverlay);
    if (!gradient) {
      if (rect) canvas.remove(rect);
      return null;
    }
    const colorStops = [
      { offset: 0, color: gradient.color_start, opacity: 1 },
      { offset: 1, color: gradient.color_end, opacity: gradient.end_opacity },
    ];
    let fill;
    if (gradient.kind === "linear") {
      const [dx, dy] = LINEAR_GRADIENT_DIRECTIONS[gradient.position] || LINEAR_GRADIENT_DIRECTIONS.top;
      // A straight line through the box center along (dx, dy), extended to
      // the box's own edges/corners — exactly matches an edge/corner-to-
      // corner run for any aspect ratio (same direction vectors the
      // backend derives its CSS angle from).
      const cx = w / 2;
      const cy = h / 2;
      fill = new fabric.Gradient({
        type: "linear",
        coords: { x1: cx - dx * cx, y1: cy - dy * cy, x2: cx + dx * cx, y2: cy + dy * cy },
        colorStops,
      });
    } else {
      const cx = w / 2;
      const cy = gradient.position === "top" ? 0 : gradient.position === "bottom" ? h : h / 2;
      const corners = [[0, 0], [w, 0], [0, h], [w, h]];
      const radius = Math.max(...corners.map(([x, y]) => Math.hypot(x - cx, y - cy)));
      fill = new fabric.Gradient({
        type: "radial",
        coords: { x1: cx, y1: cy, r1: 0, x2: cx, y2: cy, r2: radius },
        colorStops,
      });
    }
    if (!rect) {
      rect = new fabric.Rect({
        left: 0,
        top: 0,
        selectable: false,
        evented: false,
        excludeFromExport: true,
        hoverCursor: "default",
      });
      rect.isGradientOverlay = true;
      canvas.add(rect);
    }
    rect.set({ width: w, height: h, fill });
    canvas.sendToBack(rect);
    const pageBg = canvas.getObjects().find((o) => o.isPageBackground);
    if (pageBg) canvas.sendToBack(pageBg);
    return rect;
  };

  // A visible page-boundary outline (as in Figma), always on top so
  // it reads regardless of layer stacking — content can legitimately cover
  // the color/gradient rects but the page edge itself should never be
  // hidden by a layer that happens to extend to the canvas bounds. Same
  // excludeFromExport + re-create-after-loadFromJSON pattern as
  // ensurePageBackground; must be called (with the current w/h) alongside
  // every ensurePageBackground call site so it tracks canvas-size changes.
  const PAGE_BORDER_WIDTH = 2.5;
  const PAGE_BORDER_WIDTH_HOVER = 4;

  const ensurePageBorder = (canvas, w, h) => {
    let border = canvas.getObjects().find((o) => o.isPageBorder);
    if (!border) {
      border = new fabric.Rect({
        left: 0,
        top: 0,
        fill: "transparent",
        stroke: "#00a69c",
        strokeWidth: PAGE_BORDER_WIDTH,
        strokeUniform: true,
        selectable: false,
        evented: false,
        excludeFromExport: true,
        hoverCursor: "default",
        // Hidden: the page now reads as a sheet on the dotted workspace
        // (drop shadow + stage background in site.css), no outline needed.
        visible: false,
      });
      border.isPageBorder = true;
      canvas.add(border);
    }
    border.set({ width: w, height: h });
    canvas.bringToFront(border);
    return border;
  };

  // Thickens further on hover so the page edge reads clearly the moment
  // the designer moves the cursor in, on top of the already-heavier base
  // width above (was too thin to notice at all before either change).
  // Toggled from a plain mouseenter/mouseleave pair on .canvas-wrap (JSX
  // below) rather than a fabric event — the border itself is
  // evented:false (it must never intercept clicks meant for the page
  // underneath it), so fabric never sees pointer events over it directly.
  const setPageBorderHover = (hovering) => {
    const canvas = fabricRef.current;
    const border = canvas?.getObjects().find((o) => o.isPageBorder);
    if (!border) return;
    border.set({ strokeWidth: hovering ? PAGE_BORDER_WIDTH_HOVER : PAGE_BORDER_WIDTH });
    canvas.requestRenderAll();
  };

  // Hover outline on unselected elements — one lazily-created
  // shared rect, looked up by marker (not a ref) so it self-heals the same
  // way ensurePageBackground/ensurePageBorder do: canvas.clear() during
  // undo/redo removes it along with everything else, and the next
  // mouse:over just recreates it, rather than a stale ref pointing at a
  // detached object.
  const ensureHoverOutline = (canvas) => {
    let outline = canvas.getObjects().find((o) => o.isHoverOutline);
    if (!outline) {
      outline = new fabric.Rect({
        fill: "transparent",
        stroke: "#00a69c",
        strokeWidth: 2,
        strokeUniform: true,
        selectable: false,
        evented: false,
        excludeFromExport: true,
        hoverCursor: "default",
        visible: false,
      });
      outline.isHoverOutline = true;
      canvas.add(outline);
    }
    return outline;
  };

  // canvas.getObjects().length would count the page-background rect too,
  // throwing off auto-generated names like "Text 3".
  const contentObjectCount = (canvas) => canvas.getObjects().filter((o) => !o.excludeFromExport).length;

  // layerName (distinct from layerId) is the public API key used to
  // override a layer by name at render time — duplicated/pasted objects
  // must get a fresh one, or every copy silently shares one override slot.
  const uniqueLayerName = (canvas, obj) => {
    const type = obj.layerType || obj.type;
    let label;
    if (type === "shape") {
      label = [...SHAPE_DEFS, ...SPECIAL_SHAPE_DEFS].find((d) => d.kind === obj.shapeKind)?.label || "Shape";
    } else {
      label = { text: "Text", rect: "Rectangle", image: "Image", group: "Group" }[type] || "Layer";
    }
    const used = new Set();
    const collect = (o) => {
      if (o !== obj) used.add(o.layerName);
      if (o.type === "group") o.forEachObject(collect);
    };
    canvas.getObjects().forEach(collect);
    let n = contentObjectCount(canvas) + 1;
    while (used.has(`${label} ${n}`)) n += 1;
    return `${label} ${n}`;
  };

  // Re-fit the canvas viewport when panels collapse/expand or the window
  // resizes; re-centers at the current zoom (or auto-fit) afterward.
  useEffect(() => {
    const refit = () => {
      const canvas = fabricRef.current;
      if (canvas && loaded) {
        applyScale(canvas, Number(width), Number(height), zoomOverride);
        forceTick();
      }
    };
    const raf = requestAnimationFrame(refit);
    window.addEventListener("resize", refit);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", refit);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leftOpen, propsOpen, layersOpen, loaded, width, height]);

  // Always center-anchored: goes through applyScale, which resizes the
  // canvas to the new scale and re-centers the scroll position.
  const zoomAt = (nextZoom) => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    const clamped = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, nextZoom));
    // widthRef/heightRef, not the width/height state directly — zoomAt is
    // called from the wheel-zoom effect below, which only mounts once and
    // would otherwise keep calling this with whatever width/height were at
    // mount, before the real template dimensions loaded.
    applyScale(canvas, widthRef.current, heightRef.current, clamped);
    setZoomOverride(clamped);
    forceTick();
  };

  const bumpZoom = (factor) => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    zoomAt((canvas.getZoom() || 1) * factor);
  };

  const zoomToFit = () => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    applyScale(canvas, Number(width), Number(height), null);
    setZoomOverride(null);
    forceTick();
  };

  // Ctrl/Cmd+scroll zooms (Figma convention, also how trackpads
  // report a pinch gesture); plain scroll scrolls the stage.
  useEffect(() => {
    const wrap = canvasWrapRef.current;
    if (!wrap) return;
    // Resizing the canvas every frame snaps it to whole pixels, which makes it
    // tremble and is slow on big designs. So a pinch only scales the canvas
    // with a CSS transform, centered on the stage, and the real resize happens
    // once when the fingers stop.
    let pinch = null;
    let frame = 0;
    let settleTimer = 0;
    const applyPinch = () => {
      frame = 0;
      const canvas = fabricRef.current;
      if (!canvas || !pinch) return;
      const scale = pinch.zoom / pinch.startZoom;
      canvas.wrapperEl.style.transform = `translate(${pinch.dx}px, ${pinch.dy}px) scale(${scale})`;
      if (zoomPctRef.current) zoomPctRef.current.textContent = `${Math.round(pinch.zoom * 100)}%`;
      if (zoomSliderRef.current) zoomSliderRef.current.value = pinch.zoom;
    };
    const finishPinch = () => {
      const canvas = fabricRef.current;
      const finalZoom = pinch?.zoom;
      pinch = null;
      cancelAnimationFrame(frame);
      frame = 0;
      if (!canvas) return;
      canvas.wrapperEl.style.transform = "";
      if (finalZoom) zoomAt(finalZoom);
    };
    const onWheel = (e) => {
      const canvas = fabricRef.current;
      if (!canvas) return;
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        if (!pinch) {
          const stage = wrap.getBoundingClientRect();
          const box = canvas.wrapperEl.getBoundingClientRect();
          const startZoom = canvas.getZoom() || 1;
          pinch = {
            startZoom,
            zoom: startZoom,
            dx: stage.left + wrap.clientWidth / 2 - (box.left + box.width / 2),
            dy: stage.top + wrap.clientHeight / 2 - (box.top + box.height / 2),
          };
        }
        const delta = Math.max(-ZOOM_WHEEL_MAX_DELTA, Math.min(ZOOM_WHEEL_MAX_DELTA, e.deltaY));
        pinch.zoom = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, pinch.zoom * Math.exp(-delta * ZOOM_WHEEL_SPEED)));
        if (!frame) frame = requestAnimationFrame(applyPinch);
        clearTimeout(settleTimer);
        settleTimer = setTimeout(finishPinch, ZOOM_SETTLE_MS);
      } else {
        // Drive the scroll explicitly from the wheel delta rather than
        // relying on the browser's default scroll handling for this
        // element — something upstream (likely Fabric's own pointer/
        // wheel event wiring on the canvas itself) was swallowing plain
        // two-finger scroll before it reached native scroll handling.
        e.preventDefault();
        wrap.scrollLeft += e.deltaX;
        wrap.scrollTop += e.deltaY;
      }
    };
    wrap.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      wrap.removeEventListener("wheel", onWheel);
      cancelAnimationFrame(frame);
      clearTimeout(settleTimer);
    };
  }, []);

  // Hold Space + drag to pan (Figma convention) — scrolls the stage
  // the same way the wheel handler above does, just driven by mouse
  // deltas instead of wheel deltas. Suppresses object selection
  // while held so a drag pans instead of rubber-band-selecting or moving
  // whatever's under the cursor. Same input-focus guard as the
  // Delete/undo keydown handler below, so typing a space in a text field
  // (or while editing text on the canvas) never triggers panning.
  useEffect(() => {
    const wrap = canvasWrapRef.current;
    if (!wrap) return;
    const panState = { spaceHeld: false, dragging: false, lastX: 0, lastY: 0 };

    const isTypingTarget = (target) => {
      const tag = target.tagName;
      return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || target.isContentEditable;
    };

    const onKeyDown = (e) => {
      if (e.code !== "Space" || e.repeat || isTypingTarget(e.target)) return;
      const canvas = fabricRef.current;
      if (!canvas || canvas.getActiveObject()?.isEditing) return;
      panState.spaceHeld = true;
      canvas.selection = false;
      canvas.defaultCursor = canvas.hoverCursor = "grab";
      canvas.requestRenderAll();
    };
    const onKeyUp = (e) => {
      if (e.code !== "Space") return;
      const canvas = fabricRef.current;
      panState.spaceHeld = false;
      panState.dragging = false;
      if (!canvas) return;
      canvas.selection = true;
      canvas.defaultCursor = canvas.hoverCursor = "default";
      canvas.requestRenderAll();
    };
    const onMouseDown = (e) => {
      if (!panState.spaceHeld) return;
      panState.dragging = true;
      panState.lastX = e.clientX;
      panState.lastY = e.clientY;
      const canvas = fabricRef.current;
      if (canvas) canvas.defaultCursor = canvas.hoverCursor = "grabbing";
    };
    const onMouseMove = (e) => {
      if (!panState.dragging) return;
      const canvas = fabricRef.current;
      if (!canvas) return;
      // Scroll-pane model — panning is just scrolling the wrap.
      const wrap = canvasWrapRef.current;
      if (wrap) {
        wrap.scrollLeft -= e.clientX - panState.lastX;
        wrap.scrollTop -= e.clientY - panState.lastY;
      }
      panState.lastX = e.clientX;
      panState.lastY = e.clientY;
    };
    const onMouseUp = () => {
      if (!panState.dragging) return;
      panState.dragging = false;
      const canvas = fabricRef.current;
      if (canvas && panState.spaceHeld) canvas.defaultCursor = canvas.hoverCursor = "grab";
    };

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("keyup", onKeyUp);
    wrap.addEventListener("mousedown", onMouseDown);
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("keyup", onKeyUp);
      wrap.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, []);

  // Lazy-load flyout data the first time each tab is opened.
  useEffect(() => {
    if (activeTab === "uploads" && bankImages === null) {
      listBankImages().then(setBankImages);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const forceTick = () => setTick((t) => t + 1);

  // Builds the fabric object for a saved layer without adding it to the canvas yet.
  // Image loading is async, so callers must await every layer's object and add them
  // to the canvas together, in order — otherwise images (which resolve later) would
  // always land on top regardless of their saved z_index.
  // fabric.js (for the originX/originY: 'left'/'top' every layer uses)
  // rotates an object around its literal left/top CORNER, not its center.
  // The backend renderer's CSS transform:rotate() always rotates around the
  // element's CENTER — a genuinely different pivot whenever angle != 0. The
  // interactive rotate handle self-corrects for this live (fabric's
  // wrapWithFixedAnchor repositions left/top every frame to keep the
  // apparent center fixed), which is why dragging looks right — but a
  // freshly-loaded layer, positioned with one raw `.set({left, top, angle})`
  // at construction time, gets fabric's native corner-pivot placement,
  // landing it somewhere else entirely from what the CSS backend renders
  // for the exact same saved values. Reset to the unrotated baseline first
  // (where the two pivots trivially agree), apply the angle, then use
  // fabric's own setPositionByOrigin to pull the CENTER back to where the
  // backend's CSS would put it — matching the pivot the interactive handle
  // already produces, so load and drag never disagree again.
  const applyRotatedPosition = (obj, x, y, width, height, rotationDeg, flipX, flipY) => {
    // Flip mirrors around the same center as rotation, so it doesn't affect
    // this pivot math either way — set it before the early return covers
    // both branches without repeating the call.
    obj.set({ flipX: Boolean(flipX), flipY: Boolean(flipY) });
    if (!rotationDeg) {
      obj.set({ left: x, top: y, angle: 0 });
      return;
    }
    const stroke = obj.strokeWidth || 0;
    const cx = x + (width + stroke) / 2;
    const cy = y + (height + stroke) / 2;
    obj.set({ left: x, top: y, angle: 0 });
    obj.set({ angle: rotationDeg });
    obj.setPositionByOrigin(new fabric.Point(cx, cy), "center", "center");
  };

  const buildLayerObjectBase = (layer) => {
    const common = {
      left: layer.x,
      top: layer.y,
      selectable: true,
    };

    if (layer.type === "text") {
      const obj = new fabric.Textbox(layer.text || "Text", {
        ...common,
        width: layer.width,
        fontFamily: layer.font_family || "Arial, sans-serif",
        fontSize: layer.font_size || 32,
        fontWeight: layer.font_weight || "normal",
        fontStyle: layer.font_style || "normal",
        underline: Boolean(layer.underline),
        linethrough: Boolean(layer.strikethrough),
        charSpacing: pxToCharSpacing(layer.letter_spacing, layer.font_size || 32),
        fill: layer.color || "#000000",
        textAlign: layer.text_align === "justify" ? FABRIC_JUSTIFY : layer.text_align || "left",
        lineHeight: layer.line_height ?? 1.2,
        // Repurposed for text: stroke = outline, backgroundColor = the
        // whole-box "Background" effect. textBackgroundColor is fabric's
        // native per-line marker highlight, hugging the text itself.
        stroke: layer.stroke_color || "",
        strokeWidth: layer.stroke_width || 0,
        backgroundColor: layer.background_color || "",
        textBackgroundColor: layer.text_highlight_color || "",
        ...layerShadowProps(layer),
      });
      obj.layerType = "text";
      obj.layerName = layer.name || "Text";
      obj.layerId = layer.id || makeLayerId();
      installVerticalAlignOffset(obj);
      obj.verticalAlign = layer.text_vertical_align || "top";
      // Textbox always recalculates height from wrapped content on
      // construction — re-apply the saved height afterward if the user had
      // pinned it, otherwise leave it auto-computed like a normal textbox.
      if (layer.text_height_locked && layer.height) {
        obj.set({ height: layer.height });
        obj.manualHeight = layer.height;
      }
      (layer.text_script_runs || []).forEach((run) => {
        if (run.script === "super") obj.setSuperscript(run.start, run.end);
        else if (run.script === "sub") obj.setSubscript(run.start, run.end);
      });
      obj.fitToWidth = Boolean(layer.text_fit_width);
      obj.fitToHeight = Boolean(layer.text_fit_height);
      // Pinned reference width for either fit mode — re-asserted below
      // (and on every later edit) because fabric.Textbox grows its own
      // width past this whenever a single unbreakable word doesn't fit
      // (dynamicMinWidth), and construction above may already have done
      // so using the just-loaded (possibly stale) font size.
      if (obj.fitToWidth || obj.fitToHeight) obj.fitWidthBoxWidth = layer.width;
      // The saved font_size is whatever fit at save time — for a baked
      // design copy (or any hand-edited layer JSON) the text may no longer
      // match it. Always recompute on load, matching what the server
      // renderer independently computes at render time. Fit-to-height's
      // algorithm (wrap on, font sized to the fixed height using the
      // current width as the wrap boundary) already keeps BOTH dimensions
      // fixed, so it takes priority whenever both are checked — "fit to
      // width" alone (single line, no wrap) only kicks in when height
      // isn't also being fit.
      if (obj.fitToHeight) {
        const pinnedHeight = obj.manualHeight ?? layer.height;
        obj.set({
          width: layer.width,
          fontSize: computeFitToHeightFontSize(
            obj.text,
            layer.width,
            pinnedHeight,
            obj.fontFamily,
            obj.fontWeight,
            obj.lineHeight
          ),
        });
        // fontSize/width are dimension-affecting — fabric.Text's own set()
        // reruns initDimensions() (recalculating height from wrapped
        // content) as a POST-STEP after every key in a .set() call is
        // applied, even ones bundled into the very same call. height isn't
        // itself dimension-affecting, so it must be re-pinned in its own
        // separate, later call to actually stick.
        obj.set({ height: pinnedHeight });
        obj.manualHeight = pinnedHeight;
      } else if (obj.fitToWidth) {
        obj.set({
          width: layer.width,
          fontSize: computeFitToWidthFontSize(obj.text, layer.width, obj.fontFamily, obj.fontWeight),
        });
      }
      applyRotatedPosition(obj, layer.x, layer.y, layer.width, layer.height, layer.rotation, layer.flip_x, layer.flip_y);
      return Promise.resolve(obj);
    }

    if (layer.type === "rect") {
      const base = layer.border_radius || 0;
      const radii = layer.corner_radii?.length === 4
        ? layer.corner_radii
        : [base, base, base, base];
      const uniform = radii.every((r) => r === radii[0]);
      const style = {
        ...common,
        fill: layer.background_color || "#cccccc",
        stroke: layer.stroke_color || "",
        strokeWidth: layer.stroke_width || 0,
        ...layerShadowProps(layer),
      };
      const obj = uniform
        ? new fabric.Rect({
            ...style,
            width: layer.width,
            height: layer.height,
            rx: radii[0],
            ry: radii[0],
          })
        : new fabric.Path(roundedRectPath(layer.width, layer.height, radii), style);
      obj.layerType = "rect";
      obj.cornerRadii = radii;
      obj.layerName = layer.name || "Rectangle";
      obj.layerId = layer.id || makeLayerId();
      applyRotatedPosition(obj, layer.x, layer.y, layer.width, layer.height, layer.rotation, layer.flip_x, layer.flip_y);
      return Promise.resolve(obj);
    }

    if (layer.type === "shape") {
      const obj = makeFabricShape(layer.shape_kind || "circle", {
        ...common,
        width: layer.width,
        height: layer.height,
        fill: layer.background_color || (GRID_KINDS.has(layer.shape_kind) ? "transparent" : "#cccccc"),
        stroke: layer.stroke_color || "",
        strokeWidth: layer.stroke_width || 0,
        cornerRadius: layer.border_radius || 0,
      });
      obj.set({ left: layer.x, top: layer.y, ...layerShadowProps(layer) });
      obj.layerType = "shape";
      obj.shapeKind = layer.shape_kind || "circle";
      obj.gridColumns = layer.grid_columns;
      obj.gridRows = layer.grid_rows;
      obj.dotColor = layer.dot_color || "#191919";
      obj.dotSize = layer.dot_size || 8;
      obj.cornerRadius = layer.border_radius || 0;
      obj.layerName = layer.name || "Shape";
      obj.layerId = layer.id || makeLayerId();
      applyRotatedPosition(obj, layer.x, layer.y, layer.width, layer.height, layer.rotation, layer.flip_x, layer.flip_y);
      return Promise.resolve(obj);
    }

    if (layer.type === "image") {
      // "?canvas=1" keeps fabric's CORS fetch separate from any non-CORS
      // cached copy of the same image (see insertBankImage) — needed for
      // every image regardless of URL shape, since the Templates list's
      // thumbnails load the same S3 URLs via plain <img> tags first.
      // (Not for data: URLs — placeholders and frames — where a suffix would
      // corrupt the image itself.)
      const displayUrl = !layer.src
        ? FRAME_PLACEHOLDER
        : layer.src.startsWith("data:")
          ? layer.src
          : canvasUrl(layer.src);
      return new Promise((resolve) => {
        fabric.Image.fromURL(
          displayUrl,
          (img) => {
            // object-fit: cover, like the server: when the photo's ratio
            // differs from the layer box, show its centred crop instead of
            // stretching it (e.g. a photo placed in a square frame).
            const iw = img.width || layer.width;
            const ih = img.height || layer.height;
            const boxRatio = layer.width / layer.height;
            let cropW = iw;
            let cropH = ih;
            if (Math.abs(iw / ih - boxRatio) > 0.005) {
              if (iw / ih > boxRatio) cropW = ih * boxRatio;
              else cropH = iw / boxRatio;
            }
            let cropX = (iw - cropW) / 2;
            let cropY = (ih - cropH) / 2;
            // A photo moved or resized inside its frame shows that part.
            const saved = layer.src && layer.image_crop;
            if (saved && saved[2] > 0 && saved[3] > 0 && saved[0] + saved[2] <= 1.001 && saved[1] + saved[3] <= 1.001) {
              [cropX, cropY, cropW, cropH] = [saved[0] * iw, saved[1] * ih, saved[2] * iw, saved[3] * ih];
            }
            img.set({
              ...common,
              cropX,
              cropY,
              width: cropW,
              height: cropH,
              scaleX: layer.width / cropW,
              scaleY: layer.height / cropH,
            });
            img.layerType = "image";
            img.layerName = layer.name || "Image";
            img.layerId = layer.id || makeLayerId();
            img.assetSrc = layer.src || "";
            img.cornerRadius = layer.border_radius || 0;
            applyImageCornerRadius(img);
            applyRotatedPosition(img, layer.x, layer.y, layer.width, layer.height, layer.rotation, layer.flip_x, layer.flip_y);
            resolve(img);
          },
          { crossOrigin: "anonymous" }
        );
      });
    }

    return Promise.resolve(null);
  };

  // Rebuild saved groups (the file is flat, see buildPayload) at the place in
  // the stack where their first child sits. Hidden/locked on every child
  // means it was set on the group.
  const restoreGroups = (canvas, layers, objs) => {
    const groups = new Map();
    layers.forEach((layer, i) => {
      if (!layer.group_id || !objs[i]) return;
      if (!groups.has(layer.group_id)) groups.set(layer.group_id, { name: layer.group_name, members: [] });
      groups.get(layer.group_id).members.push(objs[i]);
    });
    groups.forEach(({ name, members }, groupId) => {
      if (members.length < 2) return;
      const index = canvas.getObjects().indexOf(members[0]);
      const hidden = members.every((o) => o.visible === false);
      const locked = members.every((o) => o.locked);
      members.forEach((o) => {
        if (hidden) o.visible = true;
        if (locked) applyLock(o, false);
      });
      const group = new fabric.ActiveSelection(members, { canvas }).toGroup();
      group.layerType = "group";
      group.layerName = name || `Group ${contentObjectCount(canvas)}`;
      group.layerId = groupId;
      group.visible = !hidden;
      if (locked) applyLock(group, true);
      canvas.moveTo(group, index);
    });
    canvas.discardActiveObject();
    setSelected(null);
  };

  const buildLayerObject = (layer) =>
    buildLayerObjectBase(layer).then((obj) => (obj ? applyCommonLayerProps(obj, layer) : obj));

  // --- auto-save ---
  const [saveStatus, setSaveStatus] = useState("saved"); // saved | unsaved | saving | error
  const templateIdRef = useRef(id || null);
  const autoSaveTimer = useRef(null);
  const performSaveRef = useRef(async () => {});
  const metaReadyRef = useRef(false);

  // Brief toast confirming an explicit Save click succeeded — distinct from
  // the small persistent .save-status label, which covers silent autosave
  // and is easy to not notice as feedback for an intentional click.
  const [saveToast, setSaveToast] = useState(false);
  const saveToastTimer = useRef(null);
  const flashSaveToast = () => {
    setSaveToast(true);
    clearTimeout(saveToastTimer.current);
    saveToastTimer.current = setTimeout(() => setSaveToast(false), 2200);
  };

  const scheduleAutoSave = () => {
    setSaveStatus("unsaved");
    clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => performSaveRef.current(), 1800);
  };

  // Template meta (name, tags) isn't a canvas change, so watch it directly.
  useEffect(() => {
    if (!loaded) return;
    if (!metaReadyRef.current) {
      metaReadyRef.current = true;
      return;
    }
    scheduleAutoSave();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, integration, templateType, fieldRules, loaded]);

  // Warn before leaving with unsaved changes still in flight.
  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (saveStatus === "unsaved" || saveStatus === "saving") {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [saveStatus]);

  useEffect(() => () => clearTimeout(autoSaveTimer.current), []);

  // --- undo/redo history ---
  const historyRef = useRef({ stack: [], redo: [], busy: false, timer: null });

  const snapshot = (immediate = false, { skipAutoSave = false } = {}) => {
    const history = historyRef.current;
    const canvas = fabricRef.current;
    if (!canvas || history.busy) return;
    const take = () => {
      if (history.busy || !fabricRef.current) return;
      // Reads the live fabric object rather than the backgroundColor React
      // state: this can fire synchronously right after setBackgroundColor()
      // in the same event handler, before React re-renders, so the state
      // closure here would otherwise be stale by one change.
      const bg = canvas.getObjects().find((o) => o.isPageBackground);
      history.stack.push(
        JSON.stringify({
          json: canvas.toJSON(HISTORY_PROPS),
          // widthRef/heightRef, not width/height directly — snapshot() is
          // called from canvas.on("object:added"/"modified"/etc.) handlers
          // registered once in the canvas-init effect (mount/id-scoped), so
          // a direct closure over width/height would keep reading whatever
          // they were at that effect's setup, before the real template
          // dimensions finished loading — every snapshot taken after that
          // baked in the wrong size, and restoring one on undo/redo then
          // visibly resized the canvas. Same fix as zoomAt's.
          width: widthRef.current,
          height: heightRef.current,
          backgroundColor: bg?.fill || backgroundColor,
          // No live-object equivalent to read back for a gradient (its fill
          // is a fabric.Gradient instance, not a plain value) — the ref is
          // kept in sync at every mutation point instead, same reasoning as
          // the backgroundColor comment above.
          backgroundGradient: backgroundGradientRef.current,
        })
      );
      if (history.stack.length > 60) history.stack.shift();
      history.redo = [];
      // The very first snapshot right after a template loads just records
      // the starting point for undo — it isn't a user edit, so it shouldn't
      // flip the save indicator to "Unsaved changes" before anything's
      // actually changed.
      if (!skipAutoSave) scheduleAutoSave();
      forceTick();
    };
    clearTimeout(history.timer);
    if (immediate) take();
    else history.timer = setTimeout(take, 300);
  };

  const restoreEntry = (entry) => {
    const canvas = fabricRef.current;
    const history = historyRef.current;
    const state = JSON.parse(entry);
    history.busy = true;
    canvas.loadFromJSON(state.json, () => {
      // loadFromJSON reconstructs plain data props (verticalAlign included,
      // via HISTORY_PROPS) but not the per-instance _getTopOffset override
      // installVerticalAlignOffset attaches — reattach it so vertical
      // alignment keeps rendering correctly across undo/redo. It also
      // rebuilds every Textbox through fabric's own constructor, which
      // always ends by recalculating height from wrapped content — restore
      // a pinned height from manualHeight (which survives untouched) too.
      const restoreObject = (o) => {
        if (o.type === "group") o.forEachObject(restoreObject);
        if (o.layerType === "text") {
          installVerticalAlignOffset(o);
          restorePinnedTextHeight(o);
        }
        // clipPath round-trips through fabric's own (de)serialization, but
        // its rx/ry were computed against a specific scale — recomputing
        // from the plain cornerRadius property (which survives untouched)
        // guarantees it's still correct rather than trusting that.
        if (o.layerType === "image") applyImageCornerRadius(o);
        if (o.lockAspect) applyAspectLock(o, true);
      };
      canvas.getObjects().forEach(restoreObject);
      applyScale(canvas, state.width, state.height, zoomOverride);
      setWidth(state.width);
      setHeight(state.height);
      const bgColor = state.backgroundColor || "#ffffff";
      setBackgroundColor(bgColor);
      ensurePageBackground(canvas, bgColor, state.width, state.height);
      setBackgroundGradient(state.backgroundGradient || null);
      ensureGradientOverlay(canvas, state.backgroundGradient || null, state.width, state.height);
      ensurePageBorder(canvas, state.width, state.height);
      canvas.renderAll();
      history.busy = false;
      setSelected(null);
      setBackgroundImage(canvas.backgroundImage?.assetSrc || null);
      scheduleAutoSave();
      forceTick();
    });
  };

  const undo = () => {
    const history = historyRef.current;
    if (history.stack.length < 2 || history.busy) return;
    history.redo.push(history.stack.pop());
    restoreEntry(history.stack[history.stack.length - 1]);
  };

  const redoAction = () => {
    const history = historyRef.current;
    if (history.redo.length === 0 || history.busy) return;
    const entry = history.redo.pop();
    history.stack.push(entry);
    restoreEntry(entry);
  };

  // Initialize fabric canvas once
  useEffect(() => {
    const canvas = new fabric.Canvas(canvasElRef.current, {
      preserveObjectStacking: true,
    });
    fabricRef.current = canvas;
    attachAlignmentGuides(canvas, () => ({ width: widthRef.current, height: heightRef.current }));
    // Text may get added/rendered before the self-hosted @font-face files
    // finish downloading; re-render once every catalog font has been
    // explicitly loaded so labels don't stay stuck on the fallback font.
    ensureFontsInjected().then(() => fabricRef.current?.requestRenderAll());
    historyRef.current = { stack: [], redo: [], busy: true, timer: null };
    templateIdRef.current = id || null;
    metaReadyRef.current = false;
    setSaveStatus("saved");
    clearTimeout(autoSaveTimer.current);

    const handleSelection = () => {
      if (cropRef.current.active) return; // ignore the crop rectangle
      const active = canvas.getActiveObject();
      setSelected(active || null);
      // Selecting an object that was already hover-outlined would otherwise
      // leave both borders showing at once until the next mouse:out.
      const outline = canvas.getObjects().find((o) => o.isHoverOutline);
      if (outline?.visible) outline.set({ visible: false });
      forceTick();
    };

    canvas.on("selection:created", handleSelection);
    canvas.on("selection:updated", handleSelection);
    canvas.on("selection:cleared", () => {
      if (!cropRef.current.active) setSelected(null);
      // Clicking off the photo while adjusting it in its frame finishes.
      else if (cropRef.current.mode === "adjust") setTimeout(() => actionsRef.current.applyCrop?.());
    });
    // Hover outline — the already-selected object has its own
    // selection border, so skip it there to avoid a redundant doubled-up
    // outline; excludeFromExport also filters out our own helper objects
    // (background/gradient/border), though evented:false on those already
    // keeps fabric from ever targeting them here in the first place.
    canvas.on("mouse:over", (e) => {
      const target = e.target;
      if (!target || target.excludeFromExport || target === canvas.getActiveObject()) return;
      const outline = ensureHoverOutline(canvas);
      outline.set({
        left: target.left,
        top: target.top,
        width: target.width,
        height: target.height,
        scaleX: target.scaleX,
        scaleY: target.scaleY,
        angle: target.angle,
        originX: target.originX,
        originY: target.originY,
        visible: true,
      });
      canvas.bringToFront(outline);
      canvas.requestRenderAll();
    });
    canvas.on("mouse:out", () => {
      const outline = canvas.getObjects().find((o) => o.isHoverOutline);
      if (outline) {
        outline.set({ visible: false });
        canvas.requestRenderAll();
      }
    });
    canvas.on("mouse:dblclick", (e) => {
      const target = e.target;
      if (target?.layerType !== "image" || target.locked) return;
      if (target.maskShape) actionsRef.current.enterAdjustMode?.(target);
      else enterCropMode(target);
    });
    canvas.on("object:modified", forceTick);
    // Stretching a frame from a side handle keeps the photo undistorted: it
    // re-covers the new box around the same centre.
    canvas.on("object:modified", (e) => {
      const frame = e.target;
      if (frame?.layerType === "image" && frame.maskShape && !isEmptyFrame(frame) && Math.abs(frame.scaleX / frame.scaleY - 1) > 0.001) {
        actionsRef.current.coverFrame?.(frame);
        canvas.requestRenderAll();
      }
    });
    canvas.on("object:moving", forceTick);
    canvas.on("object:scaling", (e) => {
      // A rounded image's clip radius is a fixed pixel value on the FINAL
      // box (matching the backend), not a fraction of it — dragging a
      // resize handle changes scaleX/scaleY, so the clip has to be
      // recomputed on every frame to keep the on-screen radius constant
      // instead of stretching with the box.
      if (
        e.target?.layerType === "image" &&
        (e.target.cornerRadius || e.target.maskCornerRadius || e.target.maskShape === "pill")
      ) {
        applyImageCornerRadius(e.target);
      }
      fixPillShape(e.target);
      forceTick();
    });
    canvas.on("object:rotating", forceTick);
    canvas.on("text:changed", forceTick);
    // Typing directly on the canvas (double-click to edit) bypasses
    // updateSelected entirely, so a pinned height needs its own reassertion
    // here too — otherwise it'd only survive Properties-panel edits.
    canvas.on("text:changed", (e) => {
      const obj = e.target;
      if (obj?.layerType === "text" && obj.manualHeight != null) {
        obj.set({ height: obj.manualHeight });
      }
      if (obj?.layerType === "text" && obj.fitToHeight) {
        // Takes priority over fitToWidth when both are on — wrapping stays
        // enabled and the pinned width is already the fixed wrap boundary,
        // so this alone keeps both dimensions fixed. Typing may have
        // already grown obj.width via fabric's dynamicMinWidth (an
        // unbreakable word wider than the box), so re-fit against the
        // pinned target, not that possibly-grown one.
        const targetWidth = obj.fitWidthBoxWidth ?? obj.width;
        const targetHeight = obj.manualHeight ?? obj.height;
        const fitted = computeFitToHeightFontSize(
          obj.text,
          targetWidth,
          targetHeight,
          obj.fontFamily,
          obj.fontWeight,
          obj.lineHeight
        );
        obj.set({ fontSize: fitted, width: targetWidth });
        // fontSize/width are dimension-affecting — fabric.Text's own set()
        // reruns initDimensions() (recalculating height from wrapped
        // content) as a POST-STEP after every key in a .set() call, even
        // ones bundled into the very same call — so height has to be
        // re-pinned in its own separate, later call to actually stick.
        obj.set({ height: targetHeight });
        obj.manualHeight = targetHeight;
      } else if (obj?.layerType === "text" && obj.fitToWidth) {
        const targetWidth = obj.fitWidthBoxWidth ?? obj.width;
        const fitted = computeFitToWidthFontSize(obj.text, targetWidth, obj.fontFamily, obj.fontWeight);
        obj.set({ fontSize: fitted, width: targetWidth });
      }
    });

    // Dragging a photo that's already on the canvas onto a frame fills the
    // frame with it (the loose photo goes away), like dropping one from
    // Uploads. The frame under the pointer is outlined while dragging; hold
    // Alt to just move the photo over it.
    let frameDrop = null; // { image, frame }
    const frameUnderPointer = (moving, e) => {
      const p = canvas.getPointer(e.e);
      const objs = canvas.getObjects();
      for (let i = objs.length - 1; i >= 0; i--) {
        const o = objs[i];
        if (o === moving || o.layerType !== "image" || !o.maskShape || !o.visible || o.locked) continue;
        const r = o.getBoundingRect(true, true);
        if (p.x >= r.left && p.x <= r.left + r.width && p.y >= r.top && p.y <= r.top + r.height) return o;
      }
      return null;
    };
    canvas.on("object:moving", (e) => {
      const img = e.target;
      const frame =
        img?.layerType === "image" && !img.maskShape && img.assetSrc && !e.e?.altKey ? frameUnderPointer(img, e) : null;
      if (frame !== frameDrop?.frame) canvas.requestRenderAll();
      frameDrop = frame ? { image: img, frame } : null;
    });
    // Data fields tab open: label each data field with its name, like
    // autofill tags, so designers see what an app will fill in.
    // (Fabric's renderTop fires after:render with no ctx while a selection box
    // is dragged; only the object layer's render needs these drawn.)
    canvas.on("after:render", (e) => {
      if (!e.ctx || !showFieldTagsRef.current) return;
      const ctx = e.ctx;
      const dpr = canvas.getRetinaScaling();
      ctx.save();
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.font = "600 11px Inter, system-ui, sans-serif";
      canvas.getObjects().forEach((o) => {
        if (!o.dataField || !o.layerName || o.visible === false) return;
        const r = o.getBoundingRect();
        const label = o.layerName;
        const w = ctx.measureText(label).width + 12;
        const x = r.left;
        const y = r.top >= 20 ? r.top - 19 : r.top + 2;
        ctx.fillStyle = "#007f76";
        ctx.beginPath();
        ctx.roundRect ? ctx.roundRect(x, y, w, 17, 4) : ctx.rect(x, y, w, 17);
        ctx.fill();
        ctx.fillStyle = "#ffffff";
        ctx.fillText(label, x + 6, y + 12.5);
        ctx.strokeStyle = "rgba(0, 127, 118, 0.55)";
        ctx.setLineDash([4, 3]);
        ctx.lineWidth = 1;
        ctx.strokeRect(r.left, r.top, r.width, r.height);
        ctx.setLineDash([]);
      });
      ctx.restore();
    });
    canvas.on("after:render", (e) => {
      if (!e.ctx || !frameDrop) return;
      const r = frameDrop.frame.getBoundingRect();
      const ctx = e.ctx;
      const dpr = canvas.getRetinaScaling();
      ctx.save();
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.fillStyle = "rgba(0, 127, 118, 0.14)";
      ctx.strokeStyle = "#007f76";
      ctx.lineWidth = 3;
      ctx.setLineDash([8, 5]);
      ctx.fillRect(r.left, r.top, r.width, r.height);
      ctx.strokeRect(r.left, r.top, r.width, r.height);
      ctx.restore();
    });
    canvas.on("mouse:up", () => {
      const drop = frameDrop;
      frameDrop = null;
      if (!drop) return;
      canvas.remove(drop.image);
      canvas.setActiveObject(drop.frame);
      actionsRef.current.replaceFrameImage?.(drop.frame, drop.image.assetSrc);
    });

    // History: every structural or transform change lands a snapshot.
    canvas.on("object:added", () => snapshot());
    canvas.on("object:removed", () => snapshot());
    canvas.on("object:modified", () => snapshot());
    canvas.on("text:changed", () => snapshot());

    const beginHistory = () => {
      historyRef.current.busy = false;
      snapshot(true, { skipAutoSave: true });
      setSaveStatus("saved");
    };

    if (id) {
      loadDocument(mode, id).then(async (tpl) => {
        setName(tpl.name);
        setWidth(tpl.width);
        setHeight(tpl.height);
        setBackgroundColor(tpl.background_color);
        folderIdRef.current = tpl.folder_id || null;
        setIntegration(tpl.integration || null);
        setTemplateType(tpl.template_type || null);
        setFieldRules(tpl.field_rules || {});
        setBackgroundGradient(tpl.background_gradient || null);
        ensurePageBackground(canvas, tpl.background_color, tpl.width, tpl.height);
        ensureGradientOverlay(canvas, tpl.background_gradient || null, tpl.width, tpl.height);
        ensurePageBorder(canvas, tpl.width, tpl.height);
        applyScale(canvas, tpl.width, tpl.height, zoomOverride);
        if (tpl.background_image) {
          setCanvasBackgroundImage(tpl.background_image, { withSnapshot: false });
        }
        // Copies of groups used to share their children's ids, and the Layers
        // panel needs them unique: repeats get a new id, saved on the next save.
        const seenIds = new Set();
        const sortedLayers = [...tpl.layers]
          .sort((a, b) => a.z_index - b.z_index)
          .map((layer) => {
            if (layer.id && !seenIds.has(layer.id)) {
              seenIds.add(layer.id);
              return layer;
            }
            return { ...layer, id: makeLayerId() };
          });
        const layerObjects = await Promise.all(sortedLayers.map(buildLayerObject));
        layerObjects.forEach((obj, i) => {
          if (!obj) return;
          if (sortedLayers[i].locked) applyLock(obj, true);
          canvas.add(obj);
        });
        restoreGroups(canvas, sortedLayers, layerObjects);
        canvas.requestRenderAll();
        setLoaded(true);
        beginHistory();
      }).catch((err) => setLoadError(err.message || "Could not open this design"));
    } else {
      ensurePageBackground(canvas, backgroundColor, width, height);
      ensurePageBorder(canvas, width, height);
      applyScale(canvas, width, height, zoomOverride);
      beginHistory();
    }

    return () => canvas.dispose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Keyboard: Delete/Backspace removes the selection; Cmd/Ctrl+Z undoes,
  // Cmd/Ctrl+Shift+Z (or Ctrl+Y) redoes. Ignored while typing in fields or
  // editing text on the canvas.
  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        actionsRef.current.save?.();
        return;
      }
      const tag = e.target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || e.target.isContentEditable) {
        return;
      }
      const canvas = fabricRef.current;
      if (!canvas) return;

      if (cropRef.current.active) {
        if (e.key === "Enter") {
          e.preventDefault();
          applyCrop();
        } else if (e.key === "Escape") {
          e.preventDefault();
          cancelCrop();
        }
        return;
      }

      const active = canvas.getActiveObject();
      if (active?.isEditing) return;

      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "z") {
        e.preventDefault();
        if (e.shiftKey) redoAction();
        else undo();
        return;
      }
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "y") {
        e.preventDefault();
        redoAction();
        return;
      }
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "c") {
        actionsRef.current.copySelection?.();
        return;
      }
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "v") {
        actionsRef.current.pasteClipboard?.();
        return;
      }
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "d") {
        e.preventDefault();
        actionsRef.current.duplicateSelection?.();
        return;
      }
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "g") {
        e.preventDefault();
        if (e.shiftKey) actionsRef.current.ungroupSelection?.();
        else actionsRef.current.groupSelection?.();
        return;
      }
      if (e.key === "Delete" || e.key === "Backspace") {
        const objects = canvas.getActiveObjects();
        if (objects.length === 0) return;
        e.preventDefault();
        objects.forEach((obj) => canvas.remove(obj));
        canvas.discardActiveObject();
        canvas.requestRenderAll();
        setSelected(null);
        forceTick();
      }
      if (["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)) {
        if (!active || active.lockMovementX || active.lockMovementY) return;
        e.preventDefault();
        const step = e.shiftKey ? 10 : 1;
        if (e.key === "ArrowLeft") active.left -= step;
        else if (e.key === "ArrowRight") active.left += step;
        else if (e.key === "ArrowUp") active.top -= step;
        else if (e.key === "ArrowDown") active.top += step;
        active.setCoords();
        canvas.requestRenderAll();
        snapshot();
        forceTick();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Drag and drop from the left panel: centre a new object on the drop point
  // (scene coordinates), kept inside the page.
  const placeAt = (obj, at) => {
    if (!at) return;
    const w = obj.getScaledWidth();
    const h = obj.getScaledHeight();
    const pw = Number(width) || 1080;
    const ph = Number(height) || 1080;
    const cx = Math.min(Math.max(at.x, 0), pw);
    const cy = Math.min(Math.max(at.y, 0), ph);
    obj.set({ left: Math.round(cx - w / 2), top: Math.round(cy - h / 2) });
    obj.setCoords();
  };

  const addText = (preset = {}, at = null) => {
    const canvas = fabricRef.current;
    const obj = new fabric.Textbox(preset.text || "Edit this text", {
      left: preset.left ?? 60,
      top: preset.top ?? 60,
      width: preset.width || 300,
      fontSize: preset.fontSize || 36,
      fontWeight: preset.fontWeight || "normal",
      fill: "#000000",
      fontFamily: DEFAULT_FONT_FAMILY,
    });
    obj.layerType = "text";
    obj.layerName = preset.name || `Text ${contentObjectCount(canvas) + 1}`;
    obj.layerId = makeLayerId();
    installVerticalAlignOffset(obj);
    obj.verticalAlign = "top";
    placeAt(obj, at);
    canvas.add(obj);
    canvas.setActiveObject(obj);
    forceTick();
  };

  // `drop` = {at, frame} when dragged onto the canvas: a photo dropped on a
  // frame fills it; otherwise it lands centred on the drop point. A click
  // fills the selected frame, as before.
  const insertBankImage = (bankImage, drop = null) => {
    const canvas = fabricRef.current;
    const frame = drop ? drop.frame : selectedFrame();
    if (frame) return replaceFrameImage(frame, bankImage.url);
    // Cache-buster: the flyout grid already fetched this image without CORS
    // mode, and Chromium refuses to reuse that cached response for a
    // crossOrigin canvas request. A distinct URL forces a fresh CORS fetch.
    fabric.Image.fromURL(
      canvasUrl(bankImage.url),
      (img) => {
        const maxDim = 300;
        const scale = Math.min(maxDim / img.width, maxDim / img.height, 1);
        img.set({ left: 80, top: 80, scaleX: scale, scaleY: scale });
        img.layerType = "image";
        img.layerName = bankImage.label || `Image ${contentObjectCount(canvas) + 1}`;
        img.layerId = makeLayerId();
        img.assetSrc = bankImage.url;
        placeAt(img, drop?.at);
        canvas.add(img);
        canvas.setActiveObject(img);
        forceTick();
      },
      { crossOrigin: "anonymous" }
    );
  };

  // --- Connected apps: placeholder layers for a template type's fields ---
  const appType = designMode ? null : findAppType(integrations, integration, templateType);

  // Adds a layer already named after the field, so the designer only has to
  // style and position it. Text gets the field's example wording; an image
  // gets a grey labelled box (the whole canvas for background photos) that
  // the app replaces at render time.
  const addFieldLayer = (field, index = 0) => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    const w = Number(width) || 1080;
    const h = Number(height) || 1080;
    if (field.kind === "text") {
      addText({
        name: field.key,
        text: field.example || field.label,
        left: Math.round(w * 0.08),
        top: Math.round(h * 0.1) + index * Math.round(h * 0.12),
        width: Math.round(w * 0.84),
        fontSize: index === 0 ? Math.round(w / 16) : Math.round(w / 30),
        fontWeight: index === 0 ? "bold" : "normal",
      });
      return;
    }
    const boxW = field.full_canvas ? w : Math.round(w * 0.5);
    const boxH = field.full_canvas ? h : Math.round(h * 0.35);
    const label = String(field.label).replace(/[<>&"]/g, "");
    const svg =
      `<svg xmlns="http://www.w3.org/2000/svg" width="${boxW}" height="${boxH}">` +
      `<rect width="100%" height="100%" fill="#d9dde3"/>` +
      `<text x="50%" y="50%" fill="#6b7280" font-family="Arial" font-size="${Math.max(18, Math.round(boxW / 22))}" ` +
      `text-anchor="middle" dominant-baseline="middle">${label}</text></svg>`;
    const url = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
    fabric.Image.fromURL(url, (img) => {
      img.set({
        left: field.full_canvas ? 0 : Math.round((w - boxW) / 2),
        top: field.full_canvas ? 0 : Math.round(h * 0.45),
        scaleX: boxW / img.width,
        scaleY: boxH / img.height,
      });
      img.layerType = "image";
      img.layerName = field.key;
      img.layerId = makeLayerId();
      img.assetSrc = url;
      canvas.add(img);
      // A background photo sits under everything else (but above the page
      // background object, which is always first).
      if (field.full_canvas) {
        const firstContent = canvas.getObjects().findIndex((o) => !o.excludeFromExport);
        canvas.moveTo(img, Math.max(firstContent, 0));
      }
      canvas.setActiveObject(img);
      forceTick();
    });
  };

  const addMissingFields = () => {
    if (!appType) return;
    const canvas = fabricRef.current;
    const have = new Set(
      (canvas?.getObjects() || []).filter((o) => !o.excludeFromExport).map((o) => `${o.layerName}|${o.layerType}`)
    );
    let textIndex = 0;
    // Images first so text placeholders land on top of a background photo.
    const ordered = [...appType.fields].sort((a, b) => (a.kind === "image" ? 0 : 1) - (b.kind === "image" ? 0 : 1));
    ordered.forEach((f) => {
      if (have.has(`${f.key}|${f.kind}`)) return;
      addFieldLayer(f, f.kind === "text" ? textIndex++ : 0);
    });
  };

  // A new template opened from an app's "Create a template" link starts with
  // every field already on the canvas.
  useEffect(() => {
    if (id || !loaded || !appType || autoFieldsAddedRef.current) return;
    if (!location.state?.integration) return;
    autoFieldsAddedRef.current = true;
    addMissingFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, appType]);

  // Outline variants of the shapes: no fill, a dark border.
  const GRID_STYLE = { fill: "transparent", stroke: "#191919", strokeWidth: 2 };
  const DOT_GRID_STYLE = { fill: "transparent", stroke: "", strokeWidth: 0 };
  const OUTLINE_STYLE = { fill: "transparent", stroke: "#191919", strokeWidth: 4 };

  const addRect = (opts = {}) => {
    const canvas = fabricRef.current;
    const obj = new fabric.Rect({
      left: 60,
      top: 60,
      width: 200,
      height: 120,
      fill: "#cccccc",
      ...(opts.outline ? OUTLINE_STYLE : {}),
    });
    obj.strokeDash = "solid";
    placeAt(obj, opts.at);
    obj.layerType = "rect";
    obj.layerName = `Rectangle ${contentObjectCount(canvas) + 1}`;
    obj.layerId = makeLayerId();
    canvas.add(obj);
    canvas.setActiveObject(obj);
    forceTick();
  };

  const SHAPE_DEFAULT_SIZES = {
    grid: { width: 360, height: 360 },
    dot_grid: { width: 360, height: 360 },
    line: { width: 320, height: 6 },
    pill: { width: 260, height: 96 },
    tab: { width: 260, height: 150 },
  };

  const addShape = (kind, label, opts = {}) => {
    if (kind === "rect") return addRect(opts);
    const canvas = fabricRef.current;
    const size = SHAPE_DEFAULT_SIZES[kind] || { width: 180, height: 180 };
    const obj = makeFabricShape(kind, {
      left: 60,
      top: 60,
      ...size,
      fill: "#cccccc",
      ...(opts.outline ? OUTLINE_STYLE : {}),
      ...(kind === "grid" ? GRID_STYLE : {}),
      ...(kind === "dot_grid" ? DOT_GRID_STYLE : {}),
    });
    obj.strokeDash = "solid";
    if (GRID_KINDS.has(kind)) {
      obj.gridColumns = 6;
      obj.gridRows = 6;
    }
    if (kind === "dot_grid") {
      obj.dotColor = "#191919";
      obj.dotSize = 8;
    }
    placeAt(obj, opts.at);
    obj.layerType = "shape";
    obj.shapeKind = kind;
    obj.layerName = `${label} ${contentObjectCount(canvas) + 1}`;
    obj.layerId = makeLayerId();
    canvas.add(obj);
    canvas.setActiveObject(obj);
    forceTick();
  };

  // Image frame: an image layer clipped to a shape, starting as a grey
  // placeholder. Pick an upload or Image Bank photo while it's selected to
  // fill it (see replaceFrameImage).
  const addFrame = (kind, at = null) => {
    const canvas = fabricRef.current;
    const size = kind === "pill" ? { w: 320, h: 160 } : { w: 260, h: 260 };
    fabric.Image.fromURL(FRAME_PLACEHOLDER, (img) => {
      img.set({ left: 80, top: 80, scaleX: size.w / img.width, scaleY: size.h / img.height });
      img.layerType = "image";
      img.layerName = `Image ${contentObjectCount(canvas) + 1}`;
      img.layerId = makeLayerId();
      img.assetSrc = FRAME_PLACEHOLDER;
      img.maskShape = kind;
      applyImageCornerRadius(img);
      placeAt(img, at);
      canvas.add(img);
      canvas.setActiveObject(img);
      forceTick();
    });
  };

  // Fill a selected frame with a new photo, keeping the frame's box: the
  // photo is centre-cropped to the box ratio ("cover"), the same as the
  // server's object-fit:cover, so editor and render agree.
  const replaceFrameImage = (frame, url) => {
    const canvas = fabricRef.current;
    const boxW = frame.width * frame.scaleX;
    const boxH = frame.height * frame.scaleY;
    fabric.util.loadImage(
      canvasUrl(url),
      (el) => {
        if (!el) return;
        const iw = el.naturalWidth || el.width;
        const ih = el.naturalHeight || el.height;
        const boxRatio = boxW / boxH;
        let cropW = iw;
        let cropH = ih;
        if (iw / ih > boxRatio) cropW = ih * boxRatio;
        else cropH = iw / boxRatio;
        frame.setElement(el);
        frame.set({
          cropX: (iw - cropW) / 2,
          cropY: (ih - cropH) / 2,
          width: cropW,
          height: cropH,
          scaleX: boxW / cropW,
          scaleY: boxH / cropH,
        });
        frame.assetSrc = url;
        frame.svgSrc = null;
        frame.svgEdits = null;
        applyImageCornerRadius(frame);
        frame.setCoords();
        canvas.requestRenderAll();
        snapshot();
        forceTick();
      },
      null,
      "anonymous"
    );
  };

  const selectedFrame = () => {
    const active = fabricRef.current?.getActiveObject();
    return active?.layerType === "image" && active.maskShape ? active : null;
  };

  const DRAG_MIME = "application/x-autotemplated";
  const dragProps = (payload) => ({
    draggable: true,
    onDragStart: (e) => {
      e.dataTransfer.setData(DRAG_MIME, JSON.stringify(payload));
      e.dataTransfer.effectAllowed = "copy";
    },
  });

  const [dropActive, setDropActive] = useState(false);
  const onCanvasDragOver = (e) => {
    const types = Array.from(e.dataTransfer.types || []);
    if (!types.includes(DRAG_MIME) && !types.includes("Files")) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
    if (!dropActive) setDropActive(true);
  };
  const onCanvasDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) setDropActive(false);
  };
  const onCanvasDrop = async (e) => {
    e.preventDefault();
    setDropActive(false);
    const canvas = fabricRef.current;
    if (!canvas || cropRef.current.active) return;
    // getPointer maps the browser point through the zoom/scroll transform
    // into design coordinates.
    const at = canvas.getPointer(e.nativeEvent);
    const target = canvas.findTarget(e.nativeEvent);
    const frame = target?.layerType === "image" && target.maskShape ? target : null;
    const raw = e.dataTransfer.getData(DRAG_MIME);
    if (raw) {
      const item = JSON.parse(raw);
      if (item.type === "shape") addShape(item.kind, item.label, { outline: item.outline, at });
      else if (item.type === "frame") addFrame(item.kind, at);
      else if (item.type === "text") addText(item.preset || {}, at);
      else if (item.type === "image") insertBankImage(item.image, { at, frame });
      return;
    }
    // Image files dragged in from the desktop: upload, then place (or fill
    // the frame they were dropped on).
    const files = Array.from(e.dataTransfer.files || []).filter((f) => f.type.startsWith("image/"));
    try {
      for (const [i, file] of files.entries()) {
        const label = files.length > 1 ? `${file.name} (${i + 1} of ${files.length})` : file.name;
        const url = await uploadWithProgress(file, label);
        insertBankImage(
          { url, label: file.name.replace(/\.[^.]+$/, "") },
          { at: { x: at.x + i * 24, y: at.y + i * 24 }, frame: i === 0 ? frame : null }
        );
      }
      setUpload(null);
    } catch (err) {
      uploadFailed(err);
    }
  };

  const triggerImageUpload = () => fileInputRef.current?.click();

  const onImageFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const canvas = fabricRef.current;
    const frame = selectedFrame();
    let url;
    try {
      url = await uploadWithProgress(file);
    } catch (err) {
      return uploadFailed(err);
    }
    setUpload(null);
    if (frame) return replaceFrameImage(frame, url);
    fabric.Image.fromURL(
      canvasUrl(url),
      (img) => {
        const maxDim = 300;
        const scale = Math.min(maxDim / img.width, maxDim / img.height, 1);
        img.set({ left: 80, top: 80, scaleX: scale, scaleY: scale });
        img.layerType = "image";
        img.layerName = `Image ${contentObjectCount(canvas) + 1}`;
        img.layerId = makeLayerId();
        img.assetSrc = url;
        canvas.add(img);
        canvas.setActiveObject(img);
        forceTick();
      },
      { crossOrigin: "anonymous" }
    );
  };

  // Deletes everything in the current selection (single object or
  // multi-select ActiveSelection).
  const deleteSelected = () => {
    if (cropRef.current.active) return; // don't delete the crop rectangle
    const canvas = fabricRef.current;
    const objects = canvas.getActiveObjects();
    if (objects.length === 0) return;
    objects.forEach((obj) => canvas.remove(obj));
    canvas.discardActiveObject();
    canvas.requestRenderAll();
    setSelected(null);
    forceTick();
  };

  const bringForward = () => {
    fabricRef.current.bringToFront(fabricRef.current.getActiveObject());
    forceTick();
  };

  const sendBackward = () => {
    fabricRef.current.sendToBack(fabricRef.current.getActiveObject());
    forceTick();
  };

  const updateSelected = (props) => {
    const active = fabricRef.current.getActiveObject();
    if (!active) return;
    active.set(props);
    if (["imageAdjust", "gridColumns", "gridRows", "dotColor", "dotSize"].some((k) => k in props)) active.dirty = true;
    if ("lockAspect" in props) applyAspectLock(active, props.lockAspect);
    if ("scaleX" in props || "scaleY" in props) fixPillShape(active);
    // Corner radius is a fixed pixel value on the FINAL box — recompute
    // whenever it changes, or whenever the Width/Height fields change
    // scaleX/scaleY (drag-resize is covered separately by the
    // object:scaling listener).
    if (
      active.layerType === "image" &&
      ["cornerRadius", "maskShape", "maskCornerRadius", "scaleX", "scaleY"].some((k) => k in props)
    ) {
      applyImageCornerRadius(active);
    }
    // fabric.Textbox recalculates height from wrapped content whenever
    // font/text/width change (its _dimensionAffectingProps) — re-pin it
    // here so a manually-set height survives every other edit, not just
    // the one that set it.
    if (active.layerType === "text" && active.manualHeight != null && !("height" in props)) {
      active.set({ height: active.manualHeight });
    }
    // Fixed box, adaptive font: re-fit whenever the text, box width, or font
    // itself changes. Skipped when fitToHeight is also on — the height-fit
    // block below already keeps both dimensions fixed (wrap stays enabled,
    // current width is the wrap boundary), and this single-line/no-wrap
    // logic would fight it.
    if (active.layerType === "text" && active.fitToWidth && !active.fitToHeight) {
      if ("width" in props) {
        // A deliberate resize becomes the new fixed baseline.
        active.fitWidthBoxWidth = props.width;
      }
      if ("text" in props || "width" in props || "fontFamily" in props || "fontWeight" in props) {
        // fabric.Textbox auto-grows width to fit unbreakable text (no
        // spaces to wrap at, e.g. a plain number) at whatever the CURRENT
        // font size is — so setting width back after the fact just gets
        // silently re-grown by fabric's own dynamicMinWidth logic. Compute
        // the fitted size independently first (against the pinned target
        // width, not fabric's possibly-already-grown active.width), then
        // apply both together so fabric never sees an out-of-fit state.
        const targetWidth = active.fitWidthBoxWidth ?? active.width;
        const fitted = computeFitToWidthFontSize(active.text, targetWidth, active.fontFamily, active.fontWeight);
        active.set({ fontSize: fitted, width: targetWidth });
      } else if (active.fitWidthBoxWidth != null && active.width !== active.fitWidthBoxWidth) {
        active.set({ width: active.fitWidthBoxWidth });
      }
    }
    // Fixed box, adaptive font, other axis: re-fit whenever the text, box
    // width (the wrap boundary), font, or line spacing changes.
    if (active.layerType === "text" && active.fitToHeight) {
      if ("width" in props) {
        // A deliberate resize becomes the new fixed baseline.
        active.fitWidthBoxWidth = props.width;
      }
      if (
        "text" in props ||
        "width" in props ||
        "fontFamily" in props ||
        "fontWeight" in props ||
        "lineHeight" in props
      ) {
        // Setting `text` just above may already have grown active.width via
        // fabric's dynamicMinWidth (an unbreakable word wider than the box)
        // — re-fit against the pinned target width, not that possibly-grown
        // one, then apply both together so fabric never sees an
        // inconsistent state (matches the fitToWidth-alone logic above).
        const targetWidth = active.fitWidthBoxWidth ?? active.width;
        const targetHeight = active.manualHeight ?? active.height;
        const fitted = computeFitToHeightFontSize(
          active.text,
          targetWidth,
          targetHeight,
          active.fontFamily,
          active.fontWeight,
          active.lineHeight
        );
        active.set({ fontSize: fitted, width: targetWidth });
        // fontSize/width are dimension-affecting — fabric.Text's own set()
        // reruns initDimensions() (recalculating height from wrapped
        // content) as a POST-STEP after every key in a .set() call, even
        // ones bundled into the very same call — so height has to be
        // re-pinned in its own separate, later call to actually stick.
        active.set({ height: targetHeight });
        active.manualHeight = targetHeight;
      } else if (active.fitWidthBoxWidth != null && active.width !== active.fitWidthBoxWidth) {
        active.set({ width: active.fitWidthBoxWidth });
      }
    }
    fabricRef.current.requestRenderAll();
    snapshot();
    forceTick();
  };

  // Applies superscript/subscript to the current text selection, or the
  // whole string if nothing's selected (e.g. the box isn't in edit mode).
  // "normal" clears back to the object's own base size/baseline.
  const applyTextScript = (kind) => {
    const canvas = fabricRef.current;
    const active = canvas?.getActiveObject();
    if (!active || active.layerType !== "text") return;
    const hasSelection = active.isEditing && active.selectionEnd > active.selectionStart;
    const start = hasSelection ? active.selectionStart : 0;
    const end = hasSelection ? active.selectionEnd : (active.text || "").length;
    if (start >= end) return;
    if (kind === "super") active.setSuperscript(start, end);
    else if (kind === "sub") active.setSubscript(start, end);
    else active.setSelectionStyles({ fontSize: active.fontSize, deltaY: 0 }, start, end);
    canvas.requestRenderAll();
    snapshot();
    forceTick();
  };

  // --- image crop mode (reveal the full source, drag a crop
  // window over it, Apply bakes the window into the image's cropX/cropY) ---

  const CROP_OVERLAY_COLOR = "rgba(0,0,0,0.55)";

  const naturalSize = (imageObj) => ({
    w: imageObj.getElement?.()?.naturalWidth || imageObj._element?.naturalWidth || imageObj.width,
    h: imageObj.getElement?.()?.naturalHeight || imageObj._element?.naturalHeight || imageObj.height,
  });

  const updateCropOverlay = () => {
    const { image, rect, overlays } = cropRef.current;
    if (!image || !rect || overlays.length !== 4) return;
    const { w: natW, h: natH } = naturalSize(image);
    const ix = image.left, iy = image.top;
    const iw = natW * image.scaleX, ih = natH * image.scaleY;
    const rx = rect.left, ry = rect.top;
    const rw = rect.width * rect.scaleX, rh = rect.height * rect.scaleY;
    const [top, bottom, left, right] = overlays;
    top.set({ left: ix, top: iy, width: iw, height: Math.max(0, ry - iy) });
    bottom.set({ left: ix, top: ry + rh, width: iw, height: Math.max(0, iy + ih - (ry + rh)) });
    left.set({ left: ix, top: ry, width: Math.max(0, rx - ix), height: rh });
    right.set({ left: rx + rw, top: ry, width: Math.max(0, ix + iw - (rx + rw)), height: rh });
    overlays.forEach((o) => o.setCoords());
    fabricRef.current?.requestRenderAll();
  };

  // Keeps the crop rectangle inside the full (uncropped) image bounds.
  const clampCropRect = () => {
    const { image, rect } = cropRef.current;
    if (!image || !rect) return;
    const { w: natW, h: natH } = naturalSize(image);
    const minLeft = image.left;
    const minTop = image.top;
    const maxRight = image.left + natW * image.scaleX;
    const maxBottom = image.top + natH * image.scaleY;

    let w = Math.min(rect.width * rect.scaleX, maxRight - minLeft);
    let h = Math.min(rect.height * rect.scaleY, maxBottom - minTop);
    w = Math.max(w, 12);
    h = Math.max(h, 12);
    const left = Math.max(minLeft, Math.min(rect.left, maxRight - w));
    const top = Math.max(minTop, Math.min(rect.top, maxBottom - h));

    rect.set({ left, top, width: w, height: h, scaleX: 1, scaleY: 1 });
    rect.setCoords();
  };

  const enterCropMode = (imageObj) => {
    const canvas = fabricRef.current;
    if (!canvas || !imageObj || imageObj.layerType !== "image" || cropRef.current.active) return;
    if (Math.abs((imageObj.angle || 0) % 360) > 0.5) return; // see disabled-button hint

    const { w: natW, h: natH } = naturalSize(imageObj);
    const backup = {
      left: imageObj.left,
      top: imageObj.top,
      cropX: imageObj.cropX || 0,
      cropY: imageObj.cropY || 0,
      width: imageObj.width,
      height: imageObj.height,
    };

    // Shift so the currently-visible crop window stays anchored on screen,
    // then reveal the full source image around it.
    const cropRectLeft = imageObj.left;
    const cropRectTop = imageObj.top;
    const cropRectW = imageObj.width * imageObj.scaleX;
    const cropRectH = imageObj.height * imageObj.scaleY;
    const newLeft = imageObj.left - (imageObj.cropX || 0) * imageObj.scaleX;
    const newTop = imageObj.top - (imageObj.cropY || 0) * imageObj.scaleY;

    imageObj.set({
      left: newLeft,
      top: newTop,
      cropX: 0,
      cropY: 0,
      width: natW,
      height: natH,
      selectable: false,
      evented: false,
    });
    imageObj.setCoords();

    const prevEvented = new Map();
    canvas.getObjects().forEach((o) => {
      if (o === imageObj) return;
      prevEvented.set(o, o.evented);
      o.evented = false;
    });
    canvas.discardActiveObject();

    const overlays = [0, 1, 2, 3].map(
      () =>
        new fabric.Rect({
          fill: CROP_OVERLAY_COLOR,
          selectable: false,
          evented: false,
          excludeFromExport: true,
        })
    );
    overlays.forEach((o) => canvas.add(o));

    const rect = new fabric.Rect({
      left: cropRectLeft,
      top: cropRectTop,
      width: cropRectW,
      height: cropRectH,
      fill: "rgba(0,0,0,0)",
      stroke: "#ffffff",
      strokeDashArray: [6, 4],
      strokeWidth: 1.5,
      cornerColor: "#00a69c",
      cornerStrokeColor: "#ffffff",
      transparentCorners: false,
      cornerStyle: "circle",
      cornerSize: 10,
      lockRotation: true,
      excludeFromExport: true,
    });
    rect.setControlsVisibility({ mtr: false });
    rect.on("moving", () => {
      clampCropRect();
      updateCropOverlay();
    });
    rect.on("scaling", () => {
      clampCropRect();
      updateCropOverlay();
    });
    canvas.add(rect);
    canvas.setActiveObject(rect);

    cropRef.current = { active: true, image: imageObj, rect, overlays, backup, prevEvented };
    setCropping(true);
    updateCropOverlay();
    canvas.requestRenderAll();
    forceTick();
  };

  const exitCropMode = ({ reselect } = {}) => {
    const canvas = fabricRef.current;
    const { rect, overlays, prevEvented, image } = cropRef.current;
    if (rect) canvas.remove(rect);
    overlays.forEach((o) => canvas.remove(o));
    prevEvented.forEach((val, obj) => {
      obj.evented = val;
    });
    cropRef.current = {
      active: false,
      image: null,
      rect: null,
      overlays: [],
      backup: null,
      prevEvented: new Map(),
    };
    setCropping(false);
    if (reselect && image) {
      canvas.setActiveObject(image);
      setSelected(image);
    }
    canvas.requestRenderAll();
    snapshot();
    forceTick();
  };

  // The visible part of an image as fractions of the photo, saved so a photo
  // moved or resized inside its frame renders the same way.
  const imageCrop = (obj) => {
    if (isEmptyFrame(obj)) return null;
    const { w, h } = naturalSize(obj);
    if (!w || !h) return null;
    const round = (v) => Math.round(v * 100000) / 100000;
    return [round((obj.cropX || 0) / w), round((obj.cropY || 0) / h), round(obj.width / w), round(obj.height / h)];
  };

  // Shows the photo at one uniform scale that covers the frame's box, keeping
  // the same centre and at least the current zoom.
  const coverFrame = (frame) => {
    const { w: natW, h: natH } = naturalSize(frame);
    if (!natW || !natH) return;
    const boxW = frame.width * frame.scaleX;
    const boxH = frame.height * frame.scaleY;
    const scale = Math.max(boxW / natW, boxH / natH, Math.max(frame.scaleX, frame.scaleY));
    const w = boxW / scale;
    const h = boxH / scale;
    const cx = (frame.cropX || 0) + frame.width / 2;
    const cy = (frame.cropY || 0) + frame.height / 2;
    frame.set({
      cropX: Math.max(0, Math.min(natW - w, cx - w / 2)),
      cropY: Math.max(0, Math.min(natH - h, cy - h / 2)),
      width: w,
      height: h,
      scaleX: scale,
      scaleY: scale,
    });
    applyImageCornerRadius(frame);
    frame.setCoords();
  };

  // Adjust: the whole photo shows faded around the frame and can
  // be dragged or corner-resized; the frame itself stays put and shows the
  // part of the photo inside it. Uses the crop mode's state and bar.
  const enterAdjustMode = (frame) => {
    const canvas = fabricRef.current;
    if (!canvas || !frame?.maskShape || isEmptyFrame(frame) || cropRef.current.active) return;
    if (Math.abs((frame.angle || 0) % 360) > 0.5) return;
    const backup = {
      cropX: frame.cropX || 0,
      cropY: frame.cropY || 0,
      width: frame.width,
      height: frame.height,
      scaleX: frame.scaleX,
      scaleY: frame.scaleY,
    };
    coverFrame(frame);
    const { w: natW, h: natH } = naturalSize(frame);
    const boxW = frame.width * frame.scaleX;
    const boxH = frame.height * frame.scaleY;
    const scale = frame.scaleX;
    // A flipped frame shows its crop mirrored, so the photo is laid out
    // mirrored around it too.
    const offsetX = frame.flipX ? natW - frame.cropX - frame.width : frame.cropX;
    const offsetY = frame.flipY ? natH - frame.cropY - frame.height : frame.cropY;
    const photo = new fabric.Image(frame.getElement(), {
      left: frame.left - offsetX * scale,
      top: frame.top - offsetY * scale,
      scaleX: scale,
      scaleY: scale,
      flipX: frame.flipX,
      flipY: frame.flipY,
      opacity: 0.45,
      lockRotation: true,
      lockScalingFlip: true,
      borderColor: "#00a69c",
      cornerColor: "#00a69c",
      cornerStrokeColor: "#ffffff",
      transparentCorners: false,
      cornerStyle: "circle",
      cornerSize: 10,
      excludeFromExport: true,
    });
    photo.imageAdjust = frame.imageAdjust;
    photo.setControlsVisibility({ mt: false, mb: false, ml: false, mr: false, mtr: false });

    // The photo must always cover the frame: no smaller than it, no gaps.
    const minScale = Math.max(boxW / natW, boxH / natH);
    const sync = () => {
      const s = Math.max(photo.scaleX, minScale);
      photo.set({
        scaleX: s,
        scaleY: s,
        left: Math.min(frame.left, Math.max(frame.left + boxW - natW * s, photo.left)),
        top: Math.min(frame.top, Math.max(frame.top + boxH - natH * s, photo.top)),
      });
      photo.setCoords();
      const w = boxW / s;
      const h = boxH / s;
      const x = (frame.left - photo.left) / s;
      const y = (frame.top - photo.top) / s;
      frame.set({
        cropX: frame.flipX ? natW - x - w : x,
        cropY: frame.flipY ? natH - y - h : y,
        width: w,
        height: h,
        scaleX: s,
        scaleY: s,
      });
      applyImageCornerRadius(frame);
      frame.setCoords();
      setAdjustZoom(s / minScale);
      canvas.requestRenderAll();
    };
    photo.on("moving", sync);
    photo.on("scaling", sync);
    // The bar's slider: resizes around the frame's centre. The corner handles
    // can be off the page, where they can't be grabbed.
    const zoomTo = (zoom) => {
      const cx = frame.left + boxW / 2;
      const cy = frame.top + boxH / 2;
      const s = minScale * zoom;
      photo.set({
        left: cx - ((cx - photo.left) / photo.scaleX) * s,
        top: cy - ((cy - photo.top) / photo.scaleY) * s,
        scaleX: s,
        scaleY: s,
      });
      sync();
    };

    const prevEvented = new Map();
    canvas.getObjects().forEach((o) => {
      prevEvented.set(o, o.evented);
      o.evented = false;
    });
    canvas.discardActiveObject();
    canvas.add(photo);
    canvas.setActiveObject(photo);
    cropRef.current = { active: true, mode: "adjust", image: frame, rect: photo, overlays: [], backup, prevEvented, zoomTo };
    setAdjustZoom(scale / minScale);
    setCropping(true);
    canvas.requestRenderAll();
    forceTick();
  };

  // Takes the photo out of a frame as its own image; the frame goes back to
  // empty.
  const detachFrameImage = (frame) => {
    const canvas = fabricRef.current;
    if (!canvas || !frame?.maskShape || isEmptyFrame(frame) || frame.group) return;
    // The whole photo comes out, at the size it had in the frame and placed
    // so the part that was showing stays where it was (as in adjust mode).
    // Built from the frame's loaded photo rather than cloned: no reload.
    const { w: natW, h: natH } = naturalSize(frame);
    const offsetX = frame.flipX ? natW - frame.cropX - frame.width : frame.cropX;
    const offsetY = frame.flipY ? natH - frame.cropY - frame.height : frame.cropY;
    const radians = fabric.util.degreesToRadians(frame.angle || 0);
    const cos = Math.cos(radians);
    const sin = Math.sin(radians);
    const photo = new fabric.Image(frame.getElement(), {
      ...Object.fromEntries(
        ["angle", "flipX", "flipY", "opacity", "scaleX", "scaleY", "shadow"].map((k) => [k, frame[k]])
      ),
      // The offset runs along the frame's own (possibly rotated) axes.
      left: frame.left - (offsetX || 0) * frame.scaleX * cos + (offsetY || 0) * frame.scaleY * sin,
      top: frame.top - (offsetX || 0) * frame.scaleX * sin - (offsetY || 0) * frame.scaleY * cos,
      cropX: 0,
      cropY: 0,
      width: natW,
      height: natH,
      crossOrigin: "anonymous",
    });
    Object.assign(photo, {
      layerType: "image",
      layerId: makeLayerId(),
      assetSrc: frame.assetSrc,
      svgSrc: frame.svgSrc,
      svgEdits: frame.svgEdits,
      imageAdjust: frame.imageAdjust,
      shadowColor: frame.shadowColor,
      shadowOpacity: frame.shadowOpacity,
      cornerRadius: 0,
    });
    photo.layerName = uniqueLayerName(canvas, photo);
    canvas.insertAt(photo, canvas.getObjects().indexOf(frame) + 1);

    const boxW = frame.width * frame.scaleX;
    const boxH = frame.height * frame.scaleY;
    fabric.util.loadImage(FRAME_PLACEHOLDER, (el) => {
      if (!el) return;
      frame.setElement(el);
      frame.set({ cropX: 0, cropY: 0, width: el.width, height: el.height, scaleX: boxW / el.width, scaleY: boxH / el.height });
      frame.assetSrc = FRAME_PLACEHOLDER;
      frame.svgSrc = null;
      frame.svgEdits = null;
      applyImageCornerRadius(frame);
      frame.setCoords();
      canvas.setActiveObject(photo);
      setSelected(photo);
      canvas.requestRenderAll();
      snapshot();
      forceTick();
    });
  };

  const applyCrop = () => {
    const { active, image, rect } = cropRef.current;
    if (!active) return;
    if (cropRef.current.mode === "adjust") {
      exitCropMode({ reselect: true });
      return;
    }
    const cropX = (rect.left - image.left) / image.scaleX;
    const cropY = (rect.top - image.top) / image.scaleY;
    const cropW = (rect.width * rect.scaleX) / image.scaleX;
    const cropH = (rect.height * rect.scaleY) / image.scaleY;
    const finalLeft = rect.left;
    const finalTop = rect.top;
    const finalWidth = rect.width * rect.scaleX;
    const finalHeight = rect.height * rect.scaleY;

    // Fabric's cropX/cropY/width/height give instant visual feedback, but
    // they're client-only — buildPayload() only ever serializes an image
    // layer's plain src, so a crop left in this form vanishes on reload.
    // Apply it now for responsiveness, then bake it into a real cropped
    // file in the background so what gets saved matches what's shown.
    image.set({
      cropX,
      cropY,
      width: cropW,
      height: cropH,
      left: finalLeft,
      top: finalTop,
      selectable: true,
      evented: true,
    });
    // Cropping changes the image's own natural width/height, which a
    // rounded corner's clip is sized against — recompute so the radius
    // doesn't end up applied to the pre-crop dimensions.
    applyImageCornerRadius(image);
    image.setCoords();
    exitCropMode({ reselect: true });
    bakeCroppedImage(image, { cropX, cropY, cropW, cropH, finalLeft, finalTop, finalWidth, finalHeight });
  };

  const bakeCroppedImage = async (image, { cropX, cropY, cropW, cropH, finalLeft, finalTop, finalWidth, finalHeight }) => {
    try {
      const off = document.createElement("canvas");
      off.width = Math.max(1, Math.round(cropW));
      off.height = Math.max(1, Math.round(cropH));
      off
        .getContext("2d")
        .drawImage(image.getElement(), cropX, cropY, cropW, cropH, 0, 0, off.width, off.height);
      const blob = await new Promise((resolve) => off.toBlob(resolve, "image/png"));
      const { url } = await uploadAsset(new File([blob], "cropped.png", { type: "image/png" }));

      await new Promise((resolve) =>
        image.setSrc(
          canvasUrl(url),
          () => {
            image.set({
              cropX: 0,
              cropY: 0,
              width: off.width,
              height: off.height,
              scaleX: finalWidth / off.width,
              scaleY: finalHeight / off.height,
              left: finalLeft,
              top: finalTop,
            });
            image.assetSrc = url;
            image.svgSrc = null;
            image.svgEdits = null;
            applyImageCornerRadius(image);
            image.setCoords();
            resolve();
          },
          { crossOrigin: "anonymous" }
        )
      );
      fabricRef.current?.requestRenderAll();
      snapshot(true);
      forceTick();
    } catch (err) {
      console.error("Failed to save cropped image", err);
    }
  };

  const cancelCrop = () => {
    const { active, image, backup } = cropRef.current;
    if (!active) return;
    image.set({ ...backup, selectable: true, evented: true });
    if (cropRef.current.mode === "adjust") applyImageCornerRadius(image);
    image.setCoords();
    exitCropMode({ reselect: true });
  };

  // Corner-radius edits change the object's geometry, so the fabric object is
  // rebuilt in place (same position/rotation/stacking) rather than mutated.
  const reshapeSelected = ({ cornerRadii, cornerRadius }) => {
    const canvas = fabricRef.current;
    const old = canvas.getActiveObject();
    if (!old) return;

    const width = (old.width || 1) * (old.scaleX || 1);
    const height = (old.height || 1) * (old.scaleY || 1);
    const placement = { left: old.left, top: old.top, angle: old.angle || 0 };
    const style = {
      fill: old.fill,
      stroke: old.stroke || "",
      strokeWidth: old.strokeWidth || 0,
    };

    let replacement;
    if (old.layerType === "rect" && cornerRadii) {
      const uniform = cornerRadii.every((r) => r === cornerRadii[0]);
      replacement = uniform
        ? new fabric.Rect({
            ...placement, ...style, width, height,
            rx: cornerRadii[0], ry: cornerRadii[0],
          })
        : new fabric.Path(roundedRectPath(width, height, cornerRadii), {
            ...placement, ...style,
          });
      replacement.cornerRadii = cornerRadii;
    } else if (old.layerType === "shape" && cornerRadius !== undefined) {
      replacement = makeFabricShape(old.shapeKind, {
        ...placement, ...style, width, height, cornerRadius,
      });
      replacement.set(placement);
      replacement.cornerRadius = cornerRadius;
    } else {
      return;
    }

    replacement.layerType = old.layerType;
    replacement.layerName = old.layerName;
    replacement.layerId = old.layerId;
    replacement.shapeKind = old.shapeKind;

    const index = canvas.getObjects().indexOf(old);
    canvas.remove(old);
    canvas.insertAt(replacement, index);
    canvas.setActiveObject(replacement);
    setSelected(replacement);
    canvas.requestRenderAll();
    forceTick();
  };

  const DEFAULT_GRADIENT = { kind: "radial", color_start: "#000000", color_end: "#000000", position: "center", end_opacity: 0 };

  const updateGradient = (patch) => {
    const next = { ...(backgroundGradientRef.current || DEFAULT_GRADIENT), ...patch };
    setBackgroundGradient(next);
    ensureGradientOverlay(fabricRef.current, next, Number(width), Number(height));
    fabricRef.current.requestRenderAll();
    snapshot();
  };

  const toggleGradient = (enabled) => {
    const next = enabled ? backgroundGradientRef.current || DEFAULT_GRADIENT : null;
    setBackgroundGradient(next);
    ensureGradientOverlay(fabricRef.current, next, Number(width), Number(height));
    fabricRef.current.requestRenderAll();
    snapshot();
  };

  // --- clipboard / grouping / lock / arrange actions ---

  // clipboardRef keeps the copy too, for when the browser blocks storage.
  // A newer copy made in another tab wins.
  const readClipboard = () => {
    try {
      const stored = JSON.parse(localStorage.getItem(CLIPBOARD_KEY));
      if (stored?.json && (!clipboardRef.current || stored.copiedAt >= clipboardRef.current.copiedAt)) return stored;
    } catch {
      // storage unavailable or corrupt: fall back to this page's copy
    }
    return clipboardRef.current;
  };
  const writeClipboard = (clip) => {
    clipboardRef.current = clip;
    try {
      localStorage.setItem(CLIPBOARD_KEY, JSON.stringify(clip));
    } catch {
      // too big or storage blocked: the copy still works on this page
    }
  };

  const copySelection = (thenPaste = false) => {
    const active = fabricRef.current?.getActiveObject();
    if (!active) return;
    writeClipboard({ json: active.toObject(HISTORY_PROPS), source: id, copiedAt: Date.now(), pastes: 0 });
    if (thenPaste) pasteClipboard();
  };

  // A pasted object is a new layer: a group's children need their own ids
  // and names too, or the copy shares them with the original. Each child is
  // named after the canvas's current layers, so renames run after canvas.add.
  const prepareCopy = (obj) => {
    obj.layerId = makeLayerId();
    if (obj.type === "group") obj.forEachObject(prepareCopy);
    if (obj.layerType === "text") {
      installVerticalAlignOffset(obj);
      restorePinnedTextHeight(obj);
    }
    if (obj.layerType === "image") applyImageCornerRadius(obj);
    if (obj.lockAspect) applyAspectLock(obj, true);
  };
  const renameCopy = (canvas, obj) => {
    obj.layerName = uniqueLayerName(canvas, obj);
    if (obj.type === "group") obj.forEachObject((child) => renameCopy(canvas, child));
  };

  const pasteClipboard = () => {
    const canvas = fabricRef.current;
    const clip = readClipboard();
    if (!canvas || !clip) return;
    // In the same design each paste cascades a little down-right of the
    // previous one; in another design the first paste keeps its position.
    const offset = clip.source === id ? 24 * (clip.pastes + 1) : 24 * clip.pastes;
    writeClipboard({ ...clip, pastes: clip.pastes + 1 });
    fabric.util.enlivenObjects([clip.json], ([cloned]) => {
      if (!cloned) return;
      canvas.discardActiveObject();
      cloned.set({ left: clip.json.left + offset, top: clip.json.top + offset, evented: true });
      // A copy from a bigger design can land off this page: center it instead.
      const box = cloned.getBoundingRect(true, true);
      const pageW = Number(widthRef.current);
      const pageH = Number(heightRef.current);
      if (box.left >= pageW || box.top >= pageH || box.left + box.width <= 0 || box.top + box.height <= 0) {
        const cascade = 24 * clip.pastes;
        cloned.set({
          left: cloned.left + (pageW - box.width) / 2 - box.left + cascade,
          top: cloned.top + (pageH - box.height) / 2 - box.top + cascade,
        });
      }
      if (cloned.type === "activeSelection") {
        cloned.canvas = canvas;
        cloned.forEachObject((obj) => {
          prepareCopy(obj);
          canvas.add(obj);
          renameCopy(canvas, obj);
        });
        cloned.setCoords();
      } else {
        prepareCopy(cloned);
        canvas.add(cloned);
        renameCopy(canvas, cloned);
      }
      canvas.setActiveObject(cloned);
      setSelected(cloned);
      canvas.requestRenderAll();
      forceTick();
    });
  };

  const duplicateSelection = () => copySelection(true);

  // Union / intersection of the selected shapes and frames into one new
  // custom frame (frameGeometry.js), replacing them. A photo already in one
  // of the frames carries over; otherwise it's an empty placeholder frame.
  const combineIntoFrame = (op) => {
    const canvas = fabricRef.current;
    const order = canvas.getObjects();
    const objs = canvas
      .getActiveObjects()
      .filter(isFrameOperand)
      .sort((a, b) => order.indexOf(a) - order.indexOf(b));
    if (objs.length < 2) return;
    const result = combineOutlines(objs, op);
    if (!result) {
      setCanvasNotice(
        op === "intersection"
          ? "Those shapes don't overlap, so there's nothing to intersect."
          : op === "difference"
            ? "Nothing is left after cutting. Put the shape to cut from at the bottom, with the cutting shapes on top."
            : "Couldn't combine those shapes."
      );
      return;
    }
    const all = canvas.getObjects();
    const topIndex = Math.max(...objs.map((o) => all.indexOf(o)));
    const photo = objs.find((o) => o.layerType === "image" && o.assetSrc && o.assetSrc !== FRAME_PLACEHOLDER);
    fabric.Image.fromURL(FRAME_PLACEHOLDER, (img) => {
      // One undo step for the whole swap.
      historyRef.current.busy = true;
      canvas.discardActiveObject();
      objs.forEach((o) => canvas.remove(o));
      img.set({
        left: result.left,
        top: result.top,
        scaleX: result.width / img.width,
        scaleY: result.height / img.height,
      });
      img.layerType = "image";
      img.layerName = `Frame ${contentObjectCount(canvas) + 1}`;
      img.layerId = makeLayerId();
      img.assetSrc = FRAME_PLACEHOLDER;
      img.maskShape = "custom";
      img.maskPath = result.path;
      img.maskBasePath = result.path;
      img.maskCornerRadius = 0;
      applyImageCornerRadius(img);
      canvas.insertAt(img, Math.max(0, topIndex - (objs.length - 1)));
      canvas.setActiveObject(img);
      setSelected(img);
      historyRef.current.busy = false;
      if (photo) replaceFrameImage(img, photo.assetSrc);
      else snapshot();
      canvas.requestRenderAll();
      forceTick();
    });
  };

  const groupSelection = () => {
    const canvas = fabricRef.current;
    const active = canvas.getActiveObject();
    if (!active || active.type !== "activeSelection") return;
    const group = active.toGroup();
    group.layerType = "group";
    group.layerName = `Group ${contentObjectCount(canvas)}`;
    group.layerId = makeLayerId();
    setSelected(group);
    canvas.requestRenderAll();
    snapshot();
    forceTick();
  };

  const ungroupSelection = () => {
    const canvas = fabricRef.current;
    const active = canvas.getActiveObject();
    if (!active || active.type !== "group") return;
    const selection = active.toActiveSelection();
    setSelected(selection);
    canvas.requestRenderAll();
    snapshot();
    forceTick();
  };

  const toggleLockSelection = () => {
    const canvas = fabricRef.current;
    const objects = canvas.getActiveObjects();
    if (objects.length === 0) return;
    const lock = !objects.some((obj) => obj.locked);
    objects.forEach((obj) => applyLock(obj, lock));
    if (lock) {
      canvas.discardActiveObject();
      setSelected(null);
    }
    canvas.requestRenderAll();
    snapshot();
    forceTick();
  };

  // Runs a mutation over the selection in absolute coordinates, then
  // restores the multi-selection.
  const withAbsoluteSelection = (fn, minCount = 2) => {
    const canvas = fabricRef.current;
    const objects = canvas.getActiveObjects();
    if (objects.length < minCount) return;
    canvas.discardActiveObject();
    fn(objects.map((o) => ({ o, b: o.getBoundingRect(true, true) })));
    objects.forEach((o) => o.setCoords());
    const selection = new fabric.ActiveSelection(objects, { canvas });
    canvas.setActiveObject(selection);
    setSelected(selection);
    canvas.requestRenderAll();
    snapshot();
    forceTick();
  };

  const alignSelection = (mode) =>
    withAbsoluteSelection((items) => {
      const minL = Math.min(...items.map((i) => i.b.left));
      const maxR = Math.max(...items.map((i) => i.b.left + i.b.width));
      const minT = Math.min(...items.map((i) => i.b.top));
      const maxB = Math.max(...items.map((i) => i.b.top + i.b.height));
      items.forEach(({ o, b }) => {
        if (mode === "left") o.left += minL - b.left;
        if (mode === "centerH") o.left += (minL + maxR) / 2 - (b.left + b.width / 2);
        if (mode === "right") o.left += maxR - (b.left + b.width);
        if (mode === "top") o.top += minT - b.top;
        if (mode === "middle") o.top += (minT + maxB) / 2 - (b.top + b.height / 2);
        if (mode === "bottom") o.top += maxB - (b.top + b.height);
      });
    });

  const distributeSelection = (axis) =>
    withAbsoluteSelection((items) => {
      const key = axis === "x" ? "left" : "top";
      const size = axis === "x" ? "width" : "height";
      const pos = axis === "x" ? "left" : "top";
      const sorted = [...items].sort((a, b) => a.b[key] - b.b[key]);
      const first = sorted[0];
      const last = sorted[sorted.length - 1];
      const span = last.b[key] + last.b[size] - first.b[key];
      const total = sorted.reduce((sum, i) => sum + i.b[size], 0);
      const gap = (span - total) / (sorted.length - 1);
      let cursor = first.b[key] + first.b[size] + gap;
      sorted.slice(1, -1).forEach(({ o, b }) => {
        o[pos] += cursor - b[key];
        cursor += b[size] + gap;
      });
    }, 3);

  actionsRef.current = {
    copySelection,
    pasteClipboard,
    enterAdjustMode,
    applyCrop,
    coverFrame,
    duplicateSelection,
    groupSelection,
    ungroupSelection,
    combineIntoFrame,
    toggleLockSelection,
  };

  // Swap the selected SVG's picture for its recoloured version, keeping its
  // box. Colour pickers fire quickly: only the latest edit is applied.
  const svgEditRef = useRef(null);
  const updateSvgColors = async (edits) => {
    const canvas = fabricRef.current;
    const image = canvas?.getActiveObject();
    if (!image) return;
    const source = image.svgSrc || image.assetSrc;
    const token = {};
    svgEditRef.current = token;
    const text = await loadSvgText(source);
    if (svgEditRef.current !== token) return;
    const edited = Object.keys(edits).length > 0;
    const url = edited ? recolorSvg(text, edits) : source;
    const { width, height, cropX, cropY, scaleX, scaleY } = image;
    image.setSrc(
      canvasUrl(url),
      () => {
        image.set({ width, height, cropX, cropY, scaleX, scaleY });
        image.assetSrc = url;
        image.svgSrc = edited ? source : null;
        image.svgEdits = edited ? edits : null;
        applyImageCornerRadius(image);
        image.setCoords();
        canvas.requestRenderAll();
        snapshot();
        forceTick();
      },
      { crossOrigin: "anonymous" }
    );
  };

  // A click on the empty stage around the design clears the selection. The
  // stage's own scrollbars send their clicks here too, so those are left out.
  const onStageMouseDown = (e) => {
    const stage = e.currentTarget;
    if (e.target !== stage) return;
    if (cropRef.current.active) {
      if (cropRef.current.mode === "adjust") applyCrop();
      return;
    }
    const rect = stage.getBoundingClientRect();
    if (e.clientX - rect.left >= stage.clientWidth || e.clientY - rect.top >= stage.clientHeight) return;
    const canvas = fabricRef.current;
    if (!canvas?.getActiveObject()) return;
    canvas.discardActiveObject();
    canvas.requestRenderAll();
    setSelected(null);
    forceTick();
  };

  // Layers panel rename. A data field's name is how job content finds it, so
  // those are renamed in the Data fields tab, which keeps that link valid.
  const [renaming, setRenaming] = useState(null); // { obj, text }
  const renameDoneRef = useRef(false);
  const startRename = (obj) => {
    setCtxMenu(null);
    if (!designMode && obj.dataField) {
      setActiveTab("fields");
      setLeftOpen(true);
      return;
    }
    renameDoneRef.current = false;
    setLayersOpen(true);
    setRenaming({ obj, text: obj.layerName || "" });
  };
  // Names stay unique ("Logo", "Logo 2") and can't take a data field's name:
  // either would make the layer pick up that field's content.
  const finishRename = (save) => {
    if (renameDoneRef.current || !renaming) return;
    renameDoneRef.current = true;
    const { obj, text } = renaming;
    setRenaming(null);
    const wanted = text.trim();
    if (!save || !wanted || wanted === obj.layerName) return;
    const taken = new Set((appType?.fields || []).map((f) => f.key));
    const collect = (o) => {
      if (o !== obj) taken.add(o.layerName);
      if (o.type === "group") o.forEachObject(collect);
    };
    fabricRef.current.getObjects().forEach(collect);
    let name = wanted;
    for (let n = 2; taken.has(name); n += 1) name = `${wanted} ${n}`;
    obj.layerName = name;
    snapshot();
    forceTick();
  };

  // Layers panel: click selects, ⌘/Ctrl/Shift+click adds to the selection.
  const selectLayers = (objs) => {
    const canvas = fabricRef.current;
    canvas.discardActiveObject();
    const next = objs.length > 1 ? new fabric.ActiveSelection(objs, { canvas }) : objs[0];
    if (next) canvas.setActiveObject(next);
    canvas.requestRenderAll();
    setSelected(next || null);
    forceTick();
  };
  const onLayerClick = (e, obj) => {
    const actives = fabricRef.current.getActiveObjects();
    if (!(e.metaKey || e.ctrlKey || e.shiftKey)) return selectLayers([obj]);
    selectLayers(actives.includes(obj) ? actives.filter((o) => o !== obj) : [...actives, obj]);
  };
  // Right-click in the Layers panel: the canvas's menu, for that layer.
  const openLayerMenu = (e, obj) => {
    e.preventDefault();
    if (cropRef.current.active) return;
    if (!fabricRef.current.getActiveObjects().includes(obj)) selectLayers([obj]);
    setCtxMenu({ x: e.clientX, y: e.clientY });
  };

  const openContextMenu = (e) => {
    e.preventDefault();
    if (cropRef.current.active) return;
    const canvas = fabricRef.current;
    if (!canvas) return;
    const target = canvas.findTarget(e.nativeEvent, false);
    if (target) {
      const actives = canvas.getActiveObjects();
      if (!actives.includes(target)) {
        canvas.setActiveObject(target);
        setSelected(target);
        canvas.requestRenderAll();
      }
    }
    setCtxMenu({ x: e.clientX, y: e.clientY });
    forceTick();
  };

  // Close the context menu on outside click / Escape.
  useEffect(() => {
    if (!ctxMenu) return;
    const close = (e) => {
      if (!e.target.closest?.(".ctx-menu")) setCtxMenu(null);
    };
    const onKey = (e) => e.key === "Escape" && setCtxMenu(null);
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", onKey);
    };
  }, [ctxMenu]);

  const buildPayload = () => {
    const canvas = fabricRef.current;

    const shadowFields = (obj) =>
      obj.shadow
        ? {
            shadow_color: obj.shadowColor || "#000000",
            shadow_opacity: obj.shadowOpacity ?? 0.4,
            shadow_blur: obj.shadow.blur,
            shadow_offset_x: obj.shadow.offsetX,
            shadow_offset_y: obj.shadow.offsetY,
          }
        : { shadow_color: null };

    const serializeObject = (obj, index, abs) => {
      const common = {
        id: obj.layerId || makeLayerId(),
        type: obj.layerType,
        name: obj.layerName || "",
        data_field: Boolean(obj.dataField),
        x: abs.x,
        y: abs.y,
        width: abs.width,
        height: abs.height,
        rotation: abs.rotation,
        flip_x: abs.flipX,
        flip_y: abs.flipY,
        z_index: index,
        locked: Boolean(obj.locked),
        opacity: obj.opacity ?? 1,
        hidden: obj.visible === false,
        lock_aspect: Boolean(obj.lockAspect),
      };
      if (obj.layerType === "text") {
        return {
          ...common,
          text: obj.text,
          font_family: obj.fontFamily,
          font_size: obj.fontSize,
          font_weight: String(obj.fontWeight || "normal"),
          font_style: obj.fontStyle || "normal",
          underline: Boolean(obj.underline),
          strikethrough: Boolean(obj.linethrough),
          letter_spacing: charSpacingToPx(obj.charSpacing, obj.fontSize),
          color: obj.fill,
          text_align: obj.textAlign === FABRIC_JUSTIFY ? "justify" : obj.textAlign || "left",
          text_vertical_align: obj.verticalAlign || "top",
          line_height: obj.lineHeight ?? 1.2,
          // Repurposed for text: stroke = outline, backgroundColor = the
          // whole-box "Background" effect, textBackgroundColor = the
          // per-line marker "Highlight" effect.
          stroke_color: obj.stroke || null,
          stroke_width: obj.strokeWidth || 0,
          background_color: obj.backgroundColor || null,
          text_highlight_color: obj.textBackgroundColor || null,
          text_height_locked: Boolean(obj.manualHeight),
          text_script_runs: extractScriptRuns(obj),
          text_fit_width: Boolean(obj.fitToWidth),
          text_fit_height: Boolean(obj.fitToHeight),
          ...shadowFields(obj),
        };
      }
      if (obj.layerType === "image") {
        return {
          ...common,
          src: obj.assetSrc || "",
          object_fit: "cover",
          image_crop: imageCrop(obj),
          border_radius: obj.cornerRadius || 0,
          mask_shape: obj.maskShape && obj.maskShape !== "custom" ? obj.maskShape : null,
          mask_path: obj.maskShape ? obj.maskPath || null : null,
          mask_base_path: obj.maskShape === "custom" ? obj.maskBasePath || obj.maskPath || null : null,
          mask_corner_radius: obj.maskShape ? obj.maskCornerRadius || 0 : 0,
          image_adjust: obj.imageAdjust || null,
          svg_src: obj.svgSrc || null,
          svg_edits: obj.svgEdits || null,
        };
      }
      if (obj.layerType === "rect") {
        const radii = obj.cornerRadii || [obj.rx || 0, obj.rx || 0, obj.rx || 0, obj.rx || 0];
        return {
          ...common,
          background_color: solidFill(obj),
          fill_gradient: obj.fillGradient || null,
          border_radius: radii[0],
          corner_radii: radii,
          stroke_color: obj.stroke || null,
          stroke_width: obj.strokeWidth || 0,
          stroke_dash: obj.strokeDash || "solid",
          ...shadowFields(obj),
        };
      }
      if (obj.layerType === "shape") {
        return {
          ...common,
          shape_kind: obj.shapeKind || "circle",
          grid_columns: obj.gridColumns || 6,
          grid_rows: obj.gridRows || 6,
          dot_color: obj.shapeKind === "dot_grid" ? obj.dotColor || "#191919" : null,
          dot_size: obj.shapeKind === "dot_grid" ? obj.dotSize || 8 : null,
          background_color: solidFill(obj),
          fill_gradient: obj.fillGradient || null,
          border_radius: obj.cornerRadius || 0,
          stroke_color: obj.stroke || null,
          stroke_width: obj.strokeWidth || 0,
          stroke_dash: obj.strokeDash || "solid",
          ...shadowFields(obj),
        };
      }
      return common;
    };

    // Groups are an editing convenience — the template format stays flat, so
    // group children are serialized with their absolute placement. Crop-mode
    // helper objects (the crop rectangle + dimming overlays) are excluded.
    //
    // Every object (grouped or not) is serialized via absolutePlacement,
    // which derives x/y/rotation from the object's own transform matrix
    // (ultimately its true center, via calcTransformMatrix) rather than
    // reading obj.left/obj.top directly. fabric.js rotates around an
    // object's left/top CORNER, not its center — the backend's CSS
    // transform:rotate() always rotates around the center. Those are
    // different pivots the instant an object has been rotated AND resized
    // (or resized after being rotated), so left/top alone can no longer be
    // trusted to mean anything the backend will render the same way. The
    // transform matrix is what fabric itself actually uses to paint the
    // object on screen, so deriving from it is correct no matter what
    // sequence of moves/rotates/resizes produced the current state.
    const layers = [];
    canvas.getObjects().filter((o) => !o.excludeFromExport).forEach((obj) => {
      if (obj.type === "group" && obj._objects) {
        // The format stays flat for the renderer: each child carries what
        // the group applies to it, and group_id rebuilds the group on load.
        obj._objects.forEach((child) => {
          layers.push({
            ...serializeObject(child, layers.length, absolutePlacement(child)),
            group_id: obj.layerId,
            group_name: obj.layerName,
            locked: Boolean(obj.locked || child.locked),
            hidden: obj.visible === false || child.visible === false,
            opacity: (obj.opacity ?? 1) * (child.opacity ?? 1),
          });
        });
      } else {
        layers.push(serializeObject(obj, layers.length, absolutePlacement(obj)));
      }
    });

    return {
      name,
      width: Number(width),
      height: Number(height),
      background_color: backgroundColor,
      // Read from the canvas (not React state) so undo/redo stays in sync.
      background_image: canvas.backgroundImage?.assetSrc || null,
      background_gradient: backgroundGradientRef.current,
      folder_id: folderIdRef.current,
      integration: integration || null,
      template_type: integration ? templateType || null : null,
      field_rules: integration ? fieldRules : {},
      layers,
    };
  };

  // Auto-save: performSaveRef always points at the latest closure so the
  // debounce timer never saves stale state. A brand-new template is created
  // on the first change (URL updated without a router remount).
  performSaveRef.current = async ({ render = false } = {}) => {
    if (!fabricRef.current) return false;
    const payload = buildPayload();
    setSaveStatus("saving");
    try {
      await saveDocument(mode, id, payload, { render });
      setSaveStatus("saved");
      return true;
    } catch (err) {
      console.error("Template save failed:", err.response?.data || err);
      setSaveStatus("error");
      // What the "will retry" label promises: try again on its own (a
      // restarted backend or a dropped connection then recovers unaided).
      clearTimeout(autoSaveTimer.current);
      autoSaveTimer.current = setTimeout(() => performSaveRef.current(), 10000);
      return false;
    }
  };

  // ⌘S saves and stays; the Save button saves, shows "Saved" for a moment,
  // then goes back to My Templates (into the template's folder).
  const [justSaved, setJustSaved] = useState(false);
  const handleSave = async ({ leave = false } = {}) => {
    clearTimeout(autoSaveTimer.current);
    setSaving(true);
    let ok = false;
    try {
      ok = await performSaveRef.current({ render: true });
    } finally {
      setSaving(false);
    }
    if (!ok) return;
    if (!leave) {
      flashSaveToast();
      return;
    }
    setJustSaved(true);
    setTimeout(() => {
      navigate(backTo);
    }, 650);
  };
  actionsRef.current.save = handleSave;
  // Read by the canvas drag-onto-frame handler (registered once at init).
  actionsRef.current.replaceFrameImage = replaceFrameImage;

  // Download: save, then render through the same server renderer the API
  // uses, so the file is exactly what an app would get.
  const [downloading, setDownloading] = useState(null); // "png" | "jpg" | null
  const [downloadOpen, setDownloadOpen] = useState(false);
  const downloadAs = async (format) => {
    setDownloadOpen(false);
    setDownloading(format);
    try {
      const blob = await renderDocument(buildPayload(), format);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(name || "design").replace(/[^\w-]+/g, "_")}.${format}`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) {
      console.error("Download failed:", err);
      window.alert("Could not render the download. Please try again.");
    } finally {
      setDownloading(null);
    }
  };

  // --- Layers panel: visibility and drag-to-reorder ---
  const toggleLayerVisibility = (obj) => {
    const canvas = fabricRef.current;
    obj.visible = obj.visible === false;
    if (!obj.visible && canvas.getActiveObject() === obj) {
      canvas.discardActiveObject();
      setSelected(null);
    }
    canvas.requestRenderAll();
    snapshot();
    forceTick();
  };

  const [dragLayerId, setDragLayerId] = useState(null);
  const [dropLayerId, setDropLayerId] = useState(null);
  // Dropping a row onto another takes that row's place in the stack: the
  // target's absolute index is where fabric's moveTo puts the dragged object
  // (above the target when moving up, below it when moving down).
  const aiWorking = false; // MAS has no AI tab

  const reorderLayer = (draggedId, targetId) => {
    const canvas = fabricRef.current;
    const all = canvas.getObjects();
    const dragged = all.find((o) => o.layerId === draggedId && !o.excludeFromExport);
    const target = all.find((o) => o.layerId === targetId && !o.excludeFromExport);
    if (!dragged || !target || dragged === target) return;
    canvas.moveTo(dragged, all.indexOf(target));
    canvas.requestRenderAll();
    snapshot();
    forceTick();
  };

  // Excludes crop-mode helper objects (crop rectangle + dimming overlays)
  // from the Layers list and any other "real layer" bookkeeping.
  const objects = fabricRef.current
    ? fabricRef.current.getObjects().filter((o) => !o.excludeFromExport)
    : [];

  // Every solid colour already used in the template, offered in each colour
  // picker ("In this template") so designs stay consistent.
  const templateColors = (() => {
    const found = new Set();
    const add = (c) => {
      if (typeof c === "string" && /^#[0-9a-f]{6}$/i.test(c)) found.add(c.toLowerCase());
    };
    add(backgroundColor);
    objects.forEach((o) => {
      [o.fill, o.stroke, o.backgroundColor, o.textBackgroundColor, o.shadowColor].forEach(add);
      if (o.fillGradient) {
        add(o.fillGradient.color_start);
        add(o.fillGradient.color_end);
      }
    });
    return [...found].slice(0, 18);
  })();

  return (
    <TemplateColorsContext.Provider value={templateColors}>
    <div className="editor-root">
      <div className="editor-topbar">
        <div className="editor-topbar-left">
          <Link
            to={backTo}
            className="icon-btn secondary"
            title={mode === "template" ? "Back to Templates" : "Back to History"}
            onClick={async (e) => {
              if (saveStatus !== "unsaved") return;
              e.preventDefault();
              clearTimeout(autoSaveTimer.current);
              await performSaveRef.current();
              navigate(backTo);
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </Link>
          <input
            className="editor-name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              scheduleAutoSave();
            }}
            aria-label="Template name"
          />
          <span className="editor-size">{width} × {height} px</span>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0 }}>
          <span className={`save-status ${saveStatus}`}>
            {saveStatus === "saving" && "Saving…"}
            {saveStatus === "unsaved" && "Unsaved changes"}
            {saveStatus === "saved" && "All changes saved"}
            {saveStatus === "error" && "Save failed — will retry"}
          </span>
          <button
            className="secondary icon-btn"
            title="Undo (Cmd/Ctrl+Z)"
            disabled={historyRef.current.stack.length < 2}
            onClick={undo}
          >
            <svg width="15" height="15" viewBox="0 0 24 24">
              <path d="M9 14L4 9l5-5M4 9h10a6 6 0 010 12h-3" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <button
            className="secondary icon-btn"
            title="Redo (Cmd/Ctrl+Shift+Z)"
            disabled={historyRef.current.redo.length === 0}
            onClick={redoAction}
          >
            <svg width="15" height="15" viewBox="0 0 24 24">
              <path d="M15 14l5-5-5-5M20 9H10a6 6 0 000 12h3" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <div className="download-wrap">
            <button
              className="secondary icon-text-btn"
              onClick={() => setDownloadOpen((o) => !o)}
              disabled={Boolean(downloading)}
              title="Download a render of this template"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 4v12M7 11l5 5 5-5M5 20h14" />
              </svg>
              {downloading ? `Rendering ${downloading.toUpperCase()}…` : "Download"}
            </button>
            {downloadOpen && (
              <div className="download-menu" onMouseLeave={() => setDownloadOpen(false)}>
                <button onClick={() => downloadAs("png")}>PNG <span>lossless</span></button>
                <button onClick={() => downloadAs("jpg")}>JPG <span>smaller file</span></button>
              </div>
            )}
          </div>
          <button
            className={justSaved ? "save-btn-done" : undefined}
            onClick={() => handleSave({ leave: true })}
            disabled={saving || justSaved}
            title={mode === "template" ? "Save and go back to Templates (⌘S saves and stays)" : "Save and go back to History (⌘S saves and stays)"}
          >
            {justSaved ? (
              <>
                <Icon name="check" size={14} /> Saved
              </>
            ) : saving ? (
              "Saving..."
            ) : (
              <>
                Save <kbd className="kbd-hint">⌘S</kbd>
              </>
            )}
          </button>
        </div>
      </div>

      {!loaded && !loadError && (
        <div style={{ display: "flex", justifyContent: "center", padding: 80 }}>
          <LogoLoader />
        </div>
      )}
      {loadError && (
        <div className="empty-state" style={{ padding: 40, textAlign: "center" }}>
          <p>{loadError}</p>
          <Link className="btn" to={backTo}>{mode === "template" ? "Back to Templates" : "Back to History"}</Link>
        </div>
      )}
      <div
        className="editor-layout"
        style={{
          display: loaded ? "grid" : "none",
          gridTemplateColumns: `72px ${leftOpen ? "264px" : "0px"} 1fr ${
            propsOpen ? "260px" : "34px"
          } ${layersOpen ? "200px" : "34px"}`,
        }}
      >
          <div className="editor-rail">
            {railTabs.map((tab) => (
              <button
                key={tab.id}
                className={`rail-btn ${activeTab === tab.id && leftOpen ? "active" : ""}`}
                onClick={() => {
                  if (activeTab === tab.id && leftOpen) setLeftOpen(false);
                  else {
                    setActiveTab(tab.id);
                    setLeftOpen(true);
                  }
                }}
              >
                <RailIcon path={tab.icon} stroke={tab.stroke} />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>

          <div className={`editor-flyout ${leftOpen ? "" : "closed"}`}>
            <button
              className="flyout-collapse"
              title="Collapse panel"
              onClick={() => setLeftOpen(false)}
            >
              ‹
            </button>
            <input
              type="file"
              accept="image/*"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={onImageFileSelected}
            />



            {activeTab === "elements" && (
              <>
                <h4>Shapes</h4>
                <div className="flyout-shape-grid">
                  {SHAPE_DEFS.map((def) => (
                    <button
                      key={def.kind}
                      className="flyout-shape-btn"
                      title={def.label}
                      onClick={() => addShape(def.kind, def.label)}
                      {...dragProps({ type: "shape", kind: def.kind, label: def.label })}
                    >
                      <ShapeIcon kind={def.kind} size={30} />
                      <span>{def.label}</span>
                    </button>
                  ))}
                </div>
                <h4>Special shapes</h4>
                <div className="flyout-shape-grid">
                  {SPECIAL_SHAPE_DEFS.map((def) => (
                    <button
                      key={def.kind}
                      className="flyout-shape-btn"
                      title={def.label}
                      onClick={() => addShape(def.kind, def.label)}
                      {...dragProps({ type: "shape", kind: def.kind, label: def.label })}
                    >
                      <ShapeIcon kind={def.kind} size={30} />
                      <span>{def.label}</span>
                    </button>
                  ))}
                </div>
                <h4>Organic</h4>
                <div className="flyout-shape-grid">
                  {BLOB_DEFS.map((def) => (
                    <button
                      key={def.kind}
                      className="flyout-shape-btn"
                      title={def.label}
                      onClick={() => addShape(def.kind, def.label)}
                      {...dragProps({ type: "shape", kind: def.kind, label: def.label })}
                    >
                      <ShapeIcon kind={def.kind} size={30} />
                    </button>
                  ))}
                </div>
                <h4>Outlines</h4>
                <div className="flyout-shape-grid">
                  {OUTLINE_KINDS.map((def) => (
                    <button
                      key={def.kind}
                      className="flyout-shape-btn outline"
                      title={`${def.label} outline`}
                      onClick={() => addShape(def.kind, def.label, { outline: true })}
                      {...dragProps({ type: "shape", kind: def.kind, label: def.label, outline: true })}
                    >
                      <ShapeIcon kind={def.kind} size={30} />
                    </button>
                  ))}
                </div>
              </>
            )}


            {activeTab === "frames" && (
              <>
                <h4>Frames</h4>
                <p className="flyout-hint">
                  Image placeholders. Drag a photo onto one, from Images or from the canvas, to fill it. Or select a
                  frame and pick an image in Images. Hold ⌥ Alt while dragging to move a photo over a frame without
                  filling it.
                </p>
                <div className="flyout-shape-grid">
                  {FRAME_DEFS.map((def) => (
                    <button
                      key={def.kind}
                      className="flyout-shape-btn frame"
                      title={`${def.label} frame`}
                      onClick={() => addFrame(def.kind)}
                      {...dragProps({ type: "frame", kind: def.kind })}
                    >
                      <ShapeIcon kind={def.kind} size={30} />
                    </button>
                  ))}
                </div>
              </>
            )}

            {activeTab === "text" && (
              <>
                <button className="flyout-cta" onClick={() => addText()} {...dragProps({ type: "text", preset: {} })}>
                  + Add a text box
                </button>
                <h4>Default text styles</h4>
                <button
                  className="text-preset heading"
                  onClick={() => addText({ text: "Add a heading", fontSize: 64, fontWeight: "bold", width: 560 })}
                  {...dragProps({ type: "text", preset: { text: "Add a heading", fontSize: 64, fontWeight: "bold", width: 560 } })}
                >
                  Add a heading
                </button>
                <button
                  className="text-preset subheading"
                  onClick={() => addText({ text: "Add a subheading", fontSize: 40, fontWeight: "bold", width: 460 })}
                  {...dragProps({ type: "text", preset: { text: "Add a subheading", fontSize: 40, fontWeight: "bold", width: 460 } })}
                >
                  Add a subheading
                </button>
                <button
                  className="text-preset body"
                  onClick={() => addText({ text: "Add a little bit of body text", fontSize: 24, width: 400 })}
                  {...dragProps({ type: "text", preset: { text: "Add a little bit of body text", fontSize: 24, width: 400 } })}
                >
                  Add a little bit of body text
                </button>
              </>
            )}

            {activeTab === "uploads" && (
              <>
                <button className="flyout-cta" onClick={triggerImageUpload}>
                  Upload image
                </button>
                <h4>Your uploads &amp; MAS brand assets</h4>
                {bankImages === null && <p className="flyout-hint">Loading...</p>}
                <div className="flyout-bank-grid">
                  {(bankImages || []).map((img) => (
                    <div
                      key={img.id}
                      className="flyout-bank-item"
                      title={img.label || "Unlabeled"}
                      onClick={() => insertBankImage(img)}
                      {...dragProps({ type: "image", image: { url: img.url, label: img.label } })}
                    >
                      <img src={toAbsoluteUrl(img.url)} alt={img.label} loading="lazy" />
                      <span>{img.label || "Unlabeled"}</span>
                    </div>
                  ))}
                </div>
              </>
            )}

            {activeTab === "fields" && (
              <DataFieldsPanel
                objects={objects}
                selected={selected?.type === "activeSelection" ? null : selected}
                templateId={null}
                onSelect={(obj) => {
                  fabricRef.current.setActiveObject(obj);
                  fabricRef.current.requestRenderAll();
                  setSelected(obj);
                }}
                onUpdate={(obj, props) => {
                  Object.assign(obj, props);
                  fabricRef.current.requestRenderAll();
                  snapshot();
                  forceTick();
                }}
              />
            )}


            {activeTab === "canvas" && (
              <>
                <h4>Background</h4>
                <div className="field">
                  <label>Name</label>
                  <input type="text" value={name} onChange={(e) => setName(e.target.value)} />
                </div>
                <div className="field">
                  <label>Size</label>
                  <p className="field-static" title="Set when the template was created — start a new template for a different size.">
                    {width} &times; {height} px
                  </p>
                </div>
                <div className="field">
                  <label>Background</label>
                  <ColorInput
                    value={backgroundColor}
                    onChange={(value) => {
                      setBackgroundColor(value);
                      ensurePageBackground(fabricRef.current, value, Number(width), Number(height));
                      fabricRef.current.requestRenderAll();
                      snapshot();
                    }}
                  />
                </div>

                <h4>Background image</h4>
                <input
                  type="file"
                  accept="image/*"
                  ref={bgFileInputRef}
                  style={{ display: "none" }}
                  onChange={onBgFileSelected}
                />
                {backgroundImage && (
                  <img
                    className="bg-image-preview"
                    src={toAbsoluteUrl(backgroundImage)}
                    alt="Canvas background"
                  />
                )}
                <div className="tool-buttons">
                  <button className="secondary" onClick={() => bgFileInputRef.current?.click()}>
                    Upload image
                  </button>
                  <button
                    className="secondary"
                    onClick={async () => {
                      if (!bgPickerOpen && bankImages === null) {
                        setBankImages(await listBankImages());
                      }
                      setBgPickerOpen((o) => !o);
                    }}
                  >
                    {bgPickerOpen ? "Close bank" : "Choose from bank"}
                  </button>
                  {backgroundImage && (
                    <button className="secondary" onClick={() => setCanvasBackgroundImage(null)}>
                      Remove background image
                    </button>
                  )}
                </div>
                {bgPickerOpen && (
                  <div className="flyout-bank-grid" style={{ marginTop: 10 }}>
                    {(bankImages || []).map((img) => (
                      <div
                        key={img.id}
                        className="flyout-bank-item"
                        title={img.label || "Unlabeled"}
                        onClick={() => {
                          setCanvasBackgroundImage(img.url);
                          setBgPickerOpen(false);
                        }}
                      >
                        <img src={toAbsoluteUrl(img.url)} alt={img.label} loading="lazy" />
                        <span>{img.label || "Unlabeled"}</span>
                      </div>
                    ))}
                  </div>
                )}

                <h4>Gradient overlay</h4>
                <div className="field">
                  <label>
                    <input
                      type="checkbox"
                      checked={Boolean(backgroundGradient)}
                      onChange={(e) => toggleGradient(e.target.checked)}
                    />{" "}
                    Enable
                  </label>
                  <p className="hint" style={{ marginTop: 2 }}>
                    Sits above the background color/image and below every
                    layer.
                  </p>
                </div>
                {backgroundGradient && (
                  <>
                    <div className="field">
                      <label>Type</label>
                      <select
                        value={backgroundGradient.kind}
                        onChange={(e) => {
                          const kind = e.target.value;
                          // "center" only makes sense for radial.
                          const position =
                            kind === "linear" && backgroundGradient.position === "center"
                              ? "top"
                              : backgroundGradient.position;
                          updateGradient({ kind, position });
                        }}
                      >
                        <option value="radial">Radial (spotlight)</option>
                        <option value="linear">Linear (straight band)</option>
                      </select>
                    </div>
                    <div className="field">
                      <label>{backgroundGradient.kind === "linear" ? "Direction" : "Spread from"}</label>
                      <select
                        value={backgroundGradient.position}
                        onChange={(e) => updateGradient({ position: e.target.value })}
                      >
                        {backgroundGradient.kind === "radial" && <option value="center">Center</option>}
                        <option value="top">Top</option>
                        <option value="bottom">Bottom</option>
                        {backgroundGradient.kind === "linear" && (
                          <>
                            <option value="left">Left</option>
                            <option value="right">Right</option>
                            <option value="top-left">Diagonal ↘ (top-left)</option>
                            <option value="top-right">Diagonal ↙ (top-right)</option>
                            <option value="bottom-left">Diagonal ↗ (bottom-left)</option>
                            <option value="bottom-right">Diagonal ↖ (bottom-right)</option>
                          </>
                        )}
                      </select>
                    </div>
                    <div className="field">
                      <label>Start color</label>
                      <ColorInput
                        value={backgroundGradient.color_start}
                        onChange={(value) => updateGradient({ color_start: value })}
                      />
                    </div>
                    <div className="field">
                      <label>End color</label>
                      <ColorInput
                        value={backgroundGradient.color_end}
                        onChange={(value) => updateGradient({ color_end: value })}
                      />
                    </div>
                    <div className="field">
                      <label>End opacity ({Math.round(backgroundGradient.end_opacity * 100)}%)</label>
                      <input
                        type="range"
                        min={0}
                        max={1}
                        step={0.05}
                        value={backgroundGradient.end_opacity}
                        onChange={(e) => updateGradient({ end_opacity: Number(e.target.value) })}
                      />
                      <p className="hint" style={{ marginTop: 2 }}>
                        0% fades to fully transparent (a vignette); 100% ends
                        as a solid end color.
                      </p>
                    </div>
                  </>
                )}

              </>
            )}
          </div>

          <div className="canvas-column">
            <div
              className={`canvas-wrap canvas-wrap-open ${dropActive ? "drop-active" : ""}`}
              ref={canvasWrapRef}
              onDragOver={onCanvasDragOver}
              onDragLeave={onCanvasDragLeave}
              onDrop={onCanvasDrop}
              onContextMenu={openContextMenu}
              onMouseDown={onStageMouseDown}
              onMouseEnter={() => setPageBorderHover(true)}
              onMouseLeave={() => setPageBorderHover(false)}
            >
              <div className="canvas-scroll-inner">
                <canvas ref={canvasElRef} />
              </div>
              {upload && (
                <div className="upload-overlay" role="status">
                  <LogoLoader progress={upload.placing ? 1 : upload.progress} />
                  <b>{upload.placing ? "Placing on canvas…" : `Uploading… ${Math.round(upload.progress * 100)}%`}</b>
                  <span title={upload.label}>{upload.label}</span>
                </div>
              )}
              {canvasNotice && <div className="upload-error" role="alert">{canvasNotice}</div>}
              {!cropping &&
                selected?.type === "activeSelection" &&
                (() => {
                  const count = (fabricRef.current?.getActiveObjects() || []).filter(isFrameOperand).length;
                  if (count < 2) return null;
                  return (
                    <div className="combine-bar" role="toolbar" aria-label="Make a frame from the selection">
                      <span>Make a frame from {count} shapes</span>
                      <button type="button" onClick={() => combineIntoFrame("union")} title="One frame covering all the selected shapes">
                        <svg width="18" height="14" viewBox="0 0 36 28" aria-hidden="true">
                          <path d="M14 2a12 12 0 0 1 8 3.1A12 12 0 1 1 22 22.9 12 12 0 1 1 14 2z" fill="currentColor" />
                        </svg>
                        Union
                      </button>
                      <button type="button" onClick={() => combineIntoFrame("intersection")} title="A frame of just the area where the shapes overlap">
                        <svg width="18" height="14" viewBox="0 0 36 28" aria-hidden="true">
                          <circle cx="14" cy="14" r="11" fill="none" stroke="currentColor" strokeWidth="2" opacity="0.5" />
                          <circle cx="22" cy="14" r="11" fill="none" stroke="currentColor" strokeWidth="2" opacity="0.5" />
                          <path d="M18 5.1a11 11 0 0 1 0 17.8 11 11 0 0 1 0-17.8z" fill="currentColor" />
                        </svg>
                        Intersect
                      </button>
                      <button type="button" onClick={() => combineIntoFrame("difference")} title="Cut the shapes on top out of the bottom shape">
                        <svg width="18" height="14" viewBox="0 0 36 28" aria-hidden="true">
                          <path d="M14 3a11 11 0 1 0 4 21.2A11 11 0 0 1 18 3.8 11 11 0 0 0 14 3z" fill="currentColor" />
                          <circle cx="22" cy="14" r="11" fill="none" stroke="currentColor" strokeWidth="2" strokeDasharray="3 2.5" opacity="0.6" />
                        </svg>
                        Subtract
                      </button>
                    </div>
                  );
                })()}
              {aiWorking && (
                <div className="ai-working-pill">
                  <span className="ai-dot" />
                  <span className="ai-dot" />
                  <span className="ai-dot" />
                  Applying your change…
                </div>
              )}
            </div>

            <div className="zoom-bar">
              <button
                className="zoom-btn"
                onClick={() => bumpZoom(1 / 1.1)}
                title="Zoom out"
              >
                −
              </button>
              <input
                type="range"
                ref={zoomSliderRef}
                className="zoom-slider"
                min={ZOOM_MIN}
                max={ZOOM_MAX}
                step={0.01}
                value={fabricRef.current?.getZoom() || 1}
                onChange={(e) => zoomAt(Number(e.target.value))}
              />
              <button
                className="zoom-btn"
                onClick={() => bumpZoom(1.1)}
                title="Zoom in"
              >
                +
              </button>
              <span className="zoom-pct" ref={zoomPctRef}>
                {Math.round((fabricRef.current?.getZoom() || 1) * 100)}%
              </span>
              <button
                className="zoom-btn zoom-fit"
                onClick={zoomToFit}
                title="Fit to screen"
                disabled={zoomOverride === null}
              >
                ⤢ Fit
              </button>
            </div>
          </div>

          {cropping &&
            canvasWrapRef.current &&
            (() => {
              const rect = canvasWrapRef.current.getBoundingClientRect();
              return (
                <div
                  className="crop-toolbar"
                  style={{
                    position: "fixed",
                    top: rect.top + 12,
                    left: rect.left + rect.width / 2,
                    transform: "translateX(-50%)",
                  }}
                >
                  {cropRef.current.mode === "adjust" ? (
                    <>
                      <span>Drag the photo to move it</span>
                      <label className="adjust-zoom">
                        Size
                        <input
                          type="range"
                          min={1}
                          max={5}
                          step={0.01}
                          value={adjustZoom}
                          onChange={(e) => cropRef.current.zoomTo?.(Number(e.target.value))}
                        />
                      </label>
                      <button className="btn-small" onClick={applyCrop}>Done</button>
                    </>
                  ) : (
                    <>
                      <span>Drag the handles to crop, then Apply (or press Enter)</span>
                      <button className="btn-small" onClick={applyCrop}>Apply crop</button>
                    </>
                  )}
                  <button className="secondary btn-small" onClick={cancelCrop}>Cancel</button>
                </div>
              );
            })()}

          {!cropping && selected?.layerType === "text" && (
            <TextToolbar key={selected.layerId} obj={selected} onChange={updateSelected} canvasWrapRef={canvasWrapRef} />
          )}

          {ctxMenu && (() => {
            const canvas = fabricRef.current;
            const actives = canvas?.getActiveObjects() || [];
            const active = canvas?.getActiveObject();
            const multi = actives.length > 1;
            const isGroup = active?.type === "group";
            const anyLocked = actives.some((o) => o.locked);
            const run = (fn) => () => {
              fn();
              setCtxMenu(null);
            };
            const frame = actives.length === 1 && active?.layerType === "image" && active.maskShape ? active : null;
            const items = [
              { label: "Rename", disabled: actives.length !== 1, action: () => startRename(active) },
              "---",
              ...(frame
                ? [
                    {
                      label: "Adjust photo in frame",
                      disabled: isEmptyFrame(frame) || Math.abs((frame.angle || 0) % 360) > 0.5,
                      action: run(() => enterAdjustMode(frame)),
                    },
                    {
                      label: "Detach photo from frame",
                      disabled: isEmptyFrame(frame) || Boolean(frame.group),
                      action: run(() => detachFrameImage(frame)),
                    },
                    "---",
                  ]
                : []),
              { label: "Copy", kbd: "⌘C", disabled: !actives.length, action: run(() => copySelection()) },
              { label: "Paste", kbd: "⌘V", disabled: !readClipboard(), action: run(pasteClipboard) },
              { label: "Duplicate", kbd: "⌘D", disabled: !actives.length, action: run(duplicateSelection) },
              "---",
              isGroup
                ? { label: "Ungroup", kbd: "⌘⇧G", action: run(ungroupSelection) }
                : { label: "Group", kbd: "⌘G", disabled: !multi, action: run(groupSelection) },
              { label: anyLocked ? "Unlock" : "Lock", disabled: !actives.length, action: run(toggleLockSelection) },
              "---",
              {
                label: "Merge into frame (union)",
                disabled: actives.filter(isFrameOperand).length < 2,
                action: run(() => combineIntoFrame("union")),
              },
              {
                label: "Frame from overlap (intersect)",
                disabled: actives.filter(isFrameOperand).length < 2,
                action: run(() => combineIntoFrame("intersection")),
              },
              {
                label: "Cut top shapes from bottom (subtract)",
                disabled: actives.filter(isFrameOperand).length < 2,
                action: run(() => combineIntoFrame("difference")),
              },
              "---",
              { label: "Align left", disabled: !multi, action: run(() => alignSelection("left")) },
              { label: "Align center", disabled: !multi, action: run(() => alignSelection("centerH")) },
              { label: "Align right", disabled: !multi, action: run(() => alignSelection("right")) },
              { label: "Align top", disabled: !multi, action: run(() => alignSelection("top")) },
              { label: "Align middle", disabled: !multi, action: run(() => alignSelection("middle")) },
              { label: "Align bottom", disabled: !multi, action: run(() => alignSelection("bottom")) },
              { label: "Distribute horizontally", disabled: actives.length < 3, action: run(() => distributeSelection("x")) },
              { label: "Distribute vertically", disabled: actives.length < 3, action: run(() => distributeSelection("y")) },
              "---",
              { label: "Bring to front", disabled: !actives.length, action: run(bringForward) },
              { label: "Send to back", disabled: !actives.length, action: run(sendBackward) },
              { label: "Delete", kbd: "⌫", disabled: !actives.length, action: run(deleteSelected), danger: true },
            ];
            return (
              <div
                className="ctx-menu"
                style={{ left: ctxMenu.x, top: ctxMenu.y }}
                ref={(el) => {
                  if (!el) return;
                  // Clamp into the viewport so the menu is always fully
                  // visible regardless of where the user right-clicked.
                  const rect = el.getBoundingClientRect();
                  let x = ctxMenu.x;
                  let y = ctxMenu.y;
                  if (x + rect.width > window.innerWidth - 8) {
                    x = window.innerWidth - rect.width - 8;
                  }
                  if (y + rect.height > window.innerHeight - 8) {
                    y = window.innerHeight - rect.height - 8;
                  }
                  el.style.left = `${Math.max(8, x)}px`;
                  el.style.top = `${Math.max(8, y)}px`;
                }}
              >
                {items.map((item, idx) =>
                  item === "---" ? (
                    <div className="ctx-sep" key={idx} />
                  ) : (
                    <button
                      key={item.label}
                      className={`ctx-item ${item.danger ? "danger-text" : ""}`}
                      disabled={item.disabled}
                      onClick={item.action}
                    >
                      <span>{item.label}</span>
                      {item.kbd && <kbd>{item.kbd}</kbd>}
                    </button>
                  )
                )}
              </div>
            );
          })()}

          {propsOpen ? (
            <div className="panel side-panel">
              <div className="panel-head">
                <h4>Properties</h4>
                <button className="panel-collapse" title="Collapse" onClick={() => setPropsOpen(false)}>
                  ›
                </button>
              </div>
              {cropping ? (
                <p className="hint">
                  {cropRef.current.mode === "adjust"
                    ? "Adjusting the photo in its frame: drag it to move, use Size (or a corner) to resize, then Done or Cancel above."
                    : "Cropping image — drag the handles on the canvas, then Apply or Cancel above."}
                </p>
              ) : (
                <>
                  {!selected && <p className="hint">Select a layer to edit its properties.</p>}
                  {selected && (
                    <PropertiesPanel
                      key={selected.layerId}
                      obj={selected}
                      tick={tick}
                      appFields={appType?.fields || null}
                      onRenamed={() => {
                        forceTick();
                        scheduleAutoSave();
                      }}
                      onChange={updateSelected}
                      onReshape={reshapeSelected}
                      onTextScript={applyTextScript}
                      onRemoveBackground={null}
                      onCropImage={() => (selected.maskShape ? enterAdjustMode(selected) : enterCropMode(selected))}
                      onSvgColors={updateSvgColors}
                      onDelete={deleteSelected}
                      onBringForward={bringForward}
                      onSendBackward={sendBackward}
                    />
                  )}
                </>
              )}
            </div>
          ) : (
            <button className="panel-strip" onClick={() => setPropsOpen(true)} title="Expand Properties">
              <span>Properties</span>
            </button>
          )}

          {layersOpen ? (
            <div className="panel side-panel">
              <div className="panel-head">
                <h4>Layers</h4>
                <button className="panel-collapse" title="Collapse" onClick={() => setLayersOpen(false)}>
                  ›
                </button>
              </div>
              {objects.length === 0 && <p className="hint">No layers yet.</p>}
              <ul className="layers-list">
                {objects
                  .slice()
                  .reverse()
                  .map((obj) => {
                    const appField = obj.dataField && appType?.fields?.find((f) => f.key === obj.layerName && f.kind === obj.layerType);
                    const hidden = obj.visible === false;
                    return (
                      <li
                        key={obj.layerId}
                        draggable={renaming?.obj !== obj}
                        className={[
                          fabricRef.current?.getActiveObjects().includes(obj) ? "active" : "",
                          hidden ? "is-hidden" : "",
                          dropLayerId === obj.layerId && dragLayerId !== obj.layerId ? "drop-target" : "",
                        ].join(" ")}
                        onDragStart={(e) => {
                          setDragLayerId(obj.layerId);
                          e.dataTransfer.effectAllowed = "move";
                        }}
                        onDragOver={(e) => {
                          e.preventDefault();
                          if (dropLayerId !== obj.layerId) setDropLayerId(obj.layerId);
                        }}
                        onDragEnd={() => {
                          setDragLayerId(null);
                          setDropLayerId(null);
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          reorderLayer(dragLayerId, obj.layerId);
                          setDragLayerId(null);
                          setDropLayerId(null);
                        }}
                        onClick={(e) => onLayerClick(e, obj)}
                        onDoubleClick={() => startRename(obj)}
                        onContextMenu={(e) => openLayerMenu(e, obj)}
                        title="Double-click to rename, drag to reorder, right-click for actions"
                      >
                        <span className="layer-grip" aria-hidden="true">⋮⋮</span>
                        <span className="layer-type-glyph">{LAYER_GLYPHS[obj.layerType] || "•"}</span>
                        {renaming?.obj === obj ? (
                          <input
                            className="layer-name"
                            aria-label="Layer name"
                            autoFocus
                            maxLength={60}
                            value={renaming.text}
                            style={{ padding: "1px 4px", height: 22, border: "1px solid var(--accent)", borderRadius: 4 }}
                            onFocus={(e) => e.target.select()}
                            onChange={(e) => setRenaming({ obj, text: e.target.value })}
                            onClick={(e) => e.stopPropagation()}
                            onDoubleClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => {
                              e.stopPropagation();
                              if (e.key === "Enter") finishRename(true);
                              if (e.key === "Escape") finishRename(false);
                            }}
                            onBlur={() => finishRename(true)}
                          />
                        ) : (
                          <span className="layer-name" title={obj.layerName}>{obj.layerName}</span>
                        )}
                        {appField && (
                          <span className="layer-field-tag" title={`Filled in from the job: ${appField.label}`}>
                            field
                          </span>
                        )}
                        {obj.locked && <span className="layer-lock" title="Locked">🔒</span>}
                        <button
                          type="button"
                          className="layer-eye"
                          title={hidden ? "Show layer" : "Hide layer"}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleLayerVisibility(obj);
                          }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            {hidden ? (
                              <path d="M3 3l18 18M10.6 6.1A10 10 0 0112 6c5.5 0 9 6 9 6a17 17 0 01-3.2 3.9M6.6 6.6C4.3 8.1 3 12 3 12s3.5 6 9 6c1.5 0 2.8-.4 4-1" />
                            ) : (
                              <>
                                <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
                                <circle cx="12" cy="12" r="3" />
                              </>
                            )}
                          </svg>
                        </button>
                      </li>
                    );
                  })}
              </ul>
            </div>
          ) : (
            <button className="panel-strip" onClick={() => setLayersOpen(true)} title="Expand Layers">
              <span>Layers</span>
            </button>
          )}
      </div>


      {saveToast && (
        <div className="save-toast">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 6L9 17l-5-5" />
          </svg>
          Template saved
        </div>
      )}
    </div>
    </TemplateColorsContext.Provider>
  );
}

const GRADIENT_ANGLES = [
  { angle: 0, label: "↑ Bottom to top" },
  { angle: 45, label: "↗ Diagonal" },
  { angle: 90, label: "→ Left to right" },
  { angle: 135, label: "↘ Diagonal" },
  { angle: 180, label: "↓ Top to bottom" },
  { angle: 225, label: "↙ Diagonal" },
  { angle: 270, label: "← Right to left" },
  { angle: 315, label: "↖ Diagonal" },
];

// Fill for rect/shape layers: solid colour, two-colour gradient, or none
// (outline only). The gradient spec is kept on obj.fillGradient (saved as
// fill_gradient) alongside the fabric.Gradient actually painting it.
function FillControls({ obj, onChange, title = "Fill" }) {
  const spec = obj.fillGradient;
  const mode = spec ? "gradient" : obj.fill === "transparent" || !obj.fill ? "none" : "solid";
  const solid = typeof obj.fill === "string" && obj.fill !== "transparent" ? obj.fill : spec?.color_start || "#cccccc";
  const setGradient = (patch) => {
    const next = { ...(spec || { ...DEFAULT_FILL_GRADIENT, color_start: solid }), ...patch };
    onChange({ fill: fabricGradient(next), fillGradient: next });
  };
  return (
    <div className="prop-section">
      <div className="prop-section-title">{title}</div>
      <div className="seg">
        {[
          ["solid", "Solid"],
          ["gradient", "Gradient"],
          ["none", "None"],
        ].map(([id, label]) => (
          <button
            key={id}
            type="button"
            className={mode === id ? "on" : ""}
            onClick={() => {
              if (id === "solid") onChange({ fill: solid, fillGradient: null });
              else if (id === "none") onChange({ fill: "transparent", fillGradient: null });
              else setGradient({});
            }}
          >
            {label}
          </button>
        ))}
      </div>
      {mode === "solid" && (
        <div className="field">
          <ColorInput value={solid} onChange={(value) => onChange({ fill: value, fillGradient: null })} />
        </div>
      )}
      {mode === "gradient" && (
        <>
          <div className="gradient-preview" style={{ background: `linear-gradient(90deg, ${spec.color_start}, ${spec.color_end})` }} />
          <div className="props-grid">
            <div className="field">
              <label>Type</label>
              <select value={spec.kind} onChange={(e) => setGradient({ kind: e.target.value })}>
                <option value="linear">Linear</option>
                <option value="radial">Radial</option>
              </select>
            </div>
            {spec.kind === "linear" && (
              <div className="field">
                <label>Direction</label>
                <select value={spec.angle} onChange={(e) => setGradient({ angle: Number(e.target.value) })}>
                  {GRADIENT_ANGLES.map((a) => (
                    <option key={a.angle} value={a.angle}>{a.label}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <div className="field">
            <label>{spec.kind === "radial" ? "Centre colour" : "Start colour"}</label>
            <ColorInput value={spec.color_start} onChange={(value) => setGradient({ color_start: value })} />
          </div>
          <div className="field">
            <label>{spec.kind === "radial" ? "Edge colour" : "End colour"}</label>
            <ColorInput value={spec.color_end} onChange={(value) => setGradient({ color_end: value })} />
          </div>
          <button
            type="button"
            className="secondary btn-small"
            onClick={() => setGradient({ color_start: spec.color_end, color_end: spec.color_start })}
          >
            ⇄ Swap colours
          </button>
        </>
      )}
    </div>
  );
}

// Colours of an uploaded SVG: one swatch per colour in the file, and the fill
// controls for the one picked. Changes are kept against the original file, so
// two colours set to the same value stay separate.
function SvgColorControls({ obj, onChange }) {
  const source = obj.svgSrc || obj.assetSrc;
  const edits = obj.svgEdits || {};
  const [colors, setColors] = useState(null);
  const [picked, setPicked] = useState(null);
  useEffect(() => {
    let current = true;
    setColors(null);
    loadSvgText(source)
      .then((text) => current && setColors(svgColors(text)))
      .catch(() => current && setColors([]));
    return () => {
      current = false;
    };
  }, [source]);
  if (!colors) return null;
  if (!colors.length) return <p className="hint">This SVG has no colours that can be changed.</p>;

  const original = colors.includes(picked) ? picked : colors[0];
  const edit = edits[original];
  const paintOf = (hex) => {
    const value = edits[hex] ?? hex;
    if (value === "none") return "repeating-conic-gradient(#ccc 0 25%, #fff 0 50%) 0 0 / 8px 8px";
    return typeof value === "string" ? value : `linear-gradient(135deg, ${value.color_start}, ${value.color_end})`;
  };
  const setEdit = ({ fill, fillGradient }) => {
    const next = { ...edits, [original]: fillGradient || (fill === "transparent" ? "none" : fill) };
    if (next[original] === original) delete next[original];
    onChange(next);
  };
  return (
    <>
      <div className="prop-section">
        <div className="prop-section-title">SVG colours</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {colors.map((hex) => (
            <button
              key={hex}
              type="button"
              className="svg-color-swatch"
              title={hex}
              aria-label={`Change ${hex}`}
              onClick={() => setPicked(hex)}
              style={{
                width: 28,
                height: 28,
                padding: 0,
                borderRadius: 6,
                cursor: "pointer",
                background: paintOf(hex),
                border: "1px solid rgba(0,0,0,0.15)",
                boxShadow: hex === original ? "0 0 0 2px #fff, 0 0 0 4px #5842f4" : "none",
              }}
            />
          ))}
        </div>
      </div>
      <FillControls
        title="Selected colour"
        obj={{
          fill: edit === "none" ? "transparent" : typeof edit === "string" ? edit : edit ? edit.color_start : original,
          fillGradient: edit && typeof edit === "object" ? edit : null,
        }}
        onChange={setEdit}
      />
      {Object.keys(edits).length > 0 && (
        <button type="button" className="secondary btn-small" onClick={() => onChange({})}>
          Reset to original colours
        </button>
      )}
    </>
  );
}

const IMAGE_ADJUSTMENTS = [
  { key: "brightness", label: "Brightness", min: 0, max: 200 },
  { key: "contrast", label: "Contrast", min: 0, max: 200 },
  { key: "saturation", label: "Saturation", min: 0, max: 200 },
  { key: "hue", label: "Hue", min: -180, max: 180 },
  { key: "grayscale", label: "Grayscale", min: 0, max: 100 },
  { key: "sepia", label: "Sepia", min: 0, max: 100 },
  { key: "blur", label: "Blur", min: 0, max: 40 },
];

// Brightness, contrast and friends for JPG, PNG and WebP layers.
function ImageAdjustControls({ obj, onChange }) {
  const adjust = { ...IMAGE_ADJUST_DEFAULTS, ...obj.imageAdjust };
  const set = (key, value) => onChange({ imageAdjust: { ...adjust, [key]: Number(value) } });
  return (
    <div className="prop-section">
      <div className="prop-section-title">Adjust image</div>
      {IMAGE_ADJUSTMENTS.map(({ key, label, min, max }) => (
        <div className="field" key={key}>
          <label>{label}</label>
          <div className="range-with-number">
            <input type="range" min={min} max={max} value={adjust[key]} aria-label={label} onChange={(e) => set(key, e.target.value)} />
            <input type="number" min={min} max={max} value={adjust[key]} onChange={(e) => set(key, e.target.value)} />
          </div>
        </div>
      ))}
      {obj.imageAdjust && (
        <button type="button" className="secondary btn-small" onClick={() => onChange({ imageAdjust: null })}>
          Reset adjustments
        </button>
      )}
    </div>
  );
}

// Border for rect/shape layers: colour, width and dash style. The dash
// pattern scales with the width, so it's recomputed whenever either changes.
function StrokeControls({ obj, onChange, title = "Border" }) {
  const on = Boolean(obj.stroke) && (obj.strokeWidth || 0) > 0;
  const dash = obj.strokeDash || "solid";
  const width = obj.strokeWidth || 2;
  const apply = (patch) => {
    const nextWidth = patch.strokeWidth ?? width;
    const nextDash = patch.strokeDash ?? dash;
    onChange({ ...patch, strokeWidth: nextWidth, strokeDash: nextDash, ...fabricDash(nextDash, nextWidth) });
  };
  return (
    <div className="prop-section">
      <label className="inline-check prop-section-title">
        <input
          type="checkbox"
          checked={on}
          onChange={(e) =>
            e.target.checked
              ? apply({ stroke: obj.stroke || "#191919", strokeWidth: width || 2 })
              : onChange({ stroke: null, strokeWidth: 0 })
          }
        />
        {title}
      </label>
      {on && (
        <>
          <div className="field">
            <ColorInput value={obj.stroke} onChange={(value) => apply({ stroke: value })} />
          </div>
          <div className="field">
            <label>Width ({width}px)</label>
            <input type="range" min={1} max={40} value={width} onChange={(e) => apply({ strokeWidth: Number(e.target.value) })} />
          </div>
          <div className="seg">
            {DASH_STYLES.map((style) => (
              <button
                key={style}
                type="button"
                className={dash === style ? "on" : ""}
                title={style}
                onClick={() => apply({ strokeDash: style })}
              >
                <svg width="34" height="8" viewBox="0 0 34 8">
                  <line
                    x1="2"
                    y1="4"
                    x2="32"
                    y2="4"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap={style === "dotted" ? "round" : "butt"}
                    strokeDasharray={style === "dashed" ? "8 5" : style === "dotted" ? "0 6" : undefined}
                  />
                </svg>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

const CORNER_KEYS = [
  { id: "all", label: "All" },
  { id: 0, label: "◤" },
  { id: 1, label: "◥" },
  { id: 2, label: "◢" },
  { id: 3, label: "◣" },
];

function CornerRadiusControl({ obj, onReshape }) {
  const [corner, setCorner] = useState("all");
  const radii = obj.cornerRadii || [obj.rx || 0, obj.rx || 0, obj.rx || 0, obj.rx || 0];
  const value = corner === "all" ? radii[0] : radii[corner];

  const apply = (next) => {
    const clamped = Math.max(0, Number(next) || 0);
    const nextRadii =
      corner === "all"
        ? [clamped, clamped, clamped, clamped]
        : radii.map((r, idx) => (idx === corner ? clamped : r));
    onReshape({ cornerRadii: nextRadii });
  };

  return (
    <div className="field">
      <label>Corner radius (select a corner, then adjust)</label>
      <div className="corner-picker">
        {CORNER_KEYS.map((c) => (
          <button
            key={c.id}
            type="button"
            className={`corner-btn ${corner === c.id ? "active" : ""}`}
            title={c.id === "all" ? "All corners" : ["Top left", "Top right", "Bottom right", "Bottom left"][c.id]}
            onClick={() => setCorner(c.id)}
          >
            {c.label}
          </button>
        ))}
        <input
          type="number"
          min={0}
          value={Math.round(value)}
          onChange={(e) => apply(e.target.value)}
        />
      </div>
      {corner !== "all" && (
        <p className="hint" style={{ marginTop: 4 }}>
          TL {Math.round(radii[0])} · TR {Math.round(radii[1])} · BR {Math.round(radii[2])} · BL {Math.round(radii[3])}
        </p>
      )}
    </div>
  );
}

function AlignIcon({ align }) {
  const widths = { left: [16, 11, 14], center: [16, 10, 13], right: [16, 11, 14], justify: [16, 16, 16] };
  const [w1, w2, w3] = widths[align];
  const x = (w) => (align === "left" ? 0 : align === "right" ? 16 - w : (16 - w) / 2);
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
      <rect x={x(w1)} y="2" width={w1} height="1.6" rx="0.8" fill="currentColor" />
      <rect x={x(w2)} y="7" width={w2} height="1.6" rx="0.8" fill="currentColor" />
      <rect x={x(w3)} y="12" width={w3} height="1.6" rx="0.8" fill="currentColor" />
    </svg>
  );
}

function ListIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
      <circle cx="1.5" cy="2.8" r="1.3" fill="currentColor" />
      <circle cx="1.5" cy="8" r="1.3" fill="currentColor" />
      <circle cx="1.5" cy="13.2" r="1.3" fill="currentColor" />
      <rect x="5" y="2" width="11" height="1.6" rx="0.8" fill="currentColor" />
      <rect x="5" y="7.2" width="11" height="1.6" rx="0.8" fill="currentColor" />
      <rect x="5" y="12.4" width="11" height="1.6" rx="0.8" fill="currentColor" />
    </svg>
  );
}

function AnchorIcon({ position }) {
  const barY = { top: 3, middle: 7, bottom: 11 }[position];
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
      <rect x="1" y="1" width="14" height="14" rx="1.5" stroke="currentColor" strokeWidth="1.3" />
      <rect x="3.5" y={barY} width="9" height="1.8" rx="0.9" fill="currentColor" />
    </svg>
  );
}

// Compact floating toolbar for the selected text layer — fixed
// above the canvas (same positioning approach as the crop-mode toolbar
// right above this component's usage in the JSX below: read
// canvasWrapRef's live rect, not a ref tied to the object itself, since
// Design tools' own toolbars are a constant fixed bar, not something that tracks
// the object around while panning/zooming).
// One decimal place — matches the values a designer actually types
// (2.5, 3.0), rather than fabric's raw floating-point precision.
const formatOneDecimal = (n) => (Math.round((n || 0) * 10) / 10).toFixed(1);

function TextToolbar({ obj, onChange, canvasWrapRef }) {
  const [caseMenuOpen, setCaseMenuOpen] = useState(false);
  // Buffered as plain text, not derived straight from the fabric object on
  // every render — a controlled input bound directly to a number would
  // snap "0" back the instant it's cleared (Number("") || 0 === 0),
  // making the field impossible to retype. Typing here calls onChange live
  // whenever the text is currently a valid number (so the canvas still
  // updates as you type), but an empty/mid-typing value ("", "-", "2.")
  // just stays in the input untouched until it resolves or the field is
  // left (see the blur handlers below, which re-sync from the real value).
  const [letterSpacingText, setLetterSpacingText] = useState(() =>
    formatOneDecimal(charSpacingToPx(obj.charSpacing, obj.fontSize))
  );
  const [lineHeightText, setLineHeightText] = useState(() => formatOneDecimal(obj.lineHeight ?? 1.2));
  if (!canvasWrapRef.current) return null;
  const rect = canvasWrapRef.current.getBoundingClientRect();

  const onLetterSpacingInput = (text) => {
    setLetterSpacingText(text);
    const n = parseFloat(text);
    if (Number.isFinite(n)) onChange({ charSpacing: pxToCharSpacing(n, obj.fontSize) });
  };
  const onLineHeightInput = (text) => {
    setLineHeightText(text);
    const n = parseFloat(text);
    if (Number.isFinite(n)) onChange({ lineHeight: n });
  };

  // charSpacing is font-size-relative in fabric (thousandths of an em) but
  // the toolbar shows/accepts absolute px — always resolve through the
  // object's CURRENT fontSize, and when font size itself changes, carry the
  // same px value forward at the new size rather than letting the visual
  // spacing silently change underneath the user.
  const setFontSize = (size) => {
    const clamped = Math.max(1, Math.round(size) || 1);
    const currentPx = charSpacingToPx(obj.charSpacing, obj.fontSize);
    onChange({ fontSize: clamped, charSpacing: pxToCharSpacing(currentPx, clamped) });
  };

  const applyCase = (kind) => {
    const text = obj.text || "";
    let next = text;
    if (kind === "upper") next = text.toUpperCase();
    else if (kind === "lower") next = text.toLowerCase();
    else if (kind === "capitalize") {
      next = text.replace(/\S+/g, (w) => w[0].toUpperCase() + w.slice(1).toLowerCase());
    }
    onChange({ text: next });
    setCaseMenuOpen(false);
  };

  // No structured list data — a bullet is just a literal "• " prefix per
  // non-empty line (see plan: real list schema isn't worth the surface
  // area for a flat canvas text layer). "Already listed" is inferred from
  // every non-empty line already starting with the marker, so the button
  // can act as a toggle instead of only ever adding bullets.
  const lines = (obj.text || "").split("\n");
  const nonEmpty = lines.filter((l) => l.trim());
  const isListed = nonEmpty.length > 0 && nonEmpty.every((l) => l.startsWith("• "));
  const toggleList = () => {
    const next = isListed
      ? lines.map((l) => (l.startsWith("• ") ? l.slice(2) : l))
      : lines.map((l) => (l.trim() ? (l.startsWith("• ") ? l : `• ${l}`) : l));
    onChange({ text: next.join("\n") });
  };

  return (
    <div
      className="text-toolbar"
      style={{
        position: "fixed",
        // MAS: inside the stage's top margin, so it never covers Download/Save
        top: rect.top + 6,
        left: rect.left + rect.width / 2,
        transform: "translateX(-50%)",
      }}
    >
      <select
        className="text-toolbar-font"
        value={obj.fontFamily}
        style={{ fontFamily: `'${obj.fontFamily}'` }}
        onChange={(e) => onChange({ fontFamily: e.target.value })}
      >
        {FONT_FAMILIES.map((name) => (
          <option key={name} value={name} style={{ fontFamily: `'${name}'` }}>
            {name}
          </option>
        ))}
      </select>

      <div className="text-toolbar-sep" />

      <div className="text-toolbar-stepper">
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => setFontSize(Math.round(obj.fontSize) - 1)}>
          &minus;
        </button>
        <input
          type="number"
          disabled={obj.fitToWidth || obj.fitToHeight}
          value={Math.round(obj.fontSize)}
          onChange={(e) => setFontSize(Number(e.target.value))}
        />
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => setFontSize(Math.round(obj.fontSize) + 1)}>
          +
        </button>
      </div>

      <ColorInput
        className="compact"
        title="Text color"
        value={obj.fill}
        onChange={(value) => onChange({ fill: value })}
      />

      <div className="text-toolbar-sep" />

      <div className="text-toolbar-group">
        <button
          type="button"
          className={`text-toolbar-btn ${obj.fontWeight === "bold" ? "selected" : ""}`}
          title="Bold"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onChange({ fontWeight: obj.fontWeight === "bold" ? "normal" : "bold" })}
        >
          <b>B</b>
        </button>
        <button
          type="button"
          className={`text-toolbar-btn ${obj.fontStyle === "italic" ? "selected" : ""}`}
          title="Italic"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onChange({ fontStyle: obj.fontStyle === "italic" ? "normal" : "italic" })}
        >
          <i>I</i>
        </button>
        <button
          type="button"
          className={`text-toolbar-btn ${obj.underline ? "selected" : ""}`}
          title="Underline"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onChange({ underline: !obj.underline })}
        >
          <u>U</u>
        </button>
        <button
          type="button"
          className={`text-toolbar-btn ${obj.linethrough ? "selected" : ""}`}
          title="Strikethrough"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onChange({ linethrough: !obj.linethrough })}
        >
          <s>S</s>
        </button>
      </div>

      <div className="text-toolbar-sep" />

      <div className="text-toolbar-menu-wrap">
        <button
          type="button"
          className="text-toolbar-btn"
          title="Change case"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setCaseMenuOpen((o) => !o)}
        >
          aA
        </button>
        {caseMenuOpen && (
          <div className="text-toolbar-menu" onMouseLeave={() => setCaseMenuOpen(false)}>
            <button type="button" onClick={() => applyCase("capitalize")}>Capitalize</button>
            <button type="button" onClick={() => applyCase("upper")}>UPPERCASE</button>
            <button type="button" onClick={() => applyCase("lower")}>lowercase</button>
          </div>
        )}
      </div>

      <div className="text-toolbar-sep" />

      <div className="text-toolbar-group">
        {["left", "center", "right", "justify"].map((a) => (
          <button
            key={a}
            type="button"
            className={`text-toolbar-btn ${obj.textAlign === (a === "justify" ? FABRIC_JUSTIFY : a) ? "selected" : ""}`}
            title={`Align ${a}`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onChange({ textAlign: a === "justify" ? FABRIC_JUSTIFY : a })}
          >
            <AlignIcon align={a} />
          </button>
        ))}
      </div>

      <button
        type="button"
        className={`text-toolbar-btn ${isListed ? "selected" : ""}`}
        title="Bullet list"
        onMouseDown={(e) => e.preventDefault()}
        onClick={toggleList}
      >
        <ListIcon />
      </button>

      <div className="text-toolbar-sep" />

      <div className="text-toolbar-stepper" title="Letter spacing (px)">
        <span className="text-toolbar-label">Aa</span>
        <input
          type="text"
          inputMode="decimal"
          value={letterSpacingText}
          onChange={(e) => onLetterSpacingInput(e.target.value)}
          onBlur={() => setLetterSpacingText(formatOneDecimal(charSpacingToPx(obj.charSpacing, obj.fontSize)))}
        />
      </div>

      <div className="text-toolbar-stepper" title="Line spacing">
        <span className="text-toolbar-label">&#8597;</span>
        <input
          type="text"
          inputMode="decimal"
          value={lineHeightText}
          onChange={(e) => onLineHeightInput(e.target.value)}
          onBlur={() => setLineHeightText(formatOneDecimal(obj.lineHeight ?? 1.2))}
        />
      </div>

      <div className="text-toolbar-sep" />

      <div className="text-toolbar-group">
        {["top", "middle", "bottom"].map((v) => (
          <button
            key={v}
            type="button"
            className={`text-toolbar-btn ${(obj.verticalAlign || "top") === v ? "selected" : ""}`}
            title={`Anchor ${v}`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              if (v !== "top" && obj.manualHeight == null) {
                onChange({ verticalAlign: v, height: obj.height, manualHeight: obj.height });
              } else {
                onChange({ verticalAlign: v });
              }
            }}
          >
            <AnchorIcon position={v} />
          </button>
        ))}
      </div>
    </div>
  );
}

function PropertiesPanel({
  obj,
  onChange,
  appFields,
  onRenamed,
  onReshape,
  onTextScript,
  onRemoveBackground,
  onCropImage,
  onSvgColors,
  onDelete,
  onBringForward,
  onSendBackward,
}) {
  const [name, setNameLocal] = useState(obj.layerName);
  const [removingBg, setRemovingBg] = useState(false);
  const [bgError, setBgError] = useState(null);

  const handleRemoveBg = async () => {
    setRemovingBg(true);
    setBgError(null);
    try {
      await onRemoveBackground();
    } catch (err) {
      setBgError(err.response?.data?.detail || err.message);
    } finally {
      setRemovingBg(false);
    }
  };

  useEffect(() => {
    setNameLocal(obj.layerName);
  }, [obj]);

  const commitName = (value) => {
    setNameLocal(value);
    obj.layerName = value;
    onRenamed?.();
  };

  // Connected template: pick which of the app's fields this layer fills —
  // this just sets the layer name, so nobody has to type the key.
  const fieldChoices = (appFields || []).filter((f) => f.kind === obj.layerType);

  return (
    <div>
      {appFields && (obj.layerType === "text" || obj.layerType === "image") && (
        <div className="field">
          <label>This layer is the…</label>
          <select value={fieldChoices.some((f) => f.key === name) ? name : ""} onChange={(e) => commitName(e.target.value || (obj.layerType === "text" ? "Text" : "Image"))}>
            <option value="">Fixed design (not filled in by job data)</option>
            {fieldChoices.map((f) => (
              <option key={f.key} value={f.key}>
                {f.label}
              </option>
            ))}
          </select>
          {fieldChoices.length === 0 && (
            <p className="hint" style={{ marginTop: 4 }}>
              The app has no {obj.layerType} fields for this template type.
            </p>
          )}
        </div>
      )}
      <div className="field">
        <label>Layer name</label>
        <input type="text" value={name} onChange={(e) => commitName(e.target.value)} />
      </div>

      <div className="props-grid">
        <div className="field">
          <label>X</label>
          <input
            type="number"
            value={Math.round(obj.left)}
            onChange={(e) => onChange({ left: Number(e.target.value) })}
          />
        </div>
        <div className="field">
          <label>Y</label>
          <input
            type="number"
            value={Math.round(obj.top)}
            onChange={(e) => onChange({ top: Number(e.target.value) })}
          />
        </div>
        <div className="field">
          <label>Width</label>
          <input
            type="number"
            min={1}
            value={Math.round((obj.width || 1) * (obj.scaleX || 1))}
            onChange={(e) => {
              const value = Math.max(1, Number(e.target.value) || 1);
              if (obj.layerType === "text") onChange({ width: value });
              else if (obj.lockAspect) {
                const k = value / ((obj.width || 1) * (obj.scaleX || 1));
                onChange({ scaleX: (obj.scaleX || 1) * k, scaleY: (obj.scaleY || 1) * k });
              } else onChange({ scaleX: value / (obj.width || 1) });
            }}
          />
        </div>
        <div className="field">
          <label>Height</label>
          <input
            type="number"
            min={1}
            value={Math.round((obj.height || 1) * (obj.scaleY || 1))}
            onChange={(e) => {
              const value = Math.max(1, Number(e.target.value) || 1);
              if (obj.layerType === "text") onChange({ height: value, manualHeight: value });
              else if (obj.lockAspect) {
                const k = value / ((obj.height || 1) * (obj.scaleY || 1));
                onChange({ scaleX: (obj.scaleX || 1) * k, scaleY: (obj.scaleY || 1) * k });
              } else onChange({ scaleY: value / (obj.height || 1) });
            }}
          />
        </div>
        <div className="field">
          <label>Rotation (°)</label>
          <input
            type="number"
            value={Math.round(obj.angle || 0)}
            onChange={(e) => onChange({ angle: Number(e.target.value) || 0 })}
          />
        </div>
      </div>

      <div className="field">
        <label>Flip</label>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            type="button"
            className={`btn-small flip-btn ${obj.flipX ? "selected" : ""}`}
            onClick={() => onChange({ flipX: !obj.flipX })}
          >
            ↔ Horizontal
          </button>
          <button
            type="button"
            className={`btn-small flip-btn ${obj.flipY ? "selected" : ""}`}
            onClick={() => onChange({ flipY: !obj.flipY })}
          >
            ↕ Vertical
          </button>
        </div>
      </div>

      <div className="prop-section">
        <div className="prop-section-title">Appearance</div>
        <div className="field">
          <label>Opacity ({Math.round((obj.opacity ?? 1) * 100)}%)</label>
          <input
            type="range"
            min={0}
            max={1}
            step={0.01}
            value={obj.opacity ?? 1}
            onChange={(e) => onChange({ opacity: Number(e.target.value) })}
          />
        </div>
        {obj.layerType !== "text" && (
          <label className="inline-check">
            <input
              type="checkbox"
              checked={Boolean(obj.lockAspect)}
              onChange={(e) => onChange({ lockAspect: e.target.checked })}
            />
            Lock aspect ratio
          </label>
        )}
      </div>

      {obj.layerType === "text" && (
        <>
          <div className="field">
            <label>Text</label>
            <textarea
              rows={3}
              value={obj.text}
              onChange={(e) => onChange({ text: e.target.value })}
            />
          </div>
          <p className="hint" style={{ marginTop: -4, marginBottom: 10 }}>
            Font, size, color, style, alignment, spacing and anchor are set
            from the toolbar above the canvas when this layer is selected.
          </p>
          <div className="field">
            <label>
              <input
                type="checkbox"
                checked={Boolean(obj.fitToWidth)}
                onChange={(e) => {
                  if (e.target.checked) {
                    const targetWidth = obj.width;
                    if (obj.fitToHeight) {
                      // Fit-to-height's wrap-based sizing already governs
                      // the font (and already treats the current width as
                      // the fixed wrap boundary) — just pin the width too
                      // so both dimensions are recorded as fixed.
                      onChange({ fitToWidth: true, fitWidthBoxWidth: targetWidth });
                    } else {
                      const fitted = computeFitToWidthFontSize(obj.text, targetWidth, obj.fontFamily, obj.fontWeight);
                      onChange({
                        fitToWidth: true,
                        fitWidthBoxWidth: targetWidth,
                        width: targetWidth,
                        fontSize: fitted,
                      });
                    }
                  } else {
                    // Leave the font at whatever size is currently on
                    // screen — switching to manual control shouldn't itself
                    // change how the text looks.
                    onChange({ fitToWidth: false });
                  }
                }}
              />{" "}
              Fit to width
            </label>
            <p className="hint" style={{ marginTop: 2 }}>
              {obj.fitToHeight
                ? "Combined with Fit to height: the box stays a completely fixed size and text wraps normally within it."
                : "Keeps the box a fixed width and grows or shrinks the font so the text always fills one line without wrapping. Turn on Fit to height too for a fully fixed-size box with wrapping."}
            </p>
          </div>
          <div className="field">
            <label>
              <input
                type="checkbox"
                checked={Boolean(obj.fitToHeight)}
                onChange={(e) => {
                  if (e.target.checked) {
                    const targetHeight = obj.manualHeight ?? obj.height;
                    const fitted = computeFitToHeightFontSize(
                      obj.text,
                      obj.width,
                      targetHeight,
                      obj.fontFamily,
                      obj.fontWeight,
                      obj.lineHeight
                    );
                    onChange({
                      fitToHeight: true,
                      fitWidthBoxWidth: obj.width,
                      width: obj.width,
                      height: targetHeight,
                      manualHeight: targetHeight,
                      fontSize: fitted,
                    });
                  } else if (obj.fitToWidth) {
                    // Width-only sizing takes back over now that height-fit
                    // no longer overrides it.
                    const targetWidth = obj.fitWidthBoxWidth ?? obj.width;
                    const fitted = computeFitToWidthFontSize(obj.text, targetWidth, obj.fontFamily, obj.fontWeight);
                    onChange({ fitToHeight: false, fontSize: fitted, width: targetWidth });
                  } else {
                    onChange({ fitToHeight: false });
                  }
                }}
              />{" "}
              Fit to height
            </label>
            <p className="hint" style={{ marginTop: 2 }}>
              {obj.fitToWidth
                ? "Combined with Fit to width: the box stays a completely fixed size and text wraps normally within it."
                : "Keeps the box a fixed height and grows or shrinks the font so the (still wrapping) text always fills the box vertically. Turn on Fit to width too for a fully fixed-size box."}
            </p>
          </div>
          <div className="field">
            <label>Script (select text first, or applies to all)</label>
            <div className="tool-buttons">
              <button
                type="button"
                className="secondary btn-small"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => onTextScript("super")}
              >
                x² Super
              </button>
              <button
                type="button"
                className="secondary btn-small"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => onTextScript("sub")}
              >
                x₂ Sub
              </button>
              <button
                type="button"
                className="secondary btn-small"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => onTextScript("normal")}
              >
                Normal
              </button>
            </div>
          </div>

          <div className="field">
            <label>
              <input
                type="checkbox"
                checked={Boolean(obj.stroke)}
                onChange={(e) =>
                  onChange(
                    e.target.checked
                      ? { stroke: "#000000", strokeWidth: 2 }
                      : { stroke: null, strokeWidth: 0 }
                  )
                }
              />{" "}
              Outline
            </label>
            {obj.stroke && (
              <div className="effects-controls">
                <div className="field">
                  <label>Outline color</label>
                  <ColorInput value={obj.stroke} onChange={(value) => onChange({ stroke: value })} />
                </div>
                <div className="field">
                  <label>Outline width ({obj.strokeWidth || 2}px)</label>
                  <input
                    type="range"
                    min={1}
                    max={20}
                    value={obj.strokeWidth || 2}
                    onChange={(e) => onChange({ strokeWidth: Number(e.target.value) })}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="field">
            <label>
              <input
                type="checkbox"
                checked={Boolean(obj.backgroundColor)}
                onChange={(e) =>
                  onChange({ backgroundColor: e.target.checked ? obj.backgroundColor || "#ffff00" : null })
                }
              />{" "}
              Background
            </label>
            {obj.backgroundColor && (
              <div className="effects-controls">
                <div className="field">
                  <label>Background color</label>
                  <ColorInput
                    value={obj.backgroundColor}
                    onChange={(value) => onChange({ backgroundColor: value })}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="field">
            <label>
              <input
                type="checkbox"
                checked={Boolean(obj.textBackgroundColor)}
                onChange={(e) =>
                  onChange({
                    textBackgroundColor: e.target.checked ? obj.textBackgroundColor || "#ffff00" : null,
                  })
                }
              />{" "}
              Highlight
            </label>
            {obj.textBackgroundColor && (
              <div className="effects-controls">
                <div className="field">
                  <label>Highlight color</label>
                  <ColorInput
                    value={obj.textBackgroundColor}
                    onChange={(value) => onChange({ textBackgroundColor: value })}
                  />
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {obj.layerType === "image" && (
        <>
          {appFields && !obj.assetSrc?.startsWith("data:") && (
            <div className="field">
              <label>Image source (default; overridable by API as image_url)</label>
              <input
                type="text"
                value={obj.assetSrc || ""}
                onChange={(e) => {
                  obj.assetSrc = e.target.value;
                  onChange({});
                }}
              />
            </div>
          )}
          {isSvgUrl(obj.svgSrc || obj.assetSrc) ? (
            <SvgColorControls obj={obj} onChange={onSvgColors} />
          ) : (
            <ImageAdjustControls obj={obj} onChange={onChange} />
          )}
          <div className="field">
            <label>Frame</label>
            <select value={obj.maskShape || ""} onChange={(e) => onChange({ maskShape: e.target.value || null })}>
              <option value="">None (rectangle)</option>
              {obj.maskShape === "custom" && <option value="custom">Custom (combined shapes)</option>}
              {FRAME_DEFS.map((d, i) => (
                <option key={`${d.kind}-${i}`} value={d.kind}>
                  {d.label}
                  {d.kind.startsWith("blob") ? ` ${d.kind.slice(4)}` : ""}
                </option>
              ))}
            </select>
            {obj.maskShape && (
              <p className="hint" style={{ marginTop: 4 }}>
                While this frame is selected, picking an image in Uploads fills it.
              </p>
            )}
          </div>
          {obj.maskShape && obj.maskShape !== "pill" && obj.maskShape !== "circle" && (
            <div className="field">
              <label>Corner radius</label>
              <div className="range-with-number">
                <input
                  type="range"
                  min={0}
                  max={200}
                  value={Math.round(obj.maskCornerRadius || 0)}
                  onChange={(e) => onChange({ maskCornerRadius: Number(e.target.value) })}
                />
                <input
                  type="number"
                  min={0}
                  value={Math.round(obj.maskCornerRadius || 0)}
                  onChange={(e) => onChange({ maskCornerRadius: Math.max(0, Number(e.target.value) || 0) })}
                />
              </div>
              <p className="hint" style={{ marginTop: 4 }}>Rounds every sharp corner, outer and inner.</p>
            </div>
          )}
          {!obj.maskShape && (
            <div className="field">
              <label>Corner radius</label>
              <input
                type="number"
                min={0}
                value={Math.round(obj.cornerRadius || 0)}
                onChange={(e) => onChange({ cornerRadius: Math.max(0, Number(e.target.value) || 0) })}
              />
            </div>
          )}
          <div className="field">
            <button
              className="secondary"
              style={{ width: "100%" }}
              onClick={onCropImage}
              disabled={Math.abs((obj.angle || 0) % 360) > 0.5 || (obj.maskShape && isEmptyFrame(obj))}
              title={
                Math.abs((obj.angle || 0) % 360) > 0.5
                  ? "Reset rotation to 0° before cropping"
                  : obj.maskShape
                    ? "Move or resize the photo inside the frame (or double-click it)"
                    : "Crop image"
              }
            >
              {obj.maskShape ? "⤢ Adjust photo in frame" : "⤢ Crop image"}
            </button>
          </div>
          {onRemoveBackground && <div className="field">
            <button
              className="secondary"
              style={{ width: "100%" }}
              onClick={handleRemoveBg}
              disabled={removingBg}
            >
              {removingBg ? "Removing background..." : "✂ Remove background"}
            </button>
            {bgError && (
              <p className="csv-badge csv-badge-error" style={{ marginTop: 6 }}>{bgError}</p>
            )}
          </div>}
        </>
      )}

      {obj.layerType === "shape" && (
        <>
          {GRID_KINDS.has(obj.shapeKind) && (
            <div className="props-grid">
              {[["gridColumns", "Columns"], ["gridRows", "Rows"]].map(([key, label]) => (
                <div className="field" key={key}>
                  <label>{label}</label>
                  <input
                    type="number"
                    min={1}
                    max={50}
                    value={obj[key] || 1}
                    onChange={(e) => onChange({ [key]: Math.min(50, Math.max(1, Math.round(Number(e.target.value)) || 1)) })}
                  />
                </div>
              ))}
            </div>
          )}
          {obj.shapeKind === "dot_grid" && (
            <div className="prop-section">
              <div className="prop-section-title">Dots</div>
              <div className="field">
                <ColorInput value={obj.dotColor || "#191919"} onChange={(value) => onChange({ dotColor: value })} />
              </div>
              <div className="field">
                <label>Size ({obj.dotSize || 8}px)</label>
                <input
                  type="range"
                  min={1}
                  max={60}
                  value={obj.dotSize || 8}
                  onChange={(e) => onChange({ dotSize: Number(e.target.value) })}
                />
              </div>
            </div>
          )}
          <FillControls obj={obj} onChange={onChange} title={obj.shapeKind === "dot_grid" ? "Background" : "Fill"} />
          {obj.shapeKind !== "dot_grid" && (
            <StrokeControls obj={obj} onChange={onChange} title={obj.shapeKind === "grid" ? "Grid lines" : "Border"} />
          )}
          {ROUNDABLE_SHAPES.has(obj.shapeKind) && (
            <div className="field">
              <label>Corner roundness</label>
              <input
                type="number"
                min={0}
                value={Math.round(obj.cornerRadius || 0)}
                onChange={(e) =>
                  onReshape({ cornerRadius: Math.max(0, Number(e.target.value) || 0) })
                }
              />
            </div>
          )}
          {obj.shapeKind === "leaf" && (
            <div className="field">
              <label>Curviness ({Math.round(obj.cornerRadius || 30)})</label>
              <input
                type="range"
                min={1}
                max={60}
                value={Math.round(obj.cornerRadius || 30)}
                onChange={(e) => onReshape({ cornerRadius: Number(e.target.value) })}
              />
              <p className="hint" style={{ marginTop: 2 }}>
                Low = fuller leaf, high = thin sickle.
              </p>
            </div>
          )}
        </>
      )}

      {obj.layerType === "rect" && (
        <>
          <FillControls obj={obj} onChange={onChange} />
          <StrokeControls obj={obj} onChange={onChange} />
          <CornerRadiusControl obj={obj} onReshape={onReshape} />
        </>
      )}

      {["text", "rect", "shape"].includes(obj.layerType) && (
        <div className="field">
          <label>
            <input
              type="checkbox"
              checked={Boolean(obj.shadow)}
              onChange={(e) =>
                onChange(e.target.checked ? shadowPatch(obj, {}) : { shadow: null })
              }
            />{" "}
            Shadow
          </label>
          {obj.shadow && (
            <div className="effects-controls">
              <div className="field">
                <label>Shadow color</label>
                <ColorInput
                  value={obj.shadowColor || "#000000"}
                  onChange={(value) => onChange(shadowPatch(obj, { color: value }))}
                />
              </div>
              <div className="field">
                <label>Opacity ({Math.round((obj.shadowOpacity ?? 0.4) * 100)}%)</label>
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.05}
                  value={obj.shadowOpacity ?? 0.4}
                  onChange={(e) => onChange(shadowPatch(obj, { opacity: Number(e.target.value) }))}
                />
              </div>
              <div className="field">
                <label>Blur ({Math.round(obj.shadow.blur)}px)</label>
                <input
                  type="range"
                  min={0}
                  max={40}
                  value={obj.shadow.blur}
                  onChange={(e) => onChange(shadowPatch(obj, { blur: Number(e.target.value) }))}
                />
              </div>
              <div className="field">
                <label>Offset X ({Math.round(obj.shadow.offsetX)}px)</label>
                <input
                  type="range"
                  min={-40}
                  max={40}
                  value={obj.shadow.offsetX}
                  onChange={(e) => onChange(shadowPatch(obj, { offsetX: Number(e.target.value) }))}
                />
              </div>
              <div className="field">
                <label>Offset Y ({Math.round(obj.shadow.offsetY)}px)</label>
                <input
                  type="range"
                  min={-40}
                  max={40}
                  value={obj.shadow.offsetY}
                  onChange={(e) => onChange(shadowPatch(obj, { offsetY: Number(e.target.value) }))}
                />
              </div>
            </div>
          )}
        </div>
      )}

      <div className="tool-buttons">
        <button className="secondary" onClick={onBringForward}>Bring to front</button>
        <button className="secondary" onClick={onSendBackward}>Send to back</button>
        <button className="danger" onClick={onDelete}>Delete layer</button>
      </div>
    </div>
  );
}
